import { createScopedErrors, registerCodes } from '@norkron/web-errors';
import { emptyTable, renderFragmentInto, renderFragmentToHtml, setFragmentKind, applyCommentMark, toggleStyleMark, applyStyleMark, editFragmentText, bodyInnerHtml, isCoalescibleText, fragmentText, allCommentIds, FRAGMENT_SURFACE_TESTID, fragmentByteLength, changeFragmentCase, clearFragmentMarks, SnapshotHistory, watchClipboardReadPermission, asyncClipboardReadable, clipboardWriteSupported, resolveListState, textFragment, marksInRange, DOCZ_BLOCK_CLASS, CLIPBOARD_MIME_DOCZ, CLIPBOARD_MIME_HTML, CLIPBOARD_MIME_PLAIN, parseClipboard, sliceFragment, schemeForPreset, asyncClipboardWritePresent, clipboardFlavours, writeFlavoursToSystem, runNativeClipboardCommand, STYLE_PROPS, resolveStyle, MIXED, DEFAULT_BAND_COLOR, shadingForCell, borderForCell, writeFlavoursTo, byteLen as byteLen$1, isSafeHref, TABLE_STYLE_PRESETS, DEFAULT_TEXT_COLOR, A4_PAGE_BOX, DEFAULT_HEADER_FILL, A4_NORMAL_MARGINS, PAGE_MARGIN_PRESETS, validatePageMargins } from '@norkron/web-docz';
export { A4_NORMAL_MARGINS, A4_PAGE_BOX, DEFAULT_BAND_COLOR, DEFAULT_HEADER_FILL, PAGE_MARGIN_PRESETS, TABLE_STYLE_PRESETS, borderForCell, schemeForPreset, shadingForCell, validatePageMargins } from '@norkron/web-docz';
import * as React from 'react';
import { forwardRef, useImperativeHandle, useState, useEffect, useMemo, useRef, useCallback, useId, useLayoutEffect, Fragment as Fragment$1 } from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { TextInput, Button, IconButton, ButtonGroup } from '@norkron/web-controls';
import { Icon } from '@norkron/web-icons';
import { Popover, PopoverTrigger, PopoverContent } from '@norkron/web-popover';
import { SegmentedControl } from '@norkron/web-segmented-control';
import { ColorPicker, ColorSwatchGrid, ColorArea, ColorHueSlider, ColorTextInput } from '@norkron/web-color-picker';
import { z } from 'zod';
import { Menu, MenuTrigger, MenuContent, MenuItem, MenuSubMenu, MenuItemCheckbox, MenuRadioGroup, MenuRadioItem } from '@norkron/web-menu';
import { TabsRoot, useTabsRootContext, Tabs } from '@norkron/web-tabs';
import { createPortal } from 'react-dom';

// src/error-codes.ts
var LIB = "@norkron/web-elaborate-editor";
var WEB_ELABORATE_EDITOR_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-elaborate-editor");
var { warn, panic, reportInvalidInput } = createScopedErrors(
  LIB,
  WEB_ELABORATE_EDITOR_TOKEN
);
try {
  registerCodes(LIB, {
    WEB_ELABORATE_EDITOR_INVALID_INPUT: {
      description: "A public function received an input that failed validation \u2014 e.g. a range outside any rendered block, or a placeCommentMark with an empty commentId.",
      severity: "error",
      docsAnchor: "elaborate-editor/invalid-input",
      exampleFix: "Ensure the selection Range falls inside a rendered `.docz-block[data-block-id]`, and pass a non-empty commentId."
    },
    WEB_ELABORATE_EDITOR_NO_BLOCK: {
      description: "A selection Range did not resolve to a rendered block (no ancestor `.docz-block[data-block-id]`). buildSelector / placeCommentMark return null rather than guess.",
      severity: "warning",
      docsAnchor: "elaborate-editor/no-block",
      exampleFix: "Only build a selector / place a comment when the selection is inside the document canvas."
    },
    WEB_ELABORATE_EDITOR_NO_DOC: {
      description: "An imperative method was called before the editor rendered a document (root is null).",
      severity: "warning",
      docsAnchor: "elaborate-editor/no-doc",
      exampleFix: "Call editor methods after the component has mounted and rendered its doc."
    },
    WEB_ELABORATE_EDITOR_VERB_FAILED: {
      description: "A docz.wasm transform verb (dz_transform_edit_text / apply_mark / split_runs) returned a non-OK status for a fragment edit \u2014 the surface keeps its prior value rather than applying a corrupt frame.",
      severity: "warning",
      docsAnchor: "elaborate-editor/verb-failed",
      exampleFix: "Ensure the edit byte range is within the fragment plain text and the loaded docz.wasm exports the transform verbs (features bit2)."
    }
  });
} catch {
}
var AFFIX = 32;
var UTF8 = new TextEncoder();
var UTF8_DEC = new TextDecoder();
var utf8Len = (s) => UTF8.encode(s).length;
function findBlockRoot(node) {
  let el = node;
  while (el && el.nodeType !== 1) el = el.parentNode;
  let cur = el;
  while (cur) {
    if (cur.classList?.contains(DOCZ_BLOCK_CLASS) && cur.hasAttribute("data-block-id")) {
      return cur;
    }
    cur = cur.parentElement;
  }
  return null;
}
function charIndex(blockRoot, node, offset) {
  const doc = blockRoot.ownerDocument;
  const r = doc.createRange();
  r.setStart(blockRoot, 0);
  try {
    r.setEnd(node, offset);
  } catch {
    return blockRoot.textContent?.length ?? 0;
  }
  return r.toString().length;
}
function buildSelectorFromOffsets(input) {
  const bytes = UTF8.encode(input.text);
  const lo = Math.max(0, Math.min(input.start, input.end, bytes.length));
  const hi = Math.max(0, Math.min(Math.max(input.start, input.end), bytes.length));
  if (hi <= lo) return null;
  const before = UTF8_DEC.decode(bytes.subarray(0, lo));
  const after = UTF8_DEC.decode(bytes.subarray(hi));
  const exactQuote = UTF8_DEC.decode(bytes.subarray(lo, hi));
  if (exactQuote.length === 0) return null;
  return {
    blockId: input.blockId,
    exactQuote,
    prefix: before.slice(Math.max(0, before.length - AFFIX)),
    suffix: after.slice(0, AFFIX),
    // Block-relative UTF-8 byte offsets (docz's internal units).
    start: lo,
    end: hi,
    baseVersionId: input.baseVersionId
    // quoteHash intentionally omitted — pending a docz.anchor WASM export.
  };
}
function locateSelectorInText(text, selector) {
  if (selector.exactQuote.length === 0) return null;
  const bytes = UTF8.encode(text);
  const lo = Math.max(0, Math.min(selector.start, bytes.length));
  const hi = Math.max(lo, Math.min(selector.end, bytes.length));
  const atStored = UTF8_DEC.decode(bytes.subarray(lo, hi));
  if (atStored === selector.exactQuote) return { start: lo, end: hi, state: "exact" };
  const idx = text.indexOf(selector.exactQuote);
  if (idx < 0) return null;
  const start = utf8Len(text.slice(0, idx));
  const end = start + utf8Len(selector.exactQuote);
  return { start, end, state: "shifted" };
}
var BLOCK_JOIN = "\n";
function buildSelectorFromSlices(input) {
  const slices = input.slices;
  const first = slices[0];
  const last = slices[slices.length - 1];
  if (!first || !last) return null;
  const clamp = (s) => {
    const bytes = UTF8.encode(s.text);
    const lo = Math.max(0, Math.min(s.start, s.end, bytes.length));
    const hi = Math.max(0, Math.min(Math.max(s.start, s.end), bytes.length));
    return { lo, hi, bytes };
  };
  const f = clamp(first);
  const l = clamp(last);
  if (slices.length === 1 && l.hi <= f.lo) return null;
  const parts = [];
  const blocks = [];
  for (const s of slices) {
    const c = clamp(s);
    parts.push(UTF8_DEC.decode(c.bytes.subarray(c.lo, c.hi)));
    blocks.push({ blockId: s.blockId, start: c.lo, end: c.hi });
  }
  const exactQuote = parts.filter((p) => p.length > 0).join(BLOCK_JOIN);
  if (slices.length === 1 && exactQuote.length === 0) return null;
  const before = UTF8_DEC.decode(f.bytes.subarray(0, f.lo));
  const after = UTF8_DEC.decode(l.bytes.subarray(l.hi));
  return {
    blockId: first.blockId,
    exactQuote,
    prefix: before.slice(Math.max(0, before.length - AFFIX)),
    suffix: after.slice(0, AFFIX),
    start: f.lo,
    end: l.hi,
    baseVersionId: input.baseVersionId,
    endBlockId: last.blockId,
    blocks
    // quoteHash intentionally omitted — pending a docz.anchor WASM export.
  };
}
function isCrossBlock(sel) {
  return sel.endBlockId != null && sel.endBlockId !== sel.blockId;
}
var PLAIN_TEXT_BLOCK_KINDS = /* @__PURE__ */ new Set([
  "heading",
  "paragraph",
  "text"
]);
function durableAnchorRefusal(sel, range) {
  if (sel.exactQuote.length === 0) {
    return "This selection has no text to quote, so the comment cannot be re-found after an edit.";
  }
  if (sel.exactQuote.trim().length === 0) {
    return "This selection is only whitespace, so the comment cannot be re-found after an edit.";
  }
  const kinds = range?.kinds;
  if (!kinds || kinds.length === 0) return null;
  const isPlain = range?.isPlainTextKind ?? ((k) => PLAIN_TEXT_BLOCK_KINDS.has(k));
  const markup = [...new Set(kinds.filter((k) => !isPlain(k)))].sort();
  if (markup.length === 0) return null;
  return `This range covers ${markup.join(", ")} content, which the document stores as markup rather than text \u2014 the comment will be created, but it can't re-find itself after the document is edited.`;
}
function locateSelectorAcrossBlocks(blocks, selector) {
  const parts = selector.exactQuote.split(BLOCK_JOIN);
  if (parts.length <= 1) return null;
  const n = parts.length;
  const runAt = (i) => {
    if (i < 0 || i >= blocks.length) return null;
    const out = [];
    let p = 0;
    for (let bi = i; bi < blocks.length && p < n; bi++) {
      const b = blocks[bi];
      if (!b) return null;
      if (b.text.length === 0) {
        if (out.length === 0) return null;
        out.push({ blockId: b.id, start: 0, end: 0 });
        continue;
      }
      const part = parts[p];
      if (part == null) return null;
      if (p === 0) {
        if (!b.text.endsWith(part)) return null;
        out.push({
          blockId: b.id,
          start: utf8Len(b.text.slice(0, b.text.length - part.length)),
          end: utf8Len(b.text)
        });
      } else if (p === n - 1) {
        if (!b.text.startsWith(part)) return null;
        out.push({ blockId: b.id, start: 0, end: utf8Len(part) });
      } else {
        if (b.text !== part) return null;
        out.push({ blockId: b.id, start: 0, end: utf8Len(b.text) });
      }
      p++;
    }
    return p === n ? out : null;
  };
  const stored = blocks.findIndex((b) => b.id === selector.blockId);
  if (stored >= 0) {
    const hit = runAt(stored);
    if (hit && hit[0]?.start === selector.start) return { state: "exact", slices: hit };
    if (hit) return { state: "shifted", slices: hit };
  }
  for (let i = 0; i < blocks.length; i++) {
    const hit = runAt(i);
    if (hit) return { state: "shifted", slices: hit };
  }
  return null;
}
function locateSelector(blocks, selector) {
  if (isCrossBlock(selector) || selector.exactQuote.includes(BLOCK_JOIN)) {
    const cross = locateSelectorAcrossBlocks(blocks, selector);
    if (cross) return cross;
  }
  const stored = blocks.findIndex((b) => b.id === selector.blockId);
  if (stored >= 0) {
    const at = blocks[stored];
    const hit = at ? locateSelectorInText(at.text, selector) : null;
    if (hit) {
      return {
        state: hit.state,
        slices: [{ blockId: selector.blockId, start: hit.start, end: hit.end }]
      };
    }
  }
  for (let i = 0; i < blocks.length; i++) {
    if (i === stored) continue;
    const b = blocks[i];
    if (!b) continue;
    const idx = b.text.indexOf(selector.exactQuote);
    if (idx < 0) continue;
    const start = utf8Len(b.text.slice(0, idx));
    return {
      state: "shifted",
      slices: [{ blockId: b.id, start, end: start + utf8Len(selector.exactQuote) }]
    };
  }
  return null;
}
function buildSelector(range, opts = {}) {
  if (range.collapsed) return null;
  const blockRoot = findBlockRoot(range.startContainer);
  if (!blockRoot) return null;
  const blockId = blockRoot.getAttribute("data-block-id");
  if (!blockId) return null;
  const plain = blockRoot.textContent ?? "";
  const startChar = charIndex(blockRoot, range.startContainer, range.startOffset);
  const endChar = charIndex(blockRoot, range.endContainer, range.endOffset);
  const lo = Math.min(startChar, endChar);
  const hi = Math.max(startChar, endChar);
  return buildSelectorFromOffsets({
    blockId,
    text: plain,
    // Convert the CHAR indices to block-relative UTF-8 byte offsets.
    start: utf8Len(plain.slice(0, lo)),
    end: utf8Len(plain.slice(0, hi)),
    baseVersionId: opts.baseVersionId
  });
}
function isEmpty(range) {
  return Math.max(range.start, range.end) <= Math.min(range.start, range.end);
}
function supportsClearMarks(module) {
  return module.supportsTransform === true;
}
function supportsChangeCase(module) {
  return module.supportsTransform === true;
}
function clearMarksOp(module, frag, range) {
  if (isEmpty(range)) return null;
  const res = clearFragmentMarks(module, frag, range);
  return res.ok ? res.fragment : null;
}
function changeCaseOp(module, frag, range, mode) {
  if (isEmpty(range)) return null;
  const res = changeFragmentCase(module, frag, range, mode);
  return res.ok ? res.fragment : null;
}
var UTF82 = new TextEncoder();
var byteLen = (s) => UTF82.encode(s).length;
function byteAt(plainText, charIdx) {
  return byteLen(plainText.slice(0, charIdx));
}
function spliceBytes(text, at, deleteBytes, insert) {
  const bytes = UTF82.encode(text);
  const a = Math.max(0, Math.min(at, bytes.length));
  const b = Math.max(a, Math.min(a + deleteBytes, bytes.length));
  const insBytes = UTF82.encode(insert);
  const out = new Uint8Array(a + insBytes.length + (bytes.length - b));
  out.set(bytes.subarray(0, a), 0);
  out.set(insBytes, a);
  out.set(bytes.subarray(b), a + insBytes.length);
  return new TextDecoder().decode(out);
}
var INSERTS = /* @__PURE__ */ new Set([
  "insertText",
  "insertReplacementText",
  "insertFromPaste",
  "insertFromDrop",
  "insertCompositionText",
  "insertParagraph",
  "insertLineBreak"
]);
var DELETES = /* @__PURE__ */ new Set([
  "deleteContentBackward",
  "deleteContentForward",
  "deleteContent",
  "deleteByCut",
  "deleteByDrag"
]);
function deriveTextEdit(params) {
  const { plainText, inputType, data } = params;
  const lo = Math.min(params.startChar, params.endChar);
  const hi = Math.max(params.startChar, params.endChar);
  const collapsed = lo === hi;
  if (INSERTS.has(inputType)) {
    const insert = inputType === "insertParagraph" || inputType === "insertLineBreak" ? "\n" : data ?? "";
    if (insert.length === 0 && collapsed) return null;
    return {
      at: byteAt(plainText, lo),
      deleteBytes: byteAt(plainText, hi) - byteAt(plainText, lo),
      insert
    };
  }
  if (DELETES.has(inputType)) {
    if (!collapsed) {
      const at = byteAt(plainText, lo);
      const del = byteAt(plainText, hi) - at;
      return del === 0 ? null : { at, deleteBytes: del, insert: "" };
    }
    if (inputType === "deleteContentForward") {
      const after = Array.from(plainText.slice(lo));
      const first = after[0];
      if (!first) return null;
      const at = byteAt(plainText, lo);
      return { at, deleteBytes: byteAt(plainText, lo + first.length) - at, insert: "" };
    }
    const before = Array.from(plainText.slice(0, lo));
    const last = before[before.length - 1];
    if (!last) return null;
    const newLo = lo - last.length;
    return {
      at: byteAt(plainText, newLo),
      deleteBytes: byteAt(plainText, lo) - byteAt(plainText, newLo),
      insert: ""
    };
  }
  return null;
}
function charIndex2(blockRoot, node, offset) {
  const doc = blockRoot.ownerDocument;
  const r = doc.createRange();
  r.setStart(blockRoot, 0);
  try {
    r.setEnd(node, offset);
  } catch {
    return blockRoot.textContent?.length ?? 0;
  }
  return r.toString().length;
}
function resolveRange(ev, container) {
  const ranges = typeof ev.getTargetRanges === "function" ? ev.getTargetRanges() : [];
  let startContainer = null;
  let startOffset = 0;
  let endContainer = null;
  let endOffset = 0;
  const tr = ranges[0];
  if (tr) {
    startContainer = tr.startContainer;
    startOffset = tr.startOffset;
    endContainer = tr.endContainer;
    endOffset = tr.endOffset;
  } else {
    const sel = container.ownerDocument.getSelection?.();
    if (!sel || sel.rangeCount === 0) return null;
    const rg = sel.getRangeAt(0);
    startContainer = rg.startContainer;
    startOffset = rg.startOffset;
    endContainer = rg.endContainer;
    endOffset = rg.endOffset;
  }
  const blockRoot = findBlockRoot(startContainer);
  if (!blockRoot || !blockRoot.classList.contains(DOCZ_BLOCK_CLASS)) return null;
  if (!container.contains(blockRoot)) return null;
  return {
    blockRoot,
    startChar: charIndex2(blockRoot, startContainer, startOffset),
    endChar: charIndex2(blockRoot, endContainer ?? startContainer, endOffset)
  };
}
function textEditFromBeforeInput(ev, container) {
  const range = resolveRange(ev, container);
  if (!range) return null;
  const blockId = range.blockRoot.getAttribute("data-block-id");
  if (!blockId) return null;
  const data = ev.data ?? ev.dataTransfer?.getData?.("text/plain") ?? (ev.dataTransfer ? "" : null);
  const edit = deriveTextEdit({
    plainText: range.blockRoot.textContent ?? "",
    startChar: range.startChar,
    endChar: range.endChar,
    inputType: ev.inputType,
    data
  });
  return edit ? { ...edit, blockId } : null;
}

// src/fragment-editing.ts
var UTF83 = new TextEncoder();
var byteLen2 = (s) => UTF83.encode(s).length;
function byteAt2(text, charIdx) {
  return byteLen2(text.slice(0, charIdx));
}
function surfacePlainText(surface) {
  return surface.textContent ?? "";
}
function charIndexWithin(surface, node, offset) {
  const doc = surface.ownerDocument;
  const r = doc.createRange();
  r.setStart(surface, 0);
  try {
    r.setEnd(node, offset);
  } catch {
    return surface.textContent?.length ?? 0;
  }
  return r.toString().length;
}
function byteOffsetWithin(surface, node, offset) {
  return byteAt2(surfacePlainText(surface), charIndexWithin(surface, node, offset));
}
function resolveRange2(ev, surface) {
  const ranges = typeof ev.getTargetRanges === "function" ? ev.getTargetRanges() : [];
  const tr = ranges[0];
  let startContainer;
  let startOffset;
  let endContainer;
  let endOffset;
  if (tr) {
    startContainer = tr.startContainer;
    startOffset = tr.startOffset;
    endContainer = tr.endContainer;
    endOffset = tr.endOffset;
  } else {
    const sel = surface.ownerDocument.getSelection?.();
    if (!sel || sel.rangeCount === 0) return null;
    const rg = sel.getRangeAt(0);
    startContainer = rg.startContainer;
    startOffset = rg.startOffset;
    endContainer = rg.endContainer;
    endOffset = rg.endOffset;
  }
  if (!surface.contains(startContainer)) return null;
  return {
    startChar: charIndexWithin(surface, startContainer, startOffset),
    endChar: charIndexWithin(surface, endContainer, endOffset)
  };
}
function fragmentTextEditFromBeforeInput(ev, surface) {
  const range = resolveRange2(ev, surface);
  if (!range) return null;
  const data = ev.data ?? ev.dataTransfer?.getData?.("text/plain") ?? (ev.dataTransfer ? "" : null);
  return deriveTextEdit({
    plainText: surfacePlainText(surface),
    startChar: range.startChar,
    endChar: range.endChar,
    inputType: ev.inputType,
    data
  });
}
function selectionByteRange(surface) {
  const sel = surface.ownerDocument.getSelection?.();
  if (!sel || sel.rangeCount === 0) return null;
  const rg = sel.getRangeAt(0);
  if (!surface.contains(rg.startContainer) || !surface.contains(rg.endContainer)) return null;
  const text = surfacePlainText(surface);
  const a = charIndexWithin(surface, rg.startContainer, rg.startOffset);
  const b = charIndexWithin(surface, rg.endContainer, rg.endOffset);
  return { start: byteAt2(text, Math.min(a, b)), end: byteAt2(text, Math.max(a, b)) };
}
function pointForByte(surface, byteOffset) {
  const doc = surface.ownerDocument;
  const walker = doc.createTreeWalker(
    surface,
    4
    /* SHOW_TEXT */
  );
  let acc = 0;
  let last = null;
  let node = walker.nextNode();
  while (node) {
    const len = byteLen2(node.data);
    if (acc + len >= byteOffset) {
      return { node, offset: charOffsetForByte(node.data, byteOffset - acc) };
    }
    acc += len;
    last = node;
    node = walker.nextNode();
  }
  return last ? { node: last, offset: last.data.length } : null;
}
function placeCaretAtByte(surface, byteOffset) {
  const doc = surface.ownerDocument;
  const sel = doc.getSelection?.();
  if (!sel) return;
  const range = doc.createRange();
  const p = pointForByte(surface, byteOffset);
  if (p) {
    range.setStart(p.node, p.offset);
    range.collapse(true);
  } else {
    range.selectNodeContents(surface);
    range.collapse(false);
  }
  sel.removeAllRanges();
  sel.addRange(range);
}
function selectByteRange(surface, startByte, endByte) {
  const doc = surface.ownerDocument;
  const sel = doc.getSelection?.();
  if (!sel) return;
  const lo = Math.min(startByte, endByte);
  const hi = Math.max(startByte, endByte);
  const start = pointForByte(surface, lo);
  const end = pointForByte(surface, hi);
  if (!start || !end) {
    placeCaretAtByte(surface, lo);
    return;
  }
  const range = doc.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);
  sel.removeAllRanges();
  sel.addRange(range);
}
function selectAcrossSurfaces(startSurface, startByte, endSurface, endByte) {
  if (startSurface === endSurface) {
    selectByteRange(startSurface, startByte, endByte);
    return;
  }
  const doc = startSurface.ownerDocument;
  const sel = doc.getSelection?.();
  if (!sel) return;
  const start = pointForByte(startSurface, startByte) ?? {
    node: startSurface,
    offset: 0
  };
  const end = pointForByte(endSurface, endByte) ?? {
    node: endSurface,
    offset: 0
  };
  const range = doc.createRange();
  try {
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
  } catch {
    return;
  }
  sel.removeAllRanges();
  sel.addRange(range);
}
function charOffsetForByte(text, byteOffset) {
  let acc = 0;
  for (let i = 0; i < text.length; i++) {
    if (acc >= byteOffset) return i;
    acc += byteLen2(text[i]);
  }
  return text.length;
}

// src/para-props.ts
var INDENT_STEP_PT = 36;
var LINE_SPACING_MULTIPLES = [1, 1.15, 1.5, 2];
var TAB_ALIGN_OPTIONS = [
  "left",
  "center",
  "right",
  "decimal",
  "bar"
];
var TAB_LEADER_OPTIONS = [
  "none",
  "dot",
  "hyphen",
  "underscore",
  "middle_dot"
];
function emptyParaProps() {
  return {};
}
var ZERO_INDENT = { leftPt: 0, rightPt: 0, firstLinePt: 0, hangingPt: 0 };
function clampPt(n) {
  return Number.isFinite(n) && n > 0 ? n : 0;
}
function mergeIndent(cur, patch) {
  const base = cur ?? ZERO_INDENT;
  let firstLine = patch.firstLinePt !== void 0 ? clampPt(patch.firstLinePt) : base.firstLinePt;
  let hanging = patch.hangingPt !== void 0 ? clampPt(patch.hangingPt) : base.hangingPt;
  if (patch.firstLinePt !== void 0 && firstLine > 0) hanging = 0;
  if (patch.hangingPt !== void 0 && hanging > 0) firstLine = 0;
  return {
    leftPt: patch.leftPt !== void 0 ? clampPt(patch.leftPt) : base.leftPt,
    rightPt: patch.rightPt !== void 0 ? clampPt(patch.rightPt) : base.rightPt,
    firstLinePt: firstLine,
    hangingPt: hanging
  };
}
function indentIsZero(i) {
  return i.leftPt === 0 && i.rightPt === 0 && i.firstLinePt === 0 && i.hangingPt === 0;
}
function mergeParaProps(cur, patch) {
  const next = {
    textAlign: cur.textAlign,
    indent: cur.indent,
    lineSpacing: cur.lineSpacing,
    spaceBeforePt: cur.spaceBeforePt,
    spaceAfterPt: cur.spaceAfterPt,
    tabStops: cur.tabStops,
    borders: cur.borders,
    shading: cur.shading,
    dropCap: cur.dropCap
  };
  if (patch.textAlign !== void 0) next.textAlign = patch.textAlign;
  if (patch.indent !== void 0) {
    const merged = mergeIndent(cur.indent, patch.indent);
    next.indent = indentIsZero(merged) ? void 0 : merged;
  }
  if (patch.lineSpacing !== void 0) next.lineSpacing = patch.lineSpacing ?? void 0;
  if (patch.spaceBeforePt !== void 0) {
    next.spaceBeforePt = patch.spaceBeforePt == null ? void 0 : clampPt(patch.spaceBeforePt);
  }
  if (patch.spaceAfterPt !== void 0) {
    next.spaceAfterPt = patch.spaceAfterPt == null ? void 0 : clampPt(patch.spaceAfterPt);
  }
  if (patch.tabStops !== void 0) {
    next.tabStops = patch.tabStops.length > 0 ? patch.tabStops : void 0;
  }
  if (patch.borders !== void 0) next.borders = patch.borders ?? void 0;
  if (patch.shading !== void 0) {
    next.shading = patch.shading == null || patch.shading === "" ? void 0 : patch.shading;
  }
  if (patch.dropCap !== void 0) next.dropCap = patch.dropCap ?? void 0;
  const out = {
    ...next.textAlign !== void 0 ? { textAlign: next.textAlign } : {},
    ...next.indent !== void 0 ? { indent: next.indent } : {},
    ...next.lineSpacing !== void 0 ? { lineSpacing: next.lineSpacing } : {},
    ...next.spaceBeforePt !== void 0 ? { spaceBeforePt: next.spaceBeforePt } : {},
    ...next.spaceAfterPt !== void 0 ? { spaceAfterPt: next.spaceAfterPt } : {},
    ...next.tabStops !== void 0 ? { tabStops: next.tabStops } : {},
    ...next.borders !== void 0 ? { borders: next.borders } : {},
    ...next.shading !== void 0 ? { shading: next.shading } : {},
    ...next.dropCap !== void 0 ? { dropCap: next.dropCap } : {}
  };
  return out;
}
function currentLeftIndentPt(props) {
  return props.indent?.leftPt ?? 0;
}

// src/para-transforms.ts
function ext(module) {
  return module;
}
function fragmentParaProps(frag) {
  return frag.para ?? {};
}
function supportsParaProps(module) {
  return ext(module).supportsParaProps === true;
}
function setParaPropsOp(module, frag, patch) {
  if (!supportsParaProps(module)) return null;
  const next = mergeParaProps(fragmentParaProps(frag), patch);
  const out = { ...frag, para: next };
  return out;
}

// src/fragment-controller.ts
function createFragmentController(surface, initial) {
  let frag = initial.value;
  let opts = initial;
  function applyChrome() {
    surface.classList.add("ela-fragment");
    surface.setAttribute("data-testid", opts.testId ?? FRAGMENT_SURFACE_TESTID);
    const editable = !opts.readOnly;
    surface.setAttribute("data-editable", String(editable));
    surface.toggleAttribute("data-readonly", !editable);
    if (editable) surface.setAttribute("contenteditable", "true");
    else surface.removeAttribute("contenteditable");
  }
  function render() {
    renderFragmentInto(opts.module, surface, frag);
    applyChrome();
  }
  function commit(next, restore) {
    frag = next;
    render();
    opts.onChange?.(frag);
    if (restore != null) {
      const max = fragmentByteLength(frag);
      if (typeof restore === "number") placeCaretAtByte(surface, Math.min(restore, max));
      else selectByteRange(surface, Math.min(restore.start, max), Math.min(restore.end, max));
    }
    return frag;
  }
  let remembered = null;
  function liveRange() {
    const r = selectionByteRange(surface);
    if (r) remembered = r;
    return r;
  }
  function targetRange(range) {
    return range ?? liveRange() ?? remembered;
  }
  function onSelectionChange() {
    liveRange();
  }
  surface.ownerDocument.addEventListener("selectionchange", onSelectionChange);
  function onBeforeInput(ev) {
    if (opts.readOnly) return;
    const edit = fragmentTextEditFromBeforeInput(ev, surface);
    if (!edit) return;
    ev.preventDefault();
    api.applyTextEdit(edit);
  }
  surface.addEventListener("beforeinput", onBeforeInput);
  const SHORTCUT_MARKS = {
    b: "bold",
    i: "italic",
    u: "underline"
  };
  function onKeyDown(ev) {
    if (opts.readOnly) return;
    const e = ev;
    if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
    const mark = SHORTCUT_MARKS[e.key.toLowerCase()];
    if (!mark) return;
    const range = selectionByteRange(surface);
    if (!range || range.end <= range.start) return;
    e.preventDefault();
    api.toggle({ type: mark }, range);
  }
  surface.addEventListener("keydown", onKeyDown);
  const api = {
    render,
    setValue(value) {
      frag = value;
      render();
    },
    setOptions(patch) {
      opts = { ...opts, ...patch };
      if (patch.value) frag = patch.value;
      render();
    },
    destroy() {
      surface.removeEventListener("beforeinput", onBeforeInput);
      surface.removeEventListener("keydown", onKeyDown);
      surface.ownerDocument.removeEventListener("selectionchange", onSelectionChange);
    },
    getValue() {
      return frag;
    },
    get root() {
      return surface;
    },
    applyTextEdit(edit) {
      if (opts.readOnly) return null;
      const res = editFragmentText(opts.module, frag, {
        start: edit.at,
        end: edit.at + edit.deleteBytes,
        replacement: edit.insert
      });
      if (!res.ok) {
        warn({
          fn: "applyTextEdit",
          code: "WEB_ELABORATE_EDITOR_VERB_FAILED",
          message: `${LIB}: dz_transform_edit_text failed (status ${res.errCode})`
        });
        return null;
      }
      return commit(res.fragment, edit.at + new TextEncoder().encode(edit.insert).length);
    },
    format(mark, range) {
      if (opts.readOnly) return null;
      const r = targetRange(range);
      if (!r || r.end <= r.start) return null;
      const res = applyStyleMark(opts.module, frag, mark, r);
      if (!res.ok) return null;
      return commit(res.fragment, r);
    },
    toggle(mark, range) {
      if (opts.readOnly) return null;
      const r = targetRange(range);
      if (!r || r.end <= r.start) return null;
      const res = toggleStyleMark(opts.module, frag, mark, r);
      if (!res.ok) return null;
      return commit(res.fragment, r);
    },
    clearFormatting(range) {
      if (opts.readOnly) return null;
      const r = targetRange(range);
      if (!r || r.end <= r.start) return null;
      const next = clearMarksOp(opts.module, frag, r);
      return next ? commit(next, r) : null;
    },
    changeCase(mode, range) {
      if (opts.readOnly) return null;
      const r = targetRange(range);
      if (!r || r.end <= r.start) return null;
      const next = changeCaseOp(opts.module, frag, r, mode);
      return next ? commit(next, r) : null;
    },
    comment(commentId, range) {
      if (!commentId) return null;
      const r = targetRange(range);
      if (!r || r.end <= r.start) return null;
      const res = applyCommentMark(opts.module, frag, { commentId, start: r.start, end: r.end });
      if (!res.ok) return null;
      return commit(res.fragment, r);
    },
    setKind(kind, level) {
      if (opts.readOnly) return null;
      const keep = targetRange();
      return commit(setFragmentKind(frag, kind, level), keep ?? void 0);
    },
    setParaProps(patch) {
      if (opts.readOnly) return null;
      const keep = targetRange();
      const next = setParaPropsOp(opts.module, frag, patch);
      return next ? commit(next, keep ?? void 0) : null;
    },
    selectionRange() {
      return targetRange();
    },
    toHtml() {
      return renderFragmentToHtml(opts.module, frag);
    }
  };
  render();
  return api;
}

// src/use-fragment-editor.ts
function useFragmentEditor(props) {
  const { module, value, readOnly, onChange } = props;
  const containerRef = useRef(null);
  const controllerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const controller = createFragmentController(container, {
      module,
      value,
      readOnly,
      testId: props.testId,
      onChange: (frag) => onChangeRef.current?.(frag)
    });
    controllerRef.current = controller;
    return () => {
      controller.destroy();
      controllerRef.current = null;
    };
  }, []);
  useEffect(() => {
    const c = controllerRef.current;
    if (c && c.getValue() !== value) c.setValue(value);
  }, [value]);
  useEffect(() => {
    controllerRef.current?.setOptions({ readOnly });
  }, [readOnly]);
  const call = useCallback((fn, fallback) => {
    const c = controllerRef.current;
    return c ? fn(c) : fallback;
  }, []);
  const handle = useMemo(
    () => ({
      getValue: () => call((c) => c.getValue(), value),
      applyTextEdit: (edit) => call((c) => c.applyTextEdit(edit), null),
      format: (mark, range) => call((c) => c.format(mark, range), null),
      toggle: (mark, range) => call((c) => c.toggle(mark, range), null),
      clearFormatting: (range) => call((c) => c.clearFormatting(range), null),
      changeCase: (mode, range) => call((c) => c.changeCase(mode, range), null),
      comment: (commentId, range) => call((c) => c.comment(commentId, range), null),
      setKind: (kind, level) => call((c) => c.setKind(kind, level), null),
      setParaProps: (patch) => call((c) => c.setParaProps(patch), null),
      selectionRange: () => call((c) => c.selectionRange(), null),
      toHtml: () => call((c) => c.toHtml(), ""),
      get root() {
        return controllerRef.current?.root ?? null;
      }
    }),
    // `value` only feeds the pre-mount fallback; the live controller owns state.
    [call, value]
  );
  return { containerRef, handle };
}
var CommentEditor = forwardRef(
  function CommentEditor2(props, ref) {
    const { containerRef, handle } = useFragmentEditor(props);
    useImperativeHandle(ref, () => handle, [handle]);
    const className = props.className ? `ela-fragment ${props.className}` : "ela-fragment";
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref: containerRef,
        className,
        "data-testid": props.testId ?? "docz-fragment",
        "data-editable": String(!props.readOnly)
      }
    );
  }
);
function createClipboardController(deps) {
  const { container } = deps;
  const doc = container.ownerDocument;
  let readPermission = "unknown";
  const unwatch = watchClipboardReadPermission((state) => {
    readPermission = state;
  });
  function writeTo(ev) {
    const blocks = deps.selectionBlocks();
    if (blocks.length === 0) return false;
    const f = clipboardFlavours(deps.getModule(), blocks);
    if (!writeFlavoursTo(ev.clipboardData, f)) return false;
    ev.preventDefault();
    return true;
  }
  function readFrom(ev) {
    if (!deps.canEdit()) return false;
    const data = ev.clipboardData;
    const blocks = parseClipboard(
      {
        payload: data?.getData(CLIPBOARD_MIME_DOCZ) ?? null,
        html: data?.getData(CLIPBOARD_MIME_HTML) ?? null,
        plain: data?.getData(CLIPBOARD_MIME_PLAIN) ?? null
      },
      { module: deps.getModule() }
    );
    if (blocks.length === 0) return false;
    deps.beforeMutate();
    deps.insertBlocks(blocks);
    return true;
  }
  function handleCopy(ev) {
    return writeTo(ev);
  }
  function handleCut(ev) {
    if (!deps.canEdit()) return false;
    if (!writeTo(ev)) return false;
    deps.beforeMutate();
    deps.deleteSelection();
    return true;
  }
  const onCopy = (ev) => {
    handleCopy(ev);
  };
  const onCut = (ev) => {
    handleCut(ev);
  };
  const onPaste = (ev) => {
    if (!deps.canEdit()) return;
    ev.preventDefault();
    readFrom(ev);
  };
  container.addEventListener("copy", onCopy);
  container.addEventListener("cut", onCut);
  container.addEventListener("paste", onPaste);
  function runNative(type) {
    if (deps.selectionBlocks().length === 0) return "empty";
    if (type === "cut" && !deps.canEdit()) return "empty";
    return runNativeClipboardCommand({
      doc,
      type,
      handle: type === "copy" ? handleCopy : handleCut
    });
  }
  function copyToClipboard() {
    return runNative("copy");
  }
  function cutToClipboard() {
    return runNative("cut");
  }
  async function writeSelectionAsync() {
    const blocks = deps.selectionBlocks();
    if (blocks.length === 0) return "empty";
    if (!asyncClipboardWritePresent()) return "unsupported";
    const f = clipboardFlavours(deps.getModule(), blocks);
    return await writeFlavoursToSystem(f) ? "ok" : "denied";
  }
  async function copyToSystem() {
    const native = copyToClipboard();
    if (native !== "unsupported") return native;
    return writeSelectionAsync();
  }
  async function cutToSystem() {
    if (!deps.canEdit()) return "empty";
    const native = cutToClipboard();
    if (native !== "unsupported") return native;
    const out = await writeSelectionAsync();
    if (out !== "ok") return out;
    deps.beforeMutate();
    deps.deleteSelection();
    return "ok";
  }
  async function pasteFromSystem() {
    if (!deps.canEdit() || !asyncClipboardReadable()) return "unsupported";
    if (readPermission === "denied") return "denied";
    try {
      const items = await navigator.clipboard.read();
      let payload = null;
      let html = null;
      let plain = null;
      for (const item of items) {
        for (const type of item.types) {
          const text = await (await item.getType(type)).text();
          if (type.endsWith(CLIPBOARD_MIME_DOCZ)) payload ??= text;
          else if (type === CLIPBOARD_MIME_HTML) html ??= text;
          else if (type === CLIPBOARD_MIME_PLAIN) plain ??= text;
        }
      }
      const blocks = parseClipboard({ payload, html, plain }, { module: deps.getModule() });
      if (blocks.length === 0) return "empty";
      deps.beforeMutate();
      deps.insertBlocks(blocks);
      return "ok";
    } catch {
      return "denied";
    }
  }
  return {
    copy: () => {
      void copyToSystem();
    },
    cut: () => {
      void cutToSystem();
    },
    paste: () => {
      void pasteFromSystem();
    },
    copyToClipboard,
    cutToClipboard,
    copyToSystem,
    cutToSystem,
    pasteFromSystem,
    canCopy: () => clipboardWriteSupported(doc, "copy"),
    canCut: () => deps.canEdit() && clipboardWriteSupported(doc, "cut"),
    canPaste: () => deps.canEdit() && asyncClipboardReadable() && readPermission !== "denied",
    pastePermission: () => readPermission,
    destroy: () => {
      unwatch();
      container.removeEventListener("copy", onCopy);
      container.removeEventListener("cut", onCut);
      container.removeEventListener("paste", onPaste);
    }
  };
}

// src/char-marks.ts
var FONT_FAMILY_STACK = [
  "Calibri",
  "Arial",
  "Times New Roman",
  "Georgia",
  "Courier New",
  "Verdana",
  "Cambria",
  "Consolas"
];
var FONT_SIZE_STOPS = [8, 9, 10, 11, 12, 14, 18, 24, 36, 48, 72];
var UNDERLINE_STYLE_OPTIONS = ["single", "double", "dotted", "dashed", "wavy"];
var LETTER_SPACING_STOPS = [
  -1,
  -0.75,
  -0.5,
  -0.25,
  0,
  0.25,
  0.5,
  0.75,
  1,
  1.5,
  2,
  3
];
function letterSpacingLabel(pt) {
  if (pt === 0) return "Normal";
  const magnitude = String(Math.abs(pt));
  return pt < 0 ? `Condensed by ${magnitude} pt` : `Expanded by ${magnitude} pt`;
}
var CHANGE_CASE_MODES = [
  "upper",
  "lower",
  "title",
  "sentence",
  "toggle"
];
var DEFAULT_FONT_SIZE_PT = 10;
function stepFontSize(current, dir) {
  const cur = current ?? DEFAULT_FONT_SIZE_PT;
  const last = FONT_SIZE_STOPS[FONT_SIZE_STOPS.length - 1] ?? cur;
  const first = FONT_SIZE_STOPS[0] ?? cur;
  if (dir > 0) return FONT_SIZE_STOPS.find((s) => s > cur) ?? last;
  return [...FONT_SIZE_STOPS].reverse().find((s) => s < cur) ?? first;
}
var MAX_FONT_SIZE_PT = 1638;
function parseFontSizeInput(raw) {
  const t = raw.trim().toLowerCase();
  const m = /^(\d+(?:\.\d+)?|\.\d+)(?:pt)?$/.exec(t);
  if (!m?.[1]) return null;
  const pt = Number(m[1]);
  if (!Number.isFinite(pt) || pt <= 0 || pt > MAX_FONT_SIZE_PT) return null;
  return Math.round(pt * 10) / 10;
}
function Svg(props) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      className: "ela-icon",
      viewBox: "0 0 24 24",
      width: "18",
      height: "18",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": "true",
      focusable: "false",
      children: props.children
    }
  );
}
var GLYPHS = {
  bold: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "M14 12a4 4 0 0 0 0-8H7v8" }),
    /* @__PURE__ */ jsx("path", { d: "M15 20a4 4 0 0 0 0-8H7v8Z" })
  ] }),
  italic: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "19", x2: "10", y1: "4", y2: "4" }),
    /* @__PURE__ */ jsx("line", { x1: "14", x2: "5", y1: "20", y2: "20" }),
    /* @__PURE__ */ jsx("line", { x1: "15", x2: "9", y1: "4", y2: "20" })
  ] }),
  underline: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "M6 4v6a6 6 0 0 0 12 0V4" }),
    /* @__PURE__ */ jsx("line", { x1: "4", x2: "20", y1: "20", y2: "20" })
  ] }),
  strikeout: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "M16 4H9a3 3 0 0 0-2.83 4" }),
    /* @__PURE__ */ jsx("path", { d: "M14 12a4 4 0 0 1 0 8H6" }),
    /* @__PURE__ */ jsx("line", { x1: "4", x2: "20", y1: "12", y2: "12" })
  ] }),
  code: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("polyline", { points: "16 18 22 12 16 6" }),
    /* @__PURE__ */ jsx("polyline", { points: "8 6 2 12 8 18" })
  ] }),
  superscript: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "m4 19 8-8" }),
    /* @__PURE__ */ jsx("path", { d: "M12 19 4 11" }),
    /* @__PURE__ */ jsx("path", { d: "M20 12h-4c0-1.5.44-2 1.5-2.5S20 8.33 20 7c0-.47-.17-.93-.48-1.29a2.1 2.1 0 0 0-2.62-.44c-.42.24-.74.62-.9 1.06" })
  ] }),
  subscript: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "m4 5 8 8" }),
    /* @__PURE__ */ jsx("path", { d: "m12 5-8 8" }),
    /* @__PURE__ */ jsx("path", { d: "M20 19h-4c0-1.5.44-2 1.5-2.5S20 15.33 20 14c0-.47-.17-.93-.48-1.29a2.1 2.1 0 0 0-2.62-.44c-.42.24-.74.62-.9 1.06" })
  ] }),
  paragraph: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "M13 4v16" }),
    /* @__PURE__ */ jsx("path", { d: "M17 4v16" }),
    /* @__PURE__ */ jsx("path", { d: "M19 4H9.5a4.5 4.5 0 0 0 0 9H13" })
  ] }),
  link: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" }),
    /* @__PURE__ */ jsx("path", { d: "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" })
  ] }),
  color: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "m6 16 6-12 6 12" }),
    /* @__PURE__ */ jsx("path", { d: "M8 12h8" }),
    /* @__PURE__ */ jsx("line", { x1: "4", x2: "20", y1: "20", y2: "20" })
  ] }),
  highlight: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "m9 11-6 6v3h9l3-3" }),
    /* @__PURE__ */ jsx("path", { d: "m22 12-4.6 4.6a2 2 0 0 1-2.8 0l-5.2-5.2a2 2 0 0 1 0-2.8L14 4" })
  ] }),
  "bullet-list": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "8", x2: "21", y1: "6", y2: "6" }),
    /* @__PURE__ */ jsx("line", { x1: "8", x2: "21", y1: "12", y2: "12" }),
    /* @__PURE__ */ jsx("line", { x1: "8", x2: "21", y1: "18", y2: "18" }),
    /* @__PURE__ */ jsx("line", { x1: "3", x2: "3.01", y1: "6", y2: "6" }),
    /* @__PURE__ */ jsx("line", { x1: "3", x2: "3.01", y1: "12", y2: "12" }),
    /* @__PURE__ */ jsx("line", { x1: "3", x2: "3.01", y1: "18", y2: "18" })
  ] }),
  "ordered-list": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "10", x2: "21", y1: "6", y2: "6" }),
    /* @__PURE__ */ jsx("line", { x1: "10", x2: "21", y1: "12", y2: "12" }),
    /* @__PURE__ */ jsx("line", { x1: "10", x2: "21", y1: "18", y2: "18" }),
    /* @__PURE__ */ jsx("path", { d: "M4 6h1v4" }),
    /* @__PURE__ */ jsx("path", { d: "M4 10h2" }),
    /* @__PURE__ */ jsx("path", { d: "M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" })
  ] }),
  blockquote: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "M9 6H5a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h3a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2" }),
    /* @__PURE__ */ jsx("path", { d: "M20 6h-4a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h3a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2" })
  ] }),
  "code-block": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1" }),
    /* @__PURE__ */ jsx("path", { d: "M16 21h1a2 2 0 0 0 2-2v-5c0-1.1.9-2 2-2a2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1" })
  ] }),
  table: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2" }),
    /* @__PURE__ */ jsx("path", { d: "M3 9h18" }),
    /* @__PURE__ */ jsx("path", { d: "M3 15h18" }),
    /* @__PURE__ */ jsx("path", { d: "M12 3v18" })
  ] }),
  image: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2", ry: "2" }),
    /* @__PURE__ */ jsx("circle", { cx: "9", cy: "9", r: "2" }),
    /* @__PURE__ */ jsx("path", { d: "m21 15-3.09-3.09a2 2 0 0 0-2.82 0L6 21" })
  ] }),
  // ─── W1 character formatting (§4.3) ─────────────────────────────────────────
  // Letter glyphs override the stroke wrapper with a filled `<text>`.
  "font-family": /* @__PURE__ */ jsx(
    "text",
    {
      x: "12",
      y: "17.5",
      fontSize: "18",
      fontWeight: "600",
      fill: "currentColor",
      stroke: "none",
      textAnchor: "middle",
      fontFamily: "Georgia, 'Times New Roman', serif",
      children: "Aa"
    }
  ),
  "font-size": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "text",
      {
        x: "8",
        y: "18.5",
        fontSize: "17",
        fontWeight: "700",
        fill: "currentColor",
        stroke: "none",
        textAnchor: "middle",
        fontFamily: "system-ui, -apple-system, sans-serif",
        children: "A"
      }
    ),
    /* @__PURE__ */ jsx(
      "text",
      {
        x: "17.5",
        y: "18.5",
        fontSize: "11",
        fontWeight: "700",
        fill: "currentColor",
        stroke: "none",
        textAnchor: "middle",
        fontFamily: "system-ui, -apple-system, sans-serif",
        children: "A"
      }
    )
  ] }),
  "all-caps": /* @__PURE__ */ jsx(
    "text",
    {
      x: "12",
      y: "17.5",
      fontSize: "15",
      fontWeight: "700",
      letterSpacing: "0.5",
      fill: "currentColor",
      stroke: "none",
      textAnchor: "middle",
      fontFamily: "system-ui, -apple-system, sans-serif",
      children: "AA"
    }
  ),
  "small-caps": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "text",
      {
        x: "8",
        y: "17.5",
        fontSize: "16",
        fontWeight: "700",
        fill: "currentColor",
        stroke: "none",
        textAnchor: "middle",
        fontFamily: "system-ui, -apple-system, sans-serif",
        children: "A"
      }
    ),
    /* @__PURE__ */ jsx(
      "text",
      {
        x: "16.5",
        y: "17.5",
        fontSize: "11",
        fontWeight: "700",
        fill: "currentColor",
        stroke: "none",
        textAnchor: "middle",
        fontFamily: "system-ui, -apple-system, sans-serif",
        children: "A"
      }
    )
  ] }),
  "underline-style": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "M6 3v7a6 6 0 0 0 12 0V3" }),
    /* @__PURE__ */ jsx("path", { d: "M4 19q1.6 -2.4 3.2 0t3.2 0 3.2 0 3.2 0" })
  ] }),
  "clear-format": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21" }),
    /* @__PURE__ */ jsx("path", { d: "M22 21H7" }),
    /* @__PURE__ */ jsx("path", { d: "m5 11 9 9" })
  ] }),
  "change-case": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "M3 15 7 5l4 10" }),
    /* @__PURE__ */ jsx("path", { d: "M4.5 12h5" }),
    /* @__PURE__ */ jsx("circle", { cx: "18", cy: "12", r: "3" }),
    /* @__PURE__ */ jsx("path", { d: "M21 9v6" })
  ] }),
  // ─── W2 paragraph geometry (§2.4) ───────────────────────────────────────────
  "align-left": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "3", x2: "21", y1: "6", y2: "6" }),
    /* @__PURE__ */ jsx("line", { x1: "3", x2: "15", y1: "12", y2: "12" }),
    /* @__PURE__ */ jsx("line", { x1: "3", x2: "18", y1: "18", y2: "18" })
  ] }),
  "align-center": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "3", x2: "21", y1: "6", y2: "6" }),
    /* @__PURE__ */ jsx("line", { x1: "6", x2: "18", y1: "12", y2: "12" }),
    /* @__PURE__ */ jsx("line", { x1: "4", x2: "20", y1: "18", y2: "18" })
  ] }),
  "align-right": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "3", x2: "21", y1: "6", y2: "6" }),
    /* @__PURE__ */ jsx("line", { x1: "9", x2: "21", y1: "12", y2: "12" }),
    /* @__PURE__ */ jsx("line", { x1: "6", x2: "21", y1: "18", y2: "18" })
  ] }),
  "align-justify": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "3", x2: "21", y1: "6", y2: "6" }),
    /* @__PURE__ */ jsx("line", { x1: "3", x2: "21", y1: "12", y2: "12" }),
    /* @__PURE__ */ jsx("line", { x1: "3", x2: "21", y1: "18", y2: "18" })
  ] }),
  indent: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "21", x2: "3", y1: "6", y2: "6" }),
    /* @__PURE__ */ jsx("line", { x1: "21", x2: "11", y1: "12", y2: "12" }),
    /* @__PURE__ */ jsx("line", { x1: "21", x2: "3", y1: "18", y2: "18" }),
    /* @__PURE__ */ jsx("polyline", { points: "3 10 7 12 3 14" })
  ] }),
  outdent: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "21", x2: "3", y1: "6", y2: "6" }),
    /* @__PURE__ */ jsx("line", { x1: "21", x2: "11", y1: "12", y2: "12" }),
    /* @__PURE__ */ jsx("line", { x1: "21", x2: "3", y1: "18", y2: "18" }),
    /* @__PURE__ */ jsx("polyline", { points: "7 10 3 12 7 14" })
  ] }),
  "line-spacing": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "10", x2: "21", y1: "6", y2: "6" }),
    /* @__PURE__ */ jsx("line", { x1: "10", x2: "21", y1: "12", y2: "12" }),
    /* @__PURE__ */ jsx("line", { x1: "10", x2: "21", y1: "18", y2: "18" }),
    /* @__PURE__ */ jsx("path", { d: "M4 5v14" }),
    /* @__PURE__ */ jsx("polyline", { points: "2 7 4 5 6 7" }),
    /* @__PURE__ */ jsx("polyline", { points: "2 17 4 19 6 17" })
  ] }),
  "para-spacing": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "3", x2: "21", y1: "4", y2: "4" }),
    /* @__PURE__ */ jsx("line", { x1: "3", x2: "21", y1: "20", y2: "20" }),
    /* @__PURE__ */ jsx("path", { d: "M12 8v8" }),
    /* @__PURE__ */ jsx("polyline", { points: "9 11 12 8 15 11" }),
    /* @__PURE__ */ jsx("polyline", { points: "9 13 12 16 15 13" })
  ] }),
  tabs: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("path", { d: "M3 5v14" }),
    /* @__PURE__ */ jsx("line", { x1: "3", x2: "21", y1: "8", y2: "8" }),
    /* @__PURE__ */ jsx("path", { d: "M9 8v3" }),
    /* @__PURE__ */ jsx("path", { d: "M15 8v3" }),
    /* @__PURE__ */ jsx("polyline", { points: "17 15 20 12 17 9" })
  ] }),
  // ─── W3 tables (§5.3) ───────────────────────────────────────────────────────
  "table-row-above": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2" }),
    /* @__PURE__ */ jsx("path", { d: "M3 9h18" }),
    /* @__PURE__ */ jsx("path", { d: "M12 12v6" }),
    /* @__PURE__ */ jsx("polyline", { points: "9 15 12 12 15 15" })
  ] }),
  "table-row-below": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2" }),
    /* @__PURE__ */ jsx("path", { d: "M3 15h18" }),
    /* @__PURE__ */ jsx("path", { d: "M12 6v6" }),
    /* @__PURE__ */ jsx("polyline", { points: "9 9 12 12 15 9" })
  ] }),
  // The whole table as ONE object: the grid, filled, so it reads as "all of it"
  // rather than "a row of it".
  "table-select": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2", fill: "currentColor", fillOpacity: "0.22" }),
    /* @__PURE__ */ jsx("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2" }),
    /* @__PURE__ */ jsx("path", { d: "M3 9h18" }),
    /* @__PURE__ */ jsx("path", { d: "M3 15h18" }),
    /* @__PURE__ */ jsx("path", { d: "M12 3v18" })
  ] }),
  // The grid with a cross through it — destructive, and unmistakably about the
  // WHOLE table rather than the row/column glyphs above.
  "table-delete": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2" }),
    /* @__PURE__ */ jsx("path", { d: "M3 9h18" }),
    /* @__PURE__ */ jsx("path", { d: "M3 15h18" }),
    /* @__PURE__ */ jsx("path", { d: "M12 3v18" }),
    /* @__PURE__ */ jsx("line", { x1: "5", x2: "19", y1: "19", y2: "5" })
  ] }),
  "table-row-delete": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2" }),
    /* @__PURE__ */ jsx("path", { d: "M3 9h18" }),
    /* @__PURE__ */ jsx("path", { d: "M3 15h18" }),
    /* @__PURE__ */ jsx("line", { x1: "8", x2: "16", y1: "12", y2: "12" })
  ] }),
  "table-col-left": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2" }),
    /* @__PURE__ */ jsx("path", { d: "M9 3v18" }),
    /* @__PURE__ */ jsx("path", { d: "M12 12h6" }),
    /* @__PURE__ */ jsx("polyline", { points: "15 9 12 12 15 15" })
  ] }),
  "table-col-right": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2" }),
    /* @__PURE__ */ jsx("path", { d: "M15 3v18" }),
    /* @__PURE__ */ jsx("path", { d: "M6 12h6" }),
    /* @__PURE__ */ jsx("polyline", { points: "9 9 12 12 9 15" })
  ] }),
  "table-col-delete": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2" }),
    /* @__PURE__ */ jsx("path", { d: "M9 3v18" }),
    /* @__PURE__ */ jsx("path", { d: "M15 3v18" }),
    /* @__PURE__ */ jsx("line", { x1: "12", x2: "12", y1: "8", y2: "16" })
  ] }),
  "table-merge": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2" }),
    /* @__PURE__ */ jsx("path", { d: "M3 12h6" }),
    /* @__PURE__ */ jsx("path", { d: "M15 12h6" }),
    /* @__PURE__ */ jsx("polyline", { points: "7 9 10 12 7 15" }),
    /* @__PURE__ */ jsx("polyline", { points: "17 9 14 12 17 15" })
  ] }),
  "table-split": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2" }),
    /* @__PURE__ */ jsx("path", { d: "M12 3v6" }),
    /* @__PURE__ */ jsx("path", { d: "M12 15v6" }),
    /* @__PURE__ */ jsx("polyline", { points: "9 11 12 9 15 11" }),
    /* @__PURE__ */ jsx("polyline", { points: "9 13 12 15 15 13" })
  ] }),
  "valign-top": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "4", x2: "20", y1: "4", y2: "4" }),
    /* @__PURE__ */ jsx("rect", { width: "8", height: "12", x: "8", y: "7", rx: "1" })
  ] }),
  "valign-middle": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "4", x2: "20", y1: "12", y2: "12" }),
    /* @__PURE__ */ jsx("rect", { width: "8", height: "8", x: "8", y: "4", rx: "1" }),
    /* @__PURE__ */ jsx("rect", { width: "8", height: "4", x: "8", y: "15", rx: "1" })
  ] }),
  "valign-bottom": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "4", x2: "20", y1: "20", y2: "20" }),
    /* @__PURE__ */ jsx("rect", { width: "8", height: "12", x: "8", y: "5", rx: "1" })
  ] }),
  "cell-shade": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "16", height: "16", x: "4", y: "4", rx: "1" }),
    /* @__PURE__ */ jsx("path", { d: "M4 12h16" }),
    /* @__PURE__ */ jsx("path", { d: "M12 4v16" }),
    /* @__PURE__ */ jsx("path", { d: "M12 12h8v8h-8z", fill: "currentColor", stroke: "none" })
  ] }),
  "cell-border": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "16", height: "16", x: "4", y: "4", rx: "1" }),
    /* @__PURE__ */ jsx("path", { d: "M4 12h16" }),
    /* @__PURE__ */ jsx("path", { d: "M12 4v16" })
  ] }),
  "table-header": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2" }),
    /* @__PURE__ */ jsx("path", { d: "M3 9h18", fill: "currentColor" }),
    /* @__PURE__ */ jsx("rect", { width: "18", height: "6", x: "3", y: "3", fill: "currentColor", stroke: "none", opacity: "0.35" })
  ] }),
  // ─── W4 lists & numbering (§7) ───────────────────────────────────────────────
  "list-format": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "10", x2: "21", y1: "6", y2: "6" }),
    /* @__PURE__ */ jsx("line", { x1: "10", x2: "21", y1: "12", y2: "12" }),
    /* @__PURE__ */ jsx("line", { x1: "10", x2: "21", y1: "18", y2: "18" }),
    /* @__PURE__ */ jsx("path", { d: "M4 5h1v4" }),
    /* @__PURE__ */ jsx("polyline", { points: "4 14 6 16 4 18", transform: "translate(-1 -2)" })
  ] }),
  "task-list": /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("line", { x1: "11", x2: "21", y1: "6", y2: "6" }),
    /* @__PURE__ */ jsx("line", { x1: "11", x2: "21", y1: "12", y2: "12" }),
    /* @__PURE__ */ jsx("line", { x1: "11", x2: "21", y1: "18", y2: "18" }),
    /* @__PURE__ */ jsx("rect", { width: "6", height: "6", x: "3", y: "3", rx: "1" }),
    /* @__PURE__ */ jsx("polyline", { points: "4 6 5.5 7.5 8 4.5", transform: "translate(-1 -1)" }),
    /* @__PURE__ */ jsx("rect", { width: "6", height: "6", x: "3", y: "15", rx: "1" })
  ] })
};
function ToolbarIcon({ name }) {
  const heading = /^heading-([1-6])$/.exec(name);
  if (heading) return /* @__PURE__ */ jsx(HeadingIcon, { level: Number(heading[1]) });
  return /* @__PURE__ */ jsx(Svg, { children: GLYPHS[name] });
}
function HeadingIcon({ level }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      className: "ela-icon ela-icon--heading",
      viewBox: "0 0 24 24",
      width: "18",
      height: "18",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": "true",
      focusable: "false",
      children: [
        /* @__PURE__ */ jsx("path", { d: "M5 5v14" }),
        /* @__PURE__ */ jsx("path", { d: "M13 5v14" }),
        /* @__PURE__ */ jsx("path", { d: "M5 12h8" }),
        /* @__PURE__ */ jsx(
          "text",
          {
            x: "18",
            y: "20",
            fontSize: "11",
            fontWeight: "700",
            fill: "currentColor",
            stroke: "none",
            textAnchor: "middle",
            fontFamily: "system-ui, -apple-system, sans-serif",
            children: level
          }
        )
      ]
    }
  );
}
function fragmentPlainText(frag) {
  let out = "";
  for (const run of frag.runs) out += run.text;
  return out;
}
function fragmentByteLen(frag) {
  return byteLen$1(fragmentPlainText(frag));
}
function fragmentTextInRange(frag, start, end) {
  const bytes = new TextEncoder().encode(fragmentPlainText(frag));
  const lo = Math.max(0, Math.min(start, bytes.length));
  const hi = Math.max(lo, Math.min(end, bytes.length));
  return new TextDecoder().decode(bytes.slice(lo, hi));
}
function linkExtentAt(frag, offset) {
  const spans = [];
  let at = 0;
  for (const run of frag.runs) {
    const len = byteLen$1(run.text);
    const link = run.marks.find((m) => m.type === "link");
    spans.push({ start: at, end: at + len, href: link?.type === "link" ? link.href : null });
    at += len;
  }
  const hit = spans.findIndex((s) => s.end > s.start && s.start <= offset && offset < s.end);
  if (hit === -1) return null;
  const href = spans[hit]?.href;
  if (href == null) return null;
  let lo = hit;
  let hi = hit;
  while (lo > 0 && spans[lo - 1]?.href === href) lo -= 1;
  while (hi + 1 < spans.length && spans[hi + 1]?.href === href) hi += 1;
  return {
    range: { start: spans[lo]?.start ?? 0, end: spans[hi]?.end ?? 0 },
    href
  };
}

// src/list-transforms.ts
function ext2(module) {
  return module;
}
function supportsLists(module) {
  return ext2(module).supportsListVerbs === true;
}

// src/table/border-model.ts
var BORDER_STYLE_OPTIONS = [
  "solid",
  "dashed",
  "dotted",
  "double"
];
var BORDER_WIDTH_OPTIONS = [0.5, 1, 1.5, 2, 3];
var BORDER_COLOR_PRESETS = [
  "#000000",
  "#434343",
  "#666666",
  "#999999",
  "#b7b7b7",
  "#cccccc",
  "#d9d9d9",
  "#ffffff",
  "#980000",
  "#ff0000",
  "#ff9900",
  "#ffff00",
  "#00ff00",
  "#00ffff",
  "#4a86e8",
  "#0000ff",
  "#9900ff",
  "#ff00ff"
];
var EMPTY = { top: false, right: false, bottom: false, left: false };
var BOX = { top: true, right: true, bottom: true, left: true };
var BORDER_PRESETS = {
  all: BOX,
  none: EMPTY,
  outer: BOX,
  inner: { top: true, right: false, bottom: true, left: false }
};
function buildCellBorders(sides, line) {
  const dto = {};
  if (sides.top) dto.top = line;
  if (sides.right) dto.right = line;
  if (sides.bottom) dto.bottom = line;
  if (sides.left) dto.left = line;
  return dto.top || dto.right || dto.bottom || dto.left ? dto : null;
}
function serializeBorderPayload(payload) {
  return JSON.stringify(payload);
}
function parseBorderPayload(value) {
  if (typeof value !== "string" || value.length === 0) return null;
  try {
    const o = JSON.parse(value);
    if (o && o.kind === "borders") {
      return { kind: "borders", borders: o.borders };
    }
    if (o && o.kind === "fill") {
      return { kind: "fill", fill: o.fill };
    }
  } catch {
  }
  return null;
}
function cellBordersToParaBorders(cell) {
  return {
    ...cell.top ? { top: cell.top } : {},
    ...cell.left ? { left: cell.left } : {},
    ...cell.bottom ? { bottom: cell.bottom } : {},
    ...cell.right ? { right: cell.right } : {}
  };
}
function cellBordersToPageBorders(cell) {
  return {
    ...cell.top ? { top: cell.top } : {},
    ...cell.left ? { left: cell.left } : {},
    ...cell.bottom ? { bottom: cell.bottom } : {},
    ...cell.right ? { right: cell.right } : {}
  };
}
function applyBorderPayload(payload, verbs) {
  if (!payload) return;
  if (payload.kind === "borders") verbs.setCellBorders(payload.borders);
  else verbs.setCellShading(payload.fill);
}

// src/format-commands.ts
function activeMarksInRange(frag, lo, hi) {
  return marksInRange(frag, lo, hi);
}
function linkHrefInRange(frag, lo, hi) {
  const mark = activeMarksInRange(frag, lo, hi).find((m) => m.type === "link");
  return mark && mark.type === "link" ? mark.href : null;
}
function textEffectsIsDefault(fx) {
  return !fx.shadow && !fx.outline && !fx.emboss && !fx.engrave && fx.glow == null && !fx.reflection;
}
function serializeTextEffects(fx) {
  return fx == null || textEffectsIsDefault(fx) ? "" : JSON.stringify(fx);
}
function activeDecorationMark(active, kind) {
  if (kind === "text_effects") {
    return active.textEffects ? { type: "text_effects", effects: active.textEffects } : null;
  }
  if (kind === "char_border") {
    return active.charBorder ? { type: "char_border", border: active.charBorder } : null;
  }
  return active.emphasis ? { type: "emphasis", emphasis: active.emphasis } : null;
}
function charBorderFromPayload(payload) {
  if (!payload || payload.kind !== "borders" || !payload.borders) return null;
  const b = payload.borders;
  const side = b.top ?? b.left ?? b.bottom ?? b.right;
  if (!side) return null;
  return { widthPt: side.widthPt, color: side.color, style: side.style };
}
function createFragmentFormatApi(handle, module) {
  const marksAtSelection = () => {
    const range = handle.selectionRange();
    const frag = handle.getValue();
    if (!range) return [];
    return activeMarksInRange(frag, range.start, range.end);
  };
  const removeDecoration = (kind) => {
    const cur = marksAtSelection().find((m) => m.type === kind);
    if (!cur) return null;
    return handle.toggle(cur);
  };
  return {
    toggleMark(type) {
      const wasActive = marksAtSelection().some((m) => m.type === type);
      if (wasActive && !module.supportsMarkToggle) {
        return { fragment: null, applied: false, toggledOff: false, pending: "toggle-off" };
      }
      const fragment = handle.toggle({ type });
      if (fragment == null) return { fragment: null, applied: false, toggledOff: false };
      return { fragment, applied: !wasActive, toggledOff: wasActive };
    },
    setLink(href) {
      if (!href) return null;
      return handle.format({ type: "link", href });
    },
    // ─── The link popover's contract ─────────────────────────────────────────
    linkTarget() {
      const frag = handle.getValue();
      const live = handle.selectionRange();
      const end = fragmentByteLen(frag);
      const sel = live ?? { start: end, end };
      const extent = sel.end <= sel.start ? linkExtentAt(frag, sel.start) : null;
      const range = extent?.range ?? sel;
      const href = extent?.href ?? linkHrefInRange(frag, range.start, range.end);
      return {
        text: fragmentTextInRange(frag, range.start, range.end),
        href,
        range,
        canEditText: true
      };
    },
    applyLink(opts) {
      const href = opts.href.trim();
      if (!href || !isSafeHref(href)) return null;
      const range = opts.range ?? handle.selectionRange();
      if (!range) return null;
      const current = fragmentTextInRange(handle.getValue(), range.start, range.end);
      let end = range.end;
      if (opts.text != null && opts.text !== current) {
        if (opts.text.length === 0) return null;
        const edited = handle.applyTextEdit({
          at: range.start,
          deleteBytes: range.end - range.start,
          insert: opts.text
        });
        if (!edited) return null;
        end = range.start + byteLen$1(opts.text);
      }
      if (end <= range.start) return null;
      return handle.format({ type: "link", href }, { start: range.start, end });
    },
    restoreFocus(range) {
      const root = handle.root;
      if (!root) return;
      root.focus();
      if (range) selectByteRange(root, range.start, range.end);
    },
    setColor(color) {
      return handle.format({ type: "color", color });
    },
    setHighlight(color) {
      return handle.format({ type: "highlight", color });
    },
    setFontFamily(name) {
      if (!name) return null;
      return handle.format({ type: "font_family", font: name });
    },
    setFontSize(pt) {
      if (!Number.isFinite(pt) || pt <= 0) return null;
      return handle.format({ type: "font_size", sizePt: pt });
    },
    setLetterSpacing(pt) {
      if (!Number.isFinite(pt)) return null;
      return handle.format({ type: "letter_spacing", spacingPt: pt });
    },
    // ─── FEATURE BUNDLE 3 W2 — char-decoration marks via the SAME handle.format
    //     path bold/color use (mark-general apply verb). ─────────────────────────
    //
    // A NULL (or all-default) value is a CLEAR, never an "ignore": it routes the
    // CURRENT mark back through the native docz toggle verb (`handle.toggle` →
    // `dz_transform_toggle_mark`), which removes it because the range is fully
    // covered by that exact value (docz `markEql`). Never a hand-rolled TS removal.
    setTextEffects(effects) {
      if (effects == null || textEffectsIsDefault(effects)) return removeDecoration("text_effects");
      return handle.format({ type: "text_effects", effects });
    },
    setCharBorder(payload) {
      const border = charBorderFromPayload(payload);
      if (!border) return removeDecoration("char_border");
      return handle.format({ type: "char_border", border });
    },
    setEmphasis(emphasis) {
      if (emphasis == null) return removeDecoration("emphasis");
      return handle.format({ type: "emphasis", emphasis });
    },
    setUnderlineStyle(style, color) {
      return handle.format({ type: "underline", style, color });
    },
    clearFormatting() {
      return handle.clearFormatting();
    },
    changeCase(mode) {
      return handle.changeCase(mode);
    },
    setBlockKind(kind, level) {
      return handle.setKind(kind, level);
    },
    // ─── W2 paragraph geometry — route to the fragment handle's setParaProps ────
    setAlignment(align) {
      return handle.setParaProps({ textAlign: align });
    },
    indent() {
      const cur = currentLeftIndentPt(fragmentParaProps(handle.getValue()));
      return handle.setParaProps({ indent: { leftPt: cur + INDENT_STEP_PT } });
    },
    outdent() {
      const cur = currentLeftIndentPt(fragmentParaProps(handle.getValue()));
      return handle.setParaProps({ indent: { leftPt: Math.max(0, cur - INDENT_STEP_PT) } });
    },
    setLineSpacing(spacing) {
      return handle.setParaProps({ lineSpacing: spacing });
    },
    setSpaceBefore(pt) {
      return handle.setParaProps({ spaceBeforePt: pt });
    },
    setSpaceAfter(pt) {
      return handle.setParaProps({ spaceAfterPt: pt });
    },
    setFirstLineIndent(pt) {
      return handle.setParaProps({ indent: { firstLinePt: pt } });
    },
    setHangingIndent(pt) {
      return handle.setParaProps({ indent: { hangingPt: pt } });
    },
    setTabStops(stops) {
      return handle.setParaProps({ tabStops: stops });
    },
    // ─── FEATURE BUNDLE 1 — paragraph borders + shading route to setParaProps ────
    setParaBorders(payload) {
      if (!payload) return null;
      if (payload.kind === "borders") {
        return handle.setParaProps({
          borders: payload.borders ? cellBordersToParaBorders(payload.borders) : null
        });
      }
      return handle.setParaProps({ shading: payload.fill });
    },
    setParaShading(hex) {
      return handle.setParaProps({ shading: hex });
    },
    setDropCap(dropCap) {
      return handle.setParaProps({ dropCap });
    },
    // ─── FEATURE BUNDLE 2 — page decoration is section/document-only ─────────────
    // A comment body carries NO section/page, so the page-layout controls are
    // unavailable here (honest degrade — the `page-layout` tab is hidden via the
    // `pageDecoration:false` support bit, and these no-op if reached anyway).
    setPageColor() {
      return null;
    },
    setPageBorders() {
      return null;
    },
    setLineNumbering() {
      return null;
    },
    setPageMargins() {
      return null;
    },
    // ─── W4 lists — document-only verbs; a comment body carries no page lists ───
    setListStyle() {
      return null;
    },
    setListItemLevel() {
      return null;
    },
    restartList() {
      return null;
    },
    toggleTaskList() {
      return null;
    },
    setChecklist() {
      return null;
    },
    setBulletGlyph() {
      return null;
    },
    insertList(ordered, items) {
      const texts = items && items.length > 0 ? items : ["List item"];
      return {
        kind: "list",
        style: ordered ? "decimal" : "bullet",
        items: texts.map((t) => [{ text: t, marks: [] }])
      };
    },
    insertTable(rows, cols) {
      return emptyTable(Math.max(1, rows), Math.max(1, cols));
    },
    insertHorizontalRule() {
      return { kind: "horizontalRule" };
    },
    insertImage(opts) {
      if (opts.svg) {
        return {
          kind: "imageSvg",
          svg: opts.src,
          width: opts.width,
          height: opts.height,
          alt: opts.alt
        };
      }
      return {
        kind: "image",
        src: opts.src,
        srcKind: "path",
        width: opts.width,
        height: opts.height,
        alt: opts.alt
      };
    },
    renderBlockHtml(block) {
      const r = module.renderBlockDto(block);
      if (!r.ok) return "";
      const open = r.html.indexOf("<body");
      if (open === -1) return r.html.trim();
      const start = r.html.indexOf(">", open);
      const close = r.html.lastIndexOf("</body>");
      return r.html.slice(start + 1, close === -1 ? void 0 : close).trim();
    },
    active() {
      const frag = handle.getValue();
      const marks = marksAtSelection();
      const link = marks.find((m) => m.type === "link");
      const color = marks.find((m) => m.type === "color");
      const highlight = marks.find((m) => m.type === "highlight");
      const fontFamily = marks.find((m) => m.type === "font_family");
      const fontSize = marks.find((m) => m.type === "font_size");
      const letterSpacing = marks.find((m) => m.type === "letter_spacing");
      const underline = marks.find((m) => m.type === "underline");
      const textEffects = marks.find((m) => m.type === "text_effects");
      const charBorder = marks.find((m) => m.type === "char_border");
      const emphasis = marks.find((m) => m.type === "emphasis");
      return {
        marks: new Set(marks.map((m) => m.type)),
        link: link && link.type === "link" ? link.href : null,
        color: color && color.type === "color" ? color.color : null,
        highlight: highlight && highlight.type === "highlight" ? highlight.color : null,
        fontFamily: fontFamily && fontFamily.type === "font_family" ? fontFamily.font : null,
        fontSize: fontSize && fontSize.type === "font_size" ? fontSize.sizePt : null,
        underlineStyle: underline && underline.type === "underline" ? underline.style ?? "single" : null,
        letterSpacing: letterSpacing && letterSpacing.type === "letter_spacing" ? letterSpacing.spacingPt : null,
        textEffects: textEffects && textEffects.type === "text_effects" ? textEffects.effects : null,
        charBorder: charBorder && charBorder.type === "char_border" ? charBorder.border : null,
        emphasis: emphasis && emphasis.type === "emphasis" ? emphasis.emphasis : null,
        kind: frag.kind ?? "paragraph",
        level: frag.level ?? null,
        para: fragmentParaProps(frag)
      };
    },
    supports: {
      styleMarks: module.supportsStyleMarks,
      blockPalette: module.supportsBlockPalette,
      toggleOff: module.supportsMarkToggle,
      clearFormatting: supportsClearMarks(module),
      changeCase: supportsChangeCase(module),
      paraProps: supportsParaProps(module),
      // A comment body is a single fragment, not a page — tables + lists stay
      // document-only regardless of the wasm's capability.
      tables: false,
      lists: supportsLists(module),
      // FEATURE BUNDLE 2 — a comment body has no section/page, so page decoration
      // is unavailable (the `page-layout` tab hides here).
      pageDecoration: false,
      // FEATURE BUNDLE 4 (1E) — a comment body carries no references machinery
      // (index / ToA / cross-refs live on the document surface, not a fragment).
      references: false,
      // A comment body is a single fragment, not a paginated document — there is
      // no page to break and no block palette to drop an image into.
      pageBreak: false,
      images: false
    }
  };
}
function emptySectionDecoration() {
  return {};
}
function mergeSectionDecoration(cur, patch) {
  let pageColor = cur.pageColor;
  let pageBorders = cur.pageBorders;
  let lineNumbering = cur.lineNumbering;
  let pageMargins = cur.pageMargins;
  if (patch.pageColor !== void 0) {
    pageColor = patch.pageColor == null || patch.pageColor === "" ? void 0 : patch.pageColor;
  }
  if (patch.pageBorders !== void 0) pageBorders = patch.pageBorders ?? void 0;
  if (patch.lineNumbering !== void 0) lineNumbering = patch.lineNumbering ?? void 0;
  if (patch.pageMargins !== void 0) pageMargins = patch.pageMargins ?? void 0;
  return {
    ...pageColor !== void 0 ? { pageColor } : {},
    ...pageBorders !== void 0 ? { pageBorders } : {},
    ...lineNumbering !== void 0 ? { lineNumbering } : {},
    ...pageMargins !== void 0 ? { pageMargins } : {}
  };
}
function isDefaultSectionDecoration(d) {
  return d.pageColor === void 0 && d.pageBorders === void 0 && d.lineNumbering === void 0 && d.pageMargins === void 0;
}
var LINE_NUMBERING_DEFAULTS = {
  start: 1,
  countBy: 1,
  restart: "new_page",
  distancePt: 18
};
var LINE_NUMBER_RESTART_OPTIONS = [
  { value: "continuous", label: "Continuous" },
  { value: "new_page", label: "Each page" },
  { value: "new_section", label: "Each section" }
];
function serializeLineNumbering(config) {
  return config == null ? "" : JSON.stringify(config);
}
function parseLineNumbering(value) {
  if (value === void 0 || value === "") return null;
  if (typeof value !== "string") return null;
  try {
    const o = JSON.parse(value);
    return o && typeof o === "object" ? o : null;
  } catch {
    return null;
  }
}
function serializePageMargins(margins) {
  return margins == null ? "" : JSON.stringify(margins);
}
function parsePageMargins(value) {
  if (value === void 0 || value === "") return null;
  if (typeof value !== "string") return null;
  let o;
  try {
    o = JSON.parse(value);
  } catch {
    return null;
  }
  if (o == null || typeof o !== "object") return null;
  const r = o;
  const sides = ["topPt", "rightPt", "bottomPt", "leftPt"];
  for (const s of sides) {
    if (typeof r[s] !== "number" || !Number.isFinite(r[s])) return null;
  }
  return {
    topPt: r.topPt,
    rightPt: r.rightPt,
    bottomPt: r.bottomPt,
    leftPt: r.leftPt
  };
}
var COLOR_FIELD_PRESETS = BORDER_COLOR_PRESETS;
var HIGHLIGHT_PRESETS = [
  "#ffff00",
  "#00ff00",
  "#00ffff",
  "#ff00ff",
  "#0000ff",
  "#ff0000",
  "#000080",
  "#008080",
  "#008000",
  "#800080",
  "#800000",
  "#808000",
  "#c0c0c0",
  "#808080",
  "#000000"
];
function ColorField(props) {
  const { label, testId, value, onDraft, onPick, compact } = props;
  const presets = props.presets ?? COLOR_FIELD_PRESETS;
  return /* @__PURE__ */ jsx("div", { className: "ela-be__color", "data-testid": testId, "data-color": value, children: /* @__PURE__ */ jsxs(ColorPicker, { defaultValue: value, format: "hex", onChange: onDraft, children: [
    /* @__PURE__ */ jsx(
      ColorSwatchGrid,
      {
        colors: presets,
        ariaLabel: `${label} presets`,
        onSelect: (hex) => onPick?.(hex)
      }
    ),
    compact ? null : /* @__PURE__ */ jsx(ColorArea, { ariaLabel: `${label} saturation and value` }),
    compact ? null : /* @__PURE__ */ jsx(ColorHueSlider, { ariaLabel: `${label} hue` }),
    /* @__PURE__ */ jsx(ColorTextInput, { format: "hex", ariaLabel: `${label} hex value` })
  ] }) });
}
function PopoverPanel(props) {
  const { testId, label, className, children } = props;
  return (
    // biome-ignore lint/a11y/useSemanticElements: a toolbar control cluster, not a form fieldset
    /* @__PURE__ */ jsx(
      "div",
      {
        className: className ? `ela-be ${className}` : "ela-be",
        "data-testid": testId,
        role: "group",
        "aria-label": label,
        children
      }
    )
  );
}
function PopoverRow(props) {
  return /* @__PURE__ */ jsx("div", { className: "ela-be__row", "data-testid": props.testId, children: props.children });
}
function PopoverField(props) {
  return /* @__PURE__ */ jsxs("label", { className: "ela-be__field", children: [
    /* @__PURE__ */ jsx("span", { className: "ela-be__field-label", children: props.label }),
    props.children
  ] });
}
function PopoverButton(props) {
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      className: "ela-be__btn",
      "data-testid": props.testId,
      title: props.title,
      onClick: props.onClick,
      children: props.children
    }
  );
}
function PopoverActions(props) {
  const { secondaryLabel, secondaryTestId, onSecondary } = props;
  return /* @__PURE__ */ jsxs(PopoverRow, { children: [
    /* @__PURE__ */ jsx(
      Button,
      {
        className: "ela-be__apply",
        variant: "primary",
        size: "sm",
        "data-testid": props.primaryTestId,
        onClick: props.onPrimary,
        children: props.primaryLabel
      }
    ),
    secondaryLabel != null && secondaryTestId != null && onSecondary != null && /* @__PURE__ */ jsx(
      Button,
      {
        className: "ela-be__clear",
        variant: "ghost",
        size: "sm",
        "data-testid": secondaryTestId,
        onClick: onSecondary,
        children: secondaryLabel
      }
    )
  ] });
}
var PRESET_LABELS = {
  all: "All",
  none: "None",
  outer: "Outer",
  inner: "Inner"
};
function BorderEditor(props) {
  const {
    onApplyBorders,
    onApplyFill,
    showBorders = true,
    showFill = true,
    initialBorderColor = "#000000",
    initialFillColor = "#ffff00",
    testId = "border-editor"
  } = props;
  const [sides, setSides] = useState(BORDER_PRESETS.none);
  const [style, setStyle] = useState("solid");
  const [widthPt, setWidthPt] = useState(1);
  const [borderColor, setBorderColor] = useState(initialBorderColor);
  const [fillColor, setFillColor] = useState(initialFillColor);
  const toggleSide = (side) => setSides((s) => ({ ...s, [side]: !s[side] }));
  const applyPreset = (id) => setSides(BORDER_PRESETS[id]);
  const emitBorders = () => {
    const line = { widthPt, color: borderColor, style };
    onApplyBorders?.({ kind: "borders", borders: buildCellBorders(sides, line) });
  };
  const emitFill = () => onApplyFill?.({ kind: "fill", fill: fillColor });
  const clearFill = () => onApplyFill?.({ kind: "fill", fill: null });
  return /* @__PURE__ */ jsxs(PopoverPanel, { testId, label: "Cell borders and shading", children: [
    showBorders && /* @__PURE__ */ jsxs("section", { className: "ela-be__section", "aria-label": "Borders", children: [
      /* @__PURE__ */ jsx("div", { className: "ela-be__presets", role: "group", "aria-label": "Border presets", children: Object.keys(PRESET_LABELS).map((id) => /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: "ela-be__preset",
          "data-testid": `${testId}-preset-${id}`,
          onClick: () => applyPreset(id),
          children: PRESET_LABELS[id]
        },
        id
      )) }),
      /* @__PURE__ */ jsxs("div", { className: "ela-be__box", role: "group", "aria-label": "Individual sides", children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "ela-be__side ela-be__side--top",
            "data-testid": `${testId}-side-top`,
            "aria-label": "Top border",
            "aria-pressed": sides.top,
            "data-active": sides.top ? "true" : void 0,
            onClick: () => toggleSide("top")
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "ela-be__side ela-be__side--left",
            "data-testid": `${testId}-side-left`,
            "aria-label": "Left border",
            "aria-pressed": sides.left,
            "data-active": sides.left ? "true" : void 0,
            onClick: () => toggleSide("left")
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "ela-be__cell", "aria-hidden": "true" }),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "ela-be__side ela-be__side--right",
            "data-testid": `${testId}-side-right`,
            "aria-label": "Right border",
            "aria-pressed": sides.right,
            "data-active": sides.right ? "true" : void 0,
            onClick: () => toggleSide("right")
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "ela-be__side ela-be__side--bottom",
            "data-testid": `${testId}-side-bottom`,
            "aria-label": "Bottom border",
            "aria-pressed": sides.bottom,
            "data-active": sides.bottom ? "true" : void 0,
            onClick: () => toggleSide("bottom")
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "ela-be__row", children: [
        /* @__PURE__ */ jsxs("label", { className: "ela-be__field", children: [
          /* @__PURE__ */ jsx("span", { className: "ela-be__field-label", children: "Style" }),
          /* @__PURE__ */ jsx(
            "select",
            {
              className: "ela-be__select",
              "data-testid": `${testId}-style`,
              "aria-label": "Border style",
              value: style,
              onChange: (e) => setStyle(e.target.value),
              children: BORDER_STYLE_OPTIONS.map((s) => /* @__PURE__ */ jsx("option", { value: s, children: s }, s))
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("label", { className: "ela-be__field", children: [
          /* @__PURE__ */ jsx("span", { className: "ela-be__field-label", children: "Width" }),
          /* @__PURE__ */ jsx(
            "select",
            {
              className: "ela-be__select",
              "data-testid": `${testId}-width`,
              "aria-label": "Border width (pt)",
              value: String(widthPt),
              onChange: (e) => setWidthPt(Number(e.target.value)),
              children: BORDER_WIDTH_OPTIONS.map((w) => /* @__PURE__ */ jsxs("option", { value: String(w), children: [
                w,
                " pt"
              ] }, w))
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "ela-be__field", children: [
        /* @__PURE__ */ jsx("span", { className: "ela-be__field-label", children: "Border color" }),
        /* @__PURE__ */ jsx(
          ColorField,
          {
            value: borderColor,
            onDraft: setBorderColor,
            onPick: setBorderColor,
            label: "Border colour",
            testId: `${testId}-border-color`
          }
        )
      ] }),
      /* @__PURE__ */ jsx(
        PopoverActions,
        {
          primaryLabel: "Apply borders",
          primaryTestId: `${testId}-apply-borders`,
          onPrimary: emitBorders
        }
      )
    ] }),
    showFill && /* @__PURE__ */ jsxs("section", { className: "ela-be__section", "aria-label": "Cell fill", children: [
      /* @__PURE__ */ jsxs("div", { className: "ela-be__field", children: [
        /* @__PURE__ */ jsx("span", { className: "ela-be__field-label", children: "Cell fill" }),
        /* @__PURE__ */ jsx(
          ColorField,
          {
            value: fillColor,
            onDraft: setFillColor,
            onPick: setFillColor,
            label: "Cell fill colour",
            testId: `${testId}-fill-color`
          }
        )
      ] }),
      /* @__PURE__ */ jsx(
        PopoverActions,
        {
          primaryLabel: "Apply fill",
          primaryTestId: `${testId}-apply-fill`,
          onPrimary: emitFill,
          secondaryLabel: "Clear fill",
          secondaryTestId: `${testId}-clear-fill`,
          onSecondary: clearFill
        }
      )
    ] })
  ] });
}
function ListRestartBody(props) {
  const [startAt, setStartAt] = React.useState(String(props.current ?? 1));
  return /* @__PURE__ */ jsxs("div", { "data-testid": props.testId, children: [
    /* @__PURE__ */ jsx("button", { type: "button", "data-testid": "list-restart-1", onClick: () => props.emit("1"), children: "Restart at 1" }),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-testid": "list-restart-continue",
        onClick: () => props.emit("continue"),
        children: "Continue previous"
      }
    ),
    /* @__PURE__ */ jsxs("label", { children: [
      "Start at",
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "number",
          min: 1,
          step: 1,
          "data-testid": "list-restart-start-input",
          "aria-label": "Start numbering at",
          value: startAt,
          onChange: (e) => setStartAt(e.target.value)
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-testid": "list-restart-start-apply",
        onClick: () => props.emit(startAt),
        children: "Set"
      }
    )
  ] });
}
function cellStyle(scheme, isHeaderRow, bodyRowIndex) {
  const style = {};
  const fill = shadingForCell(scheme, isHeaderRow, bodyRowIndex);
  if (fill) style.background = fill;
  const borders = borderForCell(scheme, isHeaderRow);
  const css = (b) => b ? `${b.widthPt}px ${b.style} ${b.color}` : void 0;
  if (borders?.top) style.borderTop = css(borders.top);
  if (borders?.left) style.borderLeft = css(borders.left);
  if (borders?.bottom) style.borderBottom = css(borders.bottom);
  if (borders?.right) style.borderRight = css(borders.right);
  return style;
}
function PresetThumbnail({ scheme }) {
  const rows = [
    { isHeaderRow: true, bodyRowIndex: -1 },
    { isHeaderRow: false, bodyRowIndex: 0 },
    { isHeaderRow: false, bodyRowIndex: 1 }
  ];
  return /* @__PURE__ */ jsx("span", { className: "ela-tstyle__thumb", "aria-hidden": "true", children: rows.map((r, ri) => /* @__PURE__ */ jsx("span", { className: "ela-tstyle__thumb-row", children: [0, 1, 2].map((ci) => /* @__PURE__ */ jsx(
    "span",
    {
      className: "ela-tstyle__thumb-cell",
      style: cellStyle(scheme, r.isHeaderRow, r.bodyRowIndex)
    },
    ci
  )) }, ri)) });
}
function TableStyleGallery(props) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: "ela-tstyle-gallery",
      "data-testid": props.testId ?? "table-style-gallery",
      role: "listbox",
      "aria-label": "Table styles",
      children: TABLE_STYLE_PRESETS.map((scheme) => /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          className: "ela-tstyle-gallery__item",
          role: "option",
          "aria-selected": false,
          "data-testid": `table-style-preset-${scheme.id}`,
          title: scheme.label,
          onClick: () => props.onSelect(scheme.id),
          children: [
            /* @__PURE__ */ jsx(PresetThumbnail, { scheme }),
            /* @__PURE__ */ jsx("span", { className: "ela-tstyle-gallery__label", children: scheme.label })
          ]
        },
        scheme.id
      ))
    }
  );
}
var BORDER_PICKERS = {
  "table.borders": { borders: true, fill: true },
  "table.cell-fill": { borders: false, fill: true },
  "para.borders": { borders: true, fill: true },
  "page.borders": { borders: true, fill: true },
  "character.border": { borders: true, fill: false }
};
var COLOR_PICKERS = {
  "character.color": { clearable: false, fallback: "#000000" },
  "character.highlight": {
    clearable: false,
    fallback: "#ffff00",
    presets: HIGHLIGHT_PRESETS
  },
  "para.shading": { clearable: true, fallback: "#fef08a" },
  "page.color": { clearable: true, fallback: "#ffffff" }
};
var TEXT_EFFECT_FLAGS = [
  { key: "shadow", label: "Shadow" },
  { key: "outline", label: "Outline" },
  { key: "emboss", label: "Emboss" },
  { key: "engrave", label: "Engrave" },
  { key: "reflection", label: "Reflection" }
];
var DEFAULT_GLOW = "#4a86e8";
function TextEffectsBody(props) {
  const { testId, current, emit } = props;
  const [fx, setFx] = useState(current ?? {});
  const glowOn = fx.glow != null;
  const flip = (key) => setFx((s) => ({ ...s, [key]: !s[key] }));
  const setGlow = (hex) => setFx((s) => {
    const { glow: _drop, ...rest } = s;
    return hex == null ? rest : { ...rest, glow: hex };
  });
  return /* @__PURE__ */ jsxs(PopoverPanel, { testId, label: "Text effects", className: "ela-fx", children: [
    TEXT_EFFECT_FLAGS.map(({ key, label }) => /* @__PURE__ */ jsxs("label", { className: "ela-fx__flag", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "checkbox",
          "data-testid": `${testId}-fx-${key}`,
          checked: fx[key] === true,
          onChange: () => flip(key)
        }
      ),
      /* @__PURE__ */ jsx("span", { children: label })
    ] }, key)),
    /* @__PURE__ */ jsxs("label", { className: "ela-fx__flag", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "checkbox",
          "data-testid": `${testId}-glow-on`,
          checked: glowOn,
          onChange: () => setGlow(glowOn ? null : DEFAULT_GLOW)
        }
      ),
      /* @__PURE__ */ jsx("span", { children: "Glow" })
    ] }),
    glowOn && // Compact (swatches + hex, no area/hue ramp): the glow is a SECONDARY
    // field of the effects list — a full picker here would bury the flag
    // toggles it belongs to. Still the ecosystem control, never a raw
    // `<input type="color">`.
    /* @__PURE__ */ jsx(
      ColorField,
      {
        label: "Glow colour",
        testId: `${testId}-glow`,
        value: fx.glow ?? DEFAULT_GLOW,
        compact: true,
        onDraft: setGlow,
        onPick: setGlow
      }
    ),
    /* @__PURE__ */ jsx(
      PopoverActions,
      {
        primaryLabel: "Apply",
        primaryTestId: `${testId}-apply`,
        onPrimary: () => emit(serializeTextEffects(fx)),
        secondaryLabel: "Clear",
        secondaryTestId: `${testId}-clear`,
        onSecondary: () => emit("")
      }
    )
  ] });
}
function num(raw, fallback) {
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}
function LineNumberingBody(props) {
  const { testId, current, emit } = props;
  const [cfg, setCfg] = useState({
    ...LINE_NUMBERING_DEFAULTS,
    ...current
  });
  const patch = (p) => setCfg((s) => ({ ...s, ...p }));
  return /* @__PURE__ */ jsxs(PopoverPanel, { testId, label: "Line numbers", className: "ela-lnum", children: [
    /* @__PURE__ */ jsxs("div", { className: "ela-pgm__grid", children: [
      /* @__PURE__ */ jsx(PopoverField, { label: "Start at", children: /* @__PURE__ */ jsx(
        "input",
        {
          className: "ela-be__input",
          type: "number",
          min: "1",
          "data-testid": `${testId}-start`,
          "aria-label": "Start at",
          value: String(cfg.start),
          onChange: (e) => patch({ start: num(e.target.value, LINE_NUMBERING_DEFAULTS.start) })
        }
      ) }),
      /* @__PURE__ */ jsx(PopoverField, { label: "Count by", children: /* @__PURE__ */ jsx(
        "input",
        {
          className: "ela-be__input",
          type: "number",
          min: "1",
          "data-testid": `${testId}-count-by`,
          "aria-label": "Count by",
          value: String(cfg.countBy),
          onChange: (e) => patch({ countBy: num(e.target.value, LINE_NUMBERING_DEFAULTS.countBy) })
        }
      ) }),
      /* @__PURE__ */ jsx(PopoverField, { label: "From text (pt)", children: /* @__PURE__ */ jsx(
        "input",
        {
          className: "ela-be__input",
          type: "number",
          min: "0",
          "data-testid": `${testId}-distance`,
          "aria-label": "Distance from text in points",
          value: String(cfg.distancePt),
          onChange: (e) => patch({ distancePt: num(e.target.value, LINE_NUMBERING_DEFAULTS.distancePt) })
        }
      ) }),
      /* @__PURE__ */ jsx(PopoverField, { label: "Restart", children: /* @__PURE__ */ jsx(
        "select",
        {
          className: "ela-be__select",
          "data-testid": `${testId}-restart`,
          "aria-label": "Restart numbering",
          value: cfg.restart,
          onChange: (e) => patch({ restart: e.target.value }),
          children: LINE_NUMBER_RESTART_OPTIONS.map((o) => /* @__PURE__ */ jsx("option", { value: o.value, children: o.label }, o.value))
        }
      ) })
    ] }),
    /* @__PURE__ */ jsx(
      PopoverActions,
      {
        primaryLabel: "Apply",
        primaryTestId: `${testId}-apply`,
        onPrimary: () => emit(serializeLineNumbering(cfg)),
        secondaryLabel: "Off",
        secondaryTestId: `${testId}-off`,
        onSecondary: () => emit("")
      }
    )
  ] });
}
function PageMarginsBody(props) {
  const { testId, current, emit, box: sheet } = props;
  const [box, setBox] = useState(current ?? A4_NORMAL_MARGINS);
  const [error, setError] = useState(null);
  const patch = (p) => {
    setBox((s) => ({ ...s, ...p }));
    setError(null);
  };
  const applyBox = (next) => {
    const bad = validatePageMargins(next, sheet);
    if (bad) {
      setError(bad.message);
      return;
    }
    setError(null);
    emit(serializePageMargins(next));
  };
  const SIDES = [
    { key: "topPt", slug: "top", label: "Top" },
    { key: "rightPt", slug: "right", label: "Right" },
    { key: "bottomPt", slug: "bottom", label: "Bottom" },
    { key: "leftPt", slug: "left", label: "Left" }
  ];
  return /* @__PURE__ */ jsxs(PopoverPanel, { testId, label: "Page margins", className: "ela-pgm", children: [
    /* @__PURE__ */ jsx(PopoverRow, { testId: `${testId}-presets`, children: PAGE_MARGIN_PRESETS.map((p) => /* @__PURE__ */ jsx(
      PopoverButton,
      {
        testId: `${testId}-preset-${p.id}`,
        title: `${p.label} \u2014 ${p.margins.topPt}/${p.margins.rightPt}/${p.margins.bottomPt}/${p.margins.leftPt}pt`,
        onClick: () => {
          setBox(p.margins);
          applyBox(p.margins);
        },
        children: p.label
      },
      p.id
    )) }),
    /* @__PURE__ */ jsx("div", { className: "ela-pgm__grid", children: SIDES.map((s) => /* @__PURE__ */ jsxs("label", { className: "ela-pgm__field", children: [
      /* @__PURE__ */ jsx("span", { children: s.label }),
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "number",
          min: "0",
          step: "1",
          "data-testid": `${testId}-${s.slug}`,
          "aria-label": `${s.label} margin in points`,
          value: String(box[s.key]),
          onChange: (e) => patch({ [s.key]: num(e.target.value, box[s.key]) })
        }
      )
    ] }, s.key)) }),
    error !== null && /* @__PURE__ */ jsx("p", { className: "ela-pgm__error", role: "alert", "data-testid": `${testId}-error`, children: error }),
    /* @__PURE__ */ jsx(
      PopoverActions,
      {
        primaryLabel: "Apply",
        primaryTestId: `${testId}-apply`,
        onPrimary: () => applyBox(box),
        secondaryLabel: "Reset",
        secondaryTestId: `${testId}-reset`,
        onSecondary: () => emit("")
      }
    )
  ] });
}
function ColorBody(props) {
  const { testId, label, current, clearable, fallback, presets, emit } = props;
  const [draft, setDraft] = useState(current ?? fallback);
  return /* @__PURE__ */ jsxs(PopoverPanel, { testId: `${testId}-panel`, label, children: [
    /* @__PURE__ */ jsx(
      ColorField,
      {
        label,
        testId,
        value: draft,
        presets,
        onDraft: setDraft,
        onPick: (hex) => {
          setDraft(hex);
          emit(hex);
        }
      }
    ),
    /* @__PURE__ */ jsx(
      PopoverActions,
      {
        primaryLabel: "Apply",
        primaryTestId: `${testId}-apply`,
        onPrimary: () => emit(draft),
        secondaryLabel: clearable ? "Clear" : void 0,
        secondaryTestId: clearable ? `${testId}-clear` : void 0,
        onSecondary: clearable ? () => emit("") : void 0
      }
    )
  ] });
}
function TableColorBody(props) {
  const [draft, setDraft] = useState(props.fallback);
  return /* @__PURE__ */ jsxs(PopoverPanel, { testId: props.testId, label: props.label, children: [
    /* @__PURE__ */ jsx(
      ColorField,
      {
        label: props.label,
        testId: props.fieldTestId,
        value: draft,
        onDraft: setDraft,
        onPick: (hex) => {
          setDraft(hex);
          props.emit(hex);
        }
      }
    ),
    /* @__PURE__ */ jsx(
      PopoverActions,
      {
        primaryLabel: props.applyLabel,
        primaryTestId: props.applyTestId,
        onPrimary: () => props.emit(draft),
        secondaryLabel: props.offLabel,
        secondaryTestId: props.offTestId,
        onSecondary: () => props.emit("")
      }
    )
  ] });
}
function PickerBody(props) {
  const { command, active, onRun, pageBox } = props;
  const testId = `picker-${command.id}`;
  const emit = (value) => onRun(command, value);
  const borders = BORDER_PICKERS[command.id];
  if (borders) {
    return /* @__PURE__ */ jsx(
      BorderEditor,
      {
        testId,
        showBorders: borders.borders,
        showFill: borders.fill,
        onApplyBorders: (payload) => emit(serializeBorderPayload(payload)),
        onApplyFill: (payload) => emit(serializeBorderPayload(payload))
      }
    );
  }
  if (command.id === "character.text-effects") {
    return /* @__PURE__ */ jsx(TextEffectsBody, { testId, current: active.textEffects, emit });
  }
  if (command.id === "page.line-numbers") {
    return /* @__PURE__ */ jsx(LineNumberingBody, { testId, current: null, emit });
  }
  if (command.id === "lists.restart") {
    const cur = command.getValue?.(active);
    return /* @__PURE__ */ jsx(ListRestartBody, { testId, current: typeof cur === "number" ? cur : null, emit });
  }
  if (command.id === "page.margins") {
    return /* @__PURE__ */ jsx(PageMarginsBody, { testId, current: null, emit, box: pageBox ?? A4_PAGE_BOX });
  }
  if (command.id === "table.style") {
    return /* @__PURE__ */ jsx(TableStyleGallery, { onSelect: (preset) => emit(preset) });
  }
  if (command.id === "table.header-style") {
    return /* @__PURE__ */ jsx(
      TableColorBody,
      {
        testId,
        label: "Header fill colour",
        fieldTestId: "table-header-style-color",
        fallback: DEFAULT_HEADER_FILL,
        applyLabel: "Apply fill",
        applyTestId: "table-header-style-apply",
        offLabel: "Clear",
        offTestId: "table-header-style-clear",
        emit
      }
    );
  }
  if (command.id === "table.banding") {
    return /* @__PURE__ */ jsx(
      TableColorBody,
      {
        testId,
        label: "Band colour",
        fieldTestId: "table-banding-color",
        fallback: DEFAULT_BAND_COLOR,
        applyLabel: "Banded",
        applyTestId: "table-banding-on",
        offLabel: "None",
        offTestId: "table-banding-off",
        emit
      }
    );
  }
  const color = COLOR_PICKERS[command.id];
  if (color) {
    const cur = command.getValue?.(active);
    return /* @__PURE__ */ jsx(
      ColorBody,
      {
        testId,
        label: command.label,
        current: typeof cur === "string" && cur.length > 0 ? cur : null,
        clearable: color.clearable,
        fallback: color.fallback,
        presets: color.presets,
        emit
      }
    );
  }
  if (command.id === "insert.table") {
    return /* @__PURE__ */ jsxs(
      "form",
      {
        "data-testid": testId,
        onSubmit: (e) => {
          e.preventDefault();
          const f = e.currentTarget;
          const rows = f.elements.namedItem("rows")?.value ?? "3";
          const cols = f.elements.namedItem("cols")?.value ?? "3";
          emit(`${rows}x${cols}`);
        },
        children: [
          /* @__PURE__ */ jsx("input", { name: "rows", type: "number", "aria-label": "Rows", defaultValue: "3", min: "1" }),
          /* @__PURE__ */ jsx("input", { name: "cols", type: "number", "aria-label": "Columns", defaultValue: "3", min: "1" }),
          /* @__PURE__ */ jsx("button", { type: "submit", children: "Insert" })
        ]
      }
    );
  }
  const seed = command.getValue?.(active);
  return /* @__PURE__ */ jsxs(
    "form",
    {
      "data-testid": testId,
      onSubmit: (e) => {
        e.preventDefault();
        const input = e.currentTarget.elements.namedItem("value");
        emit(input?.value ?? "");
      },
      children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            name: "value",
            type: "text",
            "aria-label": command.label,
            defaultValue: typeof seed === "string" ? seed : ""
          }
        ),
        /* @__PURE__ */ jsx("button", { type: "submit", children: "Apply" })
      ]
    }
  );
}

// src/commands/registry.ts
var ROLE_ORDER = {
  hero: 0,
  selector: 1,
  action: 2,
  toggle: 3,
  pair: 4,
  advanced: 5
};
function toPaletteCommand(cmd, getContext) {
  return {
    id: cmd.id,
    label: cmd.label,
    group: cmd.group,
    shortcut: cmd.shortcut,
    keywords: cmd.keywords ? [...cmd.keywords] : void 0,
    icon: cmd.icon,
    onExecute: () => cmd.run(getContext())
  };
}
var FORMAT_SUPPORT_KEYS = /* @__PURE__ */ new Set([
  "styleMarks",
  "blockPalette",
  "toggleOff",
  "clearFormatting",
  "changeCase",
  "paraProps",
  "tables",
  "lists",
  "pageDecoration",
  "pageBreak",
  "images"
]);

// src/commands/ribbon-manifest.ts
var HOME_CHARACTER = [
  "character.bold",
  "character.italic",
  "character.underline",
  "character.strikeout",
  "character.color",
  "character.highlight",
  "character.clear-format"
];
var HOME_PARAGRAPH = ["para.align", "para.indent", "para.outdent"];
var RIBBON_TABS = [
  {
    id: "home",
    label: "Home",
    intent: "The commands used constantly, curated from across the formatting tabs.",
    groups: [
      { group: "history" },
      { group: "clipboard" },
      { group: "font" },
      {
        group: "character",
        commands: HOME_CHARACTER,
        curationNote: "The everyday emphasis set. The full 18-command Text group \u2014 scripts, caps, tracking, effects, borders, change-case \u2014 is carried by the Typography tab."
      },
      { group: "styles" },
      {
        group: "paragraph",
        commands: HOME_PARAGRAPH,
        curationNote: "Align + the indent/outdent pair. Line spacing, borders, shading and drop-cap are carried in full by the Paragraph tab."
      },
      { group: "lists" }
    ]
  },
  {
    id: "typography",
    label: "Typography",
    intent: "Everything that changes how the CHARACTERS look \u2014 the font, and the text.",
    groups: [{ group: "font" }, { group: "character" }]
  },
  {
    id: "paragraph",
    label: "Paragraph",
    intent: "Everything that changes how the PARAGRAPH is laid out.",
    groups: [{ group: "paragraph" }]
  },
  {
    id: "insert",
    label: "Insert",
    intent: "Put something new into the document.",
    groups: [
      { group: "insert-table" },
      { group: "insert-media" },
      { group: "insert-structure" },
      { group: "references" }
    ]
  },
  {
    id: "layout",
    label: "Layout",
    intent: "Lay out the page itself.",
    groups: [{ group: "page-layout" }]
  },
  {
    id: "table",
    label: "Table",
    // Word-style contextual tab: only exists while the caret is inside a table.
    contextGate: "inTable",
    intent: "Edit the table the caret is in.",
    groups: [
      { group: "table-whole" },
      { group: "table-rows" },
      { group: "table-columns" },
      { group: "table-merge" },
      { group: "table-align" },
      { group: "table-borders" },
      { group: "table-styles" }
    ]
  },
  {
    id: "review",
    label: "Review",
    intent: "Comment on, search and check the document.",
    groups: [
      { group: "review" },
      { group: "find" },
      {
        group: "tools",
        commands: ["tools.word-count", "tools.spell-check", "tools.settings"],
        curationNote: "The INSPECTION half of the tools group. The output half (export/print) sits on View. This split predates the manifest \u2014 it used to be an undeclared side effect of those records naming different tabs."
      }
    ]
  },
  {
    id: "view",
    label: "View",
    intent: "Change how the document is presented, and get it out.",
    groups: [
      { group: "view" },
      {
        group: "tools",
        commands: ["file.export", "file.print"],
        curationNote: "The OUTPUT half of the tools group; the inspection half sits on Review."
      }
    ]
  }
];
var RIBBON_TAB_SPECS = new Map(
  RIBBON_TABS.map((t) => [t.id, t])
);
function ribbonTabSpec(tab) {
  return RIBBON_TAB_SPECS.get(tab);
}
function placementShowsCommand(p, cmd) {
  if (cmd.group !== p.group) return false;
  return p.commands === void 0 || p.commands.includes(cmd.id);
}
function isCommandOnTab(cmd, tab) {
  const spec = RIBBON_TAB_SPECS.get(tab);
  return spec !== void 0 && spec.groups.some((p) => placementShowsCommand(p, cmd));
}
function tabsForCommand(cmd) {
  return RIBBON_TABS.filter((t) => t.groups.some((p) => placementShowsCommand(p, cmd))).map(
    (t) => t.id
  );
}
function unplacedRibbonCommands(all) {
  return all.filter((c) => c.surfaces.includes("ribbon") && tabsForCommand(c).length === 0);
}
function danglingPlacementIds(all) {
  const known = new Set(all.map((c) => c.id));
  const out = [];
  for (const tab of RIBBON_TABS) {
    for (const p of tab.groups) {
      for (const id of p.commands ?? []) if (!known.has(id)) out.push(`${tab.id}/${p.group}/${id}`);
    }
  }
  return out;
}
function misgroupedPlacementIds(all) {
  const byId = new Map(all.map((c) => [c.id, c]));
  const out = [];
  for (const tab of RIBBON_TABS) {
    for (const p of tab.groups) {
      for (const id of p.commands ?? []) {
        const cmd = byId.get(id);
        if (cmd && cmd.group !== p.group) out.push(`${tab.id}/${p.group}/${id}@${cmd.group}`);
      }
    }
  }
  return out;
}

// src/commands/resolve.ts
var RIBBON_TAB_ORDER = RIBBON_TABS.map((t) => t.id);
var CONTEXTUAL_TABS = Object.fromEntries(
  RIBBON_TABS.filter((t) => t.contextGate !== void 0).map((t) => [t.id, t.contextGate])
);
function isContextualTab(tab) {
  return CONTEXTUAL_TABS[tab] !== void 0;
}
function isTabVisible(tab, env) {
  const gate = CONTEXTUAL_TABS[tab];
  return gate === void 0 ? true : env[gate] === true;
}
function visibleRibbonTabs(tabs, env) {
  return tabs.filter((t) => isTabVisible(t, env));
}
var MENUBAR_ORDER = [
  "File",
  "Edit",
  "View",
  "Insert",
  "Format",
  "Table",
  "Tools",
  "Review",
  "Help"
];
function survivesConfig(cmd, cfg) {
  if (!cmd.essential) {
    if (cfg.disabledCommandIds.has(cmd.id)) return false;
    if (cfg.disabledGroups.has(cmd.group)) return false;
  }
  return true;
}
function resolveCommands(all, cfg) {
  const ribbon = /* @__PURE__ */ new Map();
  const menubar = /* @__PURE__ */ new Map();
  const ribbonables = all.filter((c) => c.surfaces.includes("ribbon") && survivesConfig(c, cfg));
  for (const tab of RIBBON_TABS) {
    const groups = /* @__PURE__ */ new Map();
    for (const placement of tab.groups) {
      const cmds = ribbonables.filter((c) => {
        if (!placementShowsCommand(placement, c)) return false;
        return !cfg.disabledTabs.has(tab.id);
      });
      if (cmds.length > 0) groups.set(placement.group, cmds);
    }
    if (groups.size > 0) ribbon.set(tab.id, groups);
  }
  for (const cmd of all) {
    if (cmd.surfaces.includes("menubar") && survivesConfig(cmd, cfg)) {
      const top = cmd.menuPath[0];
      if (top) {
        const bucket = menubar.get(top) ?? [];
        bucket.push(cmd);
        menubar.set(top, bucket);
      }
    }
  }
  const present = new Set(ribbon.keys());
  const ordered = [];
  for (const t of cfg.ribbon.tabOrder) {
    if (present.has(t) && !ordered.includes(t)) {
      ordered.push(t);
    }
  }
  for (const t of RIBBON_TAB_ORDER) if (present.has(t) && !ordered.includes(t)) ordered.push(t);
  const menubarMenus = MENUBAR_ORDER.filter((m) => menubar.has(m));
  return {
    ribbon,
    ribbonTabs: ordered,
    menubar,
    menubarMenus,
    simpleToolbar: resolveSimpleToolbar(all, cfg),
    styleGallery: resolveStyleGallery(all, cfg)
  };
}
function resolveStyleGallery(all, cfg) {
  return all.filter((c) => c.group === "styles" && survivesConfig(c, cfg));
}
function resolveSimpleToolbar(all, cfg) {
  const survives = (c) => survivesConfig(c, cfg);
  const override = cfg.simple.toolbarCommandIds;
  if (override && override.length > 0) {
    const byId = new Map(all.map((c) => [c.id, c]));
    const out = [];
    for (const id of override) {
      const c = byId.get(id);
      if (c && survives(c)) out.push(c);
    }
    return out;
  }
  return all.filter((c) => c.inSimpleToolbar && c.surfaces.includes("toolbar") && survives(c));
}
function isCommandEnabled(cmd, env) {
  const r = cmd.requires;
  let ok = true;
  if (r != null) {
    if (FORMAT_SUPPORT_KEYS.has(r)) {
      ok = env.supports[r] === true;
    } else if (r === "selection") {
      ok = env.hasSelection !== false;
    } else {
      ok = env.host?.[r] === true;
    }
  }
  if (ok && cmd.contextScope === "table") ok = env.inTable === true;
  if (ok && cmd.isEnabled) ok = cmd.isEnabled(env);
  return ok;
}
function readCommandColor(cmd, active) {
  const spec = cmd.colorValue;
  if (spec == null) return void 0;
  if (spec.styleProp != null && active.mixed?.has(spec.styleProp) === true) return MIXED;
  const value = spec.read(active);
  if (value === void 0) return void 0;
  return typeof value === "string" && value.length > 0 ? value : null;
}
function matchKeybinding(kb, e) {
  const keys = typeof kb.key === "string" ? [kb.key] : kb.key;
  const evKey = e.key.toLowerCase();
  if (!keys.some((k) => k.toLowerCase() === evKey)) return false;
  const mod = Boolean(e.metaKey) || Boolean(e.ctrlKey);
  if (kb.mod ? !mod : mod) return false;
  if (Boolean(kb.shift) !== Boolean(e.shiftKey)) return false;
  if (Boolean(kb.alt) !== Boolean(e.altKey)) return false;
  return true;
}
function resolveShortcut(all, e) {
  return all.find((c) => c.keybinding != null && matchKeybinding(c.keybinding, e));
}
var VALUE_KINDS = /* @__PURE__ */ new Set(["picker", "select", "radio", "value"]);
function runShortcut(all, e, env, getContext) {
  const command = resolveShortcut(all, e);
  if (!command) return { status: "none" };
  if (!isCommandEnabled(command, env)) return { status: "disabled", command };
  if (VALUE_KINDS.has(command.kind)) return { status: "needsValue", command };
  command.run(getContext());
  return { status: "dispatched", command };
}
function dispatchShortcut(all, e, env, getContext) {
  const out = runShortcut(all, e, env, getContext);
  return out.status === "dispatched" ? out.command : void 0;
}
var keepDocumentFocus = (e) => e.preventDefault();
function CommandGlyph({
  icon
}) {
  if (!icon) return null;
  if (icon.set === "editor") return /* @__PURE__ */ jsx(ToolbarIcon, { name: icon.name });
  return /* @__PURE__ */ jsx(Icon, { name: icon.name, width: 18, height: 18, "aria-hidden": true });
}
function PickerCaret() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      className: "ela-cmd-caret-mini",
      viewBox: "0 0 12 12",
      width: "10",
      height: "10",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.6",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": true,
      focusable: "false",
      children: /* @__PURE__ */ jsx("path", { d: "m3 4.5 3 3 3-3" })
    }
  );
}
function swatchOf(reading, unset) {
  if (reading === MIXED) return { state: "mixed" };
  if (reading === null) {
    if (unset === "none") return { state: "none" };
    return {
      state: "inherit",
      // docz's own default ink — the colour an unmarked run REALLY renders in,
      // not a UI token guess. Same constant `resolveStyle` resolves to.
      style: { "--ela-cmd-swatch-fill": DEFAULT_TEXT_COLOR },
      color: DEFAULT_TEXT_COLOR
    };
  }
  return {
    state: "color",
    style: { "--ela-cmd-swatch-fill": reading },
    color: reading
  };
}
var SWATCH_WORD = {
  inherit: "default",
  none: "none",
  mixed: "mixed"
};
function swatchLabel(label, swatch) {
  if (swatch.state === "color" && swatch.color != null) return `${label}: ${swatch.color}`;
  if (swatch.state === "inherit" && swatch.color != null) {
    return `${label}: ${SWATCH_WORD.inherit} (${swatch.color})`;
  }
  return `${label}: ${SWATCH_WORD[swatch.state]}`;
}
var CAPABILITY_PROSE = {
  styleMarks: "character styling",
  blockPalette: "inserting blocks",
  toggleOff: "removing a formatting mark",
  clearFormatting: "clearing formatting",
  changeCase: "changing case",
  paraProps: "paragraph layout",
  tables: "editable tables",
  lists: "lists and numbering",
  pageDecoration: "page decoration",
  references: "references (index, tables of figures, citations)",
  pageBreak: "page breaks",
  images: "image insertion"
};
var HOST_PROSE = {
  undo: "undo history",
  redo: "undo history",
  find: "find and replace",
  clipboard: "clipboard access",
  painter: "the format painter",
  comment: "commenting",
  export: "export",
  print: "printing",
  save: "saving",
  settings: "editor settings",
  wordCount: "word count",
  spell: "spell check",
  view: "view switching",
  mode: "toolbar mode switching",
  deleteBlock: "whole-block deletion"
};
function commandDisabledReason(cmd, env) {
  if (isCommandEnabled(cmd, env)) return void 0;
  const r = cmd.requires;
  if (r != null) {
    if (r === "selection" && env.hasSelection === false) return "Select some text first.";
    const capability = CAPABILITY_PROSE[r];
    const bit = env.supports[r];
    if (capability != null && (!FORMAT_SUPPORT_KEYS.has(r) || bit !== true)) {
      return `This document engine does not support ${capability} yet.`;
    }
    const hostCap = HOST_PROSE[r];
    if (hostCap != null && env.host?.[r] !== true) {
      return `This editor has no ${hostCap} available.`;
    }
  }
  if (cmd.contextScope === "table" && env.inTable !== true) {
    return "Place the caret inside a table first.";
  }
  return "Not available in the current context.";
}
function CommandValueField(props) {
  const { value } = props;
  const text = value != null ? String(value) : "";
  const [seed, setSeed] = useState(text);
  const [draft, setDraft] = useState(text);
  if (seed !== text) {
    setSeed(text);
    setDraft(text);
  }
  const commit = (raw) => {
    const pt = parseFontSizeInput(raw);
    if (pt != null) props.onCommit(pt);
  };
  return /* @__PURE__ */ jsx(
    "input",
    {
      "data-testid": props.testId,
      className: "ecmd-value",
      type: "text",
      inputMode: "decimal",
      autoComplete: "off",
      "aria-label": props.label,
      ...props.describe,
      title: props.title,
      disabled: !props.enabled,
      value: draft,
      onChange: (e) => {
        setDraft(e.target.value);
        commit(e.target.value);
      },
      onKeyDown: (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit(e.currentTarget.value);
        }
      },
      onBlur: () => {
        if (parseFontSizeInput(draft) == null) setDraft(seed);
      }
    }
  );
}
function CommandControl(props) {
  const { command, active, env, onRun, testIdPrefix } = props;
  const tid = `${testIdPrefix}-${command.id}`;
  const enabled = isCommandEnabled(command, env);
  const pressed = command.isActive?.(active) ?? false;
  const label = command.shortcut ? `${command.label} (${command.shortcut})` : command.label;
  const reason = commandDisabledReason(command, env);
  const title = reason ? `${label} \u2014 ${reason}` : command.description ?? label;
  const describe = reason ? { "aria-description": reason } : {};
  const large = props.size === "large" || command.display === "icon-label";
  const rootData = {
    "data-command-id": command.id,
    "data-cluster-start": props.clusterStart ? "true" : void 0,
    "data-size": props.size
  };
  switch (command.kind) {
    case "select": {
      const shown = String(command.getValue?.(active) ?? "");
      const foreign = shown !== "" && !(command.options ?? []).some((o) => String(o.value) === shown);
      return /* @__PURE__ */ jsxs("span", { className: "ecmd-select-wrap", title, ...rootData, children: [
        /* @__PURE__ */ jsxs(
          "select",
          {
            "data-testid": tid,
            className: "ecmd-select",
            "aria-label": command.label,
            ...describe,
            title,
            disabled: !enabled,
            value: shown,
            onChange: (e) => onRun(command, e.target.value),
            children: [
              /* @__PURE__ */ jsx("option", { value: "", disabled: true, hidden: true, children: command.label }),
              foreign ? /* @__PURE__ */ jsx(
                "option",
                {
                  value: shown,
                  disabled: true,
                  "data-foreign-value": "true",
                  title: `${shown} \u2014 in the document, not offered by ${command.label}`,
                  children: shown
                }
              ) : null,
              command.options?.map((o) => /* @__PURE__ */ jsx("option", { value: String(o.value), children: o.sample ? `${o.label}  ${o.sample}` : o.label }, String(o.value)))
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          "svg",
          {
            className: "ecmd-caret",
            viewBox: "0 0 12 12",
            width: "12",
            height: "12",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: "1.6",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            "aria-hidden": true,
            focusable: "false",
            children: /* @__PURE__ */ jsx("path", { d: "m3 4.5 3 3 3-3" })
          }
        )
      ] });
    }
    case "value":
      return /* @__PURE__ */ jsx("span", { className: "ecmd-select-wrap ecmd-value-wrap", title, ...rootData, children: /* @__PURE__ */ jsx(
        CommandValueField,
        {
          testId: tid,
          label: command.label,
          title,
          describe,
          enabled,
          value: command.getValue?.(active) ?? null,
          onCommit: (n) => onRun(command, n)
        }
      ) });
    case "radio": {
      const value = String(command.getValue?.(active) ?? command.options?.[0]?.value ?? "");
      return (
        // NOT focus-guarded, and it is the one archetype that cannot be: the
        // guard belongs on the real segment `<button>`s and `<SegmentedControl>`
        // exposes no `onMouseDown` seam, while putting it on this static wrapper
        // trips `a11y/noStaticElementInteractions`. An alignment segment therefore
        // still takes focus on click; `after()`'s surface refocus (see
        // `createRunner`) is what returns the keyboard for it.
        /* @__PURE__ */ jsx("span", { className: "ecmd", "data-testid": tid, title, ...rootData, children: /* @__PURE__ */ jsx(
          SegmentedControl,
          {
            ariaLabel: command.label,
            value,
            onChange: (next) => onRun(command, next),
            options: (command.options ?? []).map(
              (o) => o.icon ? {
                value: String(o.value),
                label: o.label,
                icon: /* @__PURE__ */ jsx(CommandGlyph, { icon: o.icon }),
                iconOnly: true,
                // A gated radio (alignment on an engine without paragraph
                // layout) used to render fully live and silently do nothing —
                // the archetype simply never consulted the enable gate. Each
                // segment now dims, and its tooltip carries the reason.
                tooltip: reason ? `${o.label} \u2014 ${reason}` : o.label,
                disabled: !enabled
              } : {
                value: String(o.value),
                label: o.label,
                disabled: !enabled
              }
            )
          }
        ) })
      );
    }
    case "picker": {
      const caret = /* @__PURE__ */ jsx(PickerCaret, {});
      const colorReading = readCommandColor(command, active);
      let trigger;
      if (large) {
        trigger = /* @__PURE__ */ jsxs(
          Button,
          {
            className: "ela-cmd-large",
            variant: "ghost",
            size: "sm",
            leadingIcon: /* @__PURE__ */ jsx(CommandGlyph, { icon: command.icon }),
            "aria-label": command.label,
            ...describe,
            title,
            disabled: !enabled,
            children: [
              command.label,
              caret
            ]
          }
        );
      } else if (colorReading !== void 0) {
        const swatch = swatchOf(colorReading, command.colorValue?.unset ?? "none");
        trigger = /* @__PURE__ */ jsx(
          Button,
          {
            className: "ela-cmd-swatchbtn",
            variant: "ghost",
            size: "sm",
            leadingIcon: /* @__PURE__ */ jsx(CommandGlyph, { icon: command.icon }),
            "aria-label": swatchLabel(command.label, swatch),
            ...describe,
            title,
            disabled: !enabled,
            children: /* @__PURE__ */ jsx(
              "span",
              {
                className: "ela-cmd-swatch",
                "data-swatch": swatch.state,
                style: swatch.style,
                "aria-hidden": "true"
              }
            )
          }
        );
      } else if (command.display === "split") {
        trigger = /* @__PURE__ */ jsx(
          Button,
          {
            className: "ela-cmd-split",
            variant: "ghost",
            size: "sm",
            leadingIcon: /* @__PURE__ */ jsx(CommandGlyph, { icon: command.icon }),
            "aria-label": command.label,
            ...describe,
            title,
            disabled: !enabled,
            children: caret
          }
        );
      } else {
        trigger = /* @__PURE__ */ jsx(
          IconButton,
          {
            variant: "ghost",
            size: "sm",
            icon: /* @__PURE__ */ jsx(CommandGlyph, { icon: command.icon }),
            "aria-label": command.label,
            ...describe,
            title,
            disabled: !enabled
          }
        );
      }
      const wrapMod = large ? " ecmd--labeled" : colorReading !== void 0 ? " ecmd--split ecmd--swatch" : command.display === "split" ? " ecmd--split" : "";
      return /* @__PURE__ */ jsx("span", { className: `ecmd${wrapMod}`, "data-testid": tid, title, ...rootData, children: /* @__PURE__ */ jsxs(Popover, { children: [
        /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: trigger }),
        /* @__PURE__ */ jsx(PopoverContent, { role: "dialog", children: /* @__PURE__ */ jsx(PickerBody, { command, active, onRun, pageBox: env.pageBox }) })
      ] }) });
    }
    default:
      if (large) {
        return /* @__PURE__ */ jsx("span", { className: "ecmd ecmd--labeled", "data-testid": tid, title, ...rootData, children: /* @__PURE__ */ jsx(
          Button,
          {
            className: "ela-cmd-large",
            variant: "ghost",
            size: "sm",
            leadingIcon: /* @__PURE__ */ jsx(CommandGlyph, { icon: command.icon }),
            "aria-label": label,
            ...describe,
            title,
            "aria-pressed": command.kind === "toggle" ? pressed : void 0,
            "data-active": pressed ? "true" : void 0,
            disabled: !enabled,
            onMouseDown: keepDocumentFocus,
            onClick: () => onRun(command),
            children: command.label
          }
        ) });
      }
      return /* @__PURE__ */ jsx("span", { className: "ecmd", "data-testid": tid, title, ...rootData, children: /* @__PURE__ */ jsx(
        IconButton,
        {
          variant: "ghost",
          size: "sm",
          icon: /* @__PURE__ */ jsx(CommandGlyph, { icon: command.icon }),
          "aria-label": label,
          ...describe,
          title,
          "aria-pressed": command.kind === "toggle" ? pressed : void 0,
          "data-active": pressed ? "true" : void 0,
          disabled: !enabled,
          onMouseDown: keepDocumentFocus,
          onClick: () => onRun(command)
        }
      ) });
  }
}

// src/list-format.ts
var LIST_FORMAT_ENTRIES = [
  { style: "decimal", label: "Decimal", sample: "1." },
  { style: "lower_alpha", label: "Lower alpha", sample: "a." },
  { style: "upper_alpha", label: "Upper alpha", sample: "A." },
  { style: "lower_roman", label: "Lower roman", sample: "i." },
  { style: "upper_roman", label: "Upper roman", sample: "I." },
  { style: "decimal_leading_zero", label: "Leading zero", sample: "01." }
];
var ALL_LIST_STYLES = [
  "bullet",
  "decimal",
  "lower_alpha",
  "upper_alpha",
  "lower_roman",
  "upper_roman",
  "decimal_leading_zero"
];
var BULLET_GLYPHS = ["\u2022", "\u25E6", "\u25AA", "\u25B8", "\u2013", "\u2713", "\u203A", "\xBB"];
var LIST_NEST_MIN = 0;
var LIST_NEST_MAX = 8;
function clampListLevel(level) {
  if (!Number.isFinite(level)) return LIST_NEST_MIN;
  return Math.max(LIST_NEST_MIN, Math.min(LIST_NEST_MAX, Math.trunc(level)));
}
var ORDERED_STYLES = new Set(LIST_FORMAT_ENTRIES.map((e) => e.style));
function orderedListStyle(list) {
  const style = list?.style;
  return typeof style === "string" && ORDERED_STYLES.has(style) ? style : null;
}
function isChecklist(list) {
  return list?.checked === true || list?.checked === false;
}
function parseListStart(raw) {
  if (raw === null || raw === void 0) return null;
  if (typeof raw === "number") {
    return Number.isInteger(raw) && raw >= 1 ? { kind: "start", start: raw } : null;
  }
  const text = raw.trim();
  if (text === "" || text.toLowerCase() === "continue") return { kind: "continue" };
  if (!/^\d+$/.test(text)) return null;
  const n = Number.parseInt(text, 10);
  return n >= 1 ? { kind: "start", start: n } : null;
}
function listStartValue(list) {
  const start = list?.startOverride;
  return typeof start === "number" ? start : null;
}
function bulletGlyphValue(list) {
  const glyph = list?.bulletChar;
  return typeof glyph === "string" && glyph !== "" ? glyph : null;
}
function isCuratedBulletGlyph(glyph) {
  return BULLET_GLYPHS.includes(glyph);
}

// src/commands/catalog.ts
var S_ALL = ["ribbon", "menubar", "toolbar", "palette"];
var S_RM = ["ribbon", "menubar", "palette"];
var S_RMC = ["ribbon", "menubar", "palette", "context"];
var ed = (name) => ({ set: "editor", name });
var lu = (name) => ({ set: "lucide", name });
var FONT_OPTIONS = FONT_FAMILY_STACK.map((f) => ({
  value: f,
  label: f
}));
var SIZE_OPTIONS = FONT_SIZE_STOPS.map((s) => ({
  value: s,
  label: String(s)
}));
var CASE_LABELS = {
  upper: "UPPERCASE",
  lower: "lowercase",
  title: "Title Case",
  sentence: "Sentence case",
  toggle: "tOGGLE cASE"
};
var CASE_OPTIONS = CHANGE_CASE_MODES.map((m) => ({
  value: m,
  label: CASE_LABELS[m] ?? m
}));
var LIST_FORMAT_OPTIONS = LIST_FORMAT_ENTRIES.map((e) => ({
  value: e.style,
  label: e.label,
  sample: e.sample
}));
var BULLET_GLYPH_OPTIONS = BULLET_GLYPHS.map((g) => ({
  value: g,
  label: g
}));
var UNDERLINE_OPTIONS = UNDERLINE_STYLE_OPTIONS.map((u) => ({
  value: u,
  label: u
}));
var LETTER_SPACING_OPTIONS = LETTER_SPACING_STOPS.map((pt) => ({
  value: pt,
  label: letterSpacingLabel(pt)
}));
var LINE_SPACING_OPTIONS = LINE_SPACING_MULTIPLES.map((v) => ({
  value: v,
  label: v.toFixed(2).replace(/\.00$/, "").replace(/0$/, "")
}));
var ALIGN_OPTIONS = [
  { value: "start", label: "Left", icon: ed("align-left") },
  { value: "center", label: "Center", icon: ed("align-center") },
  { value: "end", label: "Right", icon: ed("align-right") },
  { value: "justify", label: "Justify", icon: ed("align-justify") }
];
var MODE_OPTIONS = [
  { value: "ribbon", label: "Ribbon" },
  { value: "simple", label: "Simple" }
];
var EMPHASIS_OPTIONS = [
  { value: "", label: "None" },
  { value: "dot", label: "Dot" },
  { value: "comma", label: "Sesame" },
  { value: "circle", label: "Circle" },
  { value: "under_dot", label: "Dot (below)" }
];
var DROP_CAP_OPTIONS = [
  { value: "", label: "None" },
  { value: "dropped", label: "Dropped" },
  { value: "in_margin", label: "In margin" }
];
function parseTextEffects(value) {
  if (value == null || value === "") return null;
  try {
    const o = typeof value === "string" ? JSON.parse(value) : value;
    if (o && typeof o === "object") return o;
  } catch {
  }
  return null;
}
function parseDropCap(value) {
  if (value == null || value === "") return null;
  if (value === "dropped" || value === "in_margin") {
    return { lines: 3, kind: value, distancePt: 0 };
  }
  try {
    const o = typeof value === "string" ? JSON.parse(value) : value;
    if (o && typeof o === "object" && "kind" in o) {
      const k = o.kind;
      const kind = k === "in_margin" ? "in_margin" : "dropped";
      const lines = Number(o.lines);
      const distancePt = Number(o.distancePt);
      return {
        lines: Number.isFinite(lines) && lines > 0 ? Math.floor(lines) : 3,
        kind,
        distancePt: Number.isFinite(distancePt) ? distancePt : 0
      };
    }
  } catch {
  }
  return null;
}
function markCommand(d) {
  return {
    id: `character.${d.type}`,
    label: d.label,
    icon: ed(d.icon),
    shortcut: d.shortcut,
    keybinding: d.keybinding,
    kind: "toggle",
    group: "character",
    role: d.role,
    order: d.order,
    cluster: d.cluster,
    menuPath: d.menu,
    surfaces: S_ALL,
    inSimpleToolbar: d.inSimple,
    essential: d.essential,
    requires: d.requires,
    isActive: (a) => a.marks.has(d.type),
    run: (ctx) => {
      ctx.api.toggleMark(d.type);
      ctx.after();
    }
  };
}
var MARK_DEFS = [
  {
    type: "bold",
    label: "Bold",
    icon: "bold",
    menu: ["Format", "Text"],
    shortcut: "\u2318B",
    keybinding: { key: "b", mod: true },
    inSimple: true,
    essential: true,
    role: "toggle",
    order: 0,
    cluster: "biu"
  },
  {
    type: "italic",
    label: "Italic",
    icon: "italic",
    menu: ["Format", "Text"],
    shortcut: "\u2318I",
    keybinding: { key: "i", mod: true },
    inSimple: true,
    essential: true,
    role: "toggle",
    order: 1,
    cluster: "biu"
  },
  {
    type: "underline",
    label: "Underline",
    icon: "underline",
    menu: ["Format", "Text"],
    shortcut: "\u2318U",
    keybinding: { key: "u", mod: true },
    inSimple: true,
    essential: true,
    role: "toggle",
    order: 2,
    cluster: "biu"
  },
  {
    type: "strikeout",
    label: "Strikethrough",
    icon: "strikeout",
    menu: ["Format", "Text"],
    role: "toggle",
    order: 3,
    cluster: "deco"
  },
  {
    type: "code",
    label: "Inline code",
    icon: "code",
    menu: ["Format", "Text"],
    role: "toggle",
    order: 4,
    cluster: "deco"
  },
  {
    type: "superscript",
    label: "Superscript",
    icon: "superscript",
    menu: ["Format", "Text"],
    role: "pair",
    order: 5,
    cluster: "script"
  },
  {
    type: "subscript",
    label: "Subscript",
    icon: "subscript",
    menu: ["Format", "Text"],
    role: "pair",
    order: 6,
    cluster: "script"
  },
  {
    type: "smallcaps",
    label: "Small caps",
    icon: "small-caps",
    menu: ["Format", "Text"],
    shortcut: "\u2318\u21E7K",
    keybinding: { key: "k", mod: true, shift: true },
    role: "toggle",
    order: 7,
    cluster: "caps"
  },
  {
    type: "all_caps",
    label: "All caps",
    icon: "all-caps",
    menu: ["Format", "Text"],
    role: "toggle",
    order: 8,
    cluster: "caps"
  }
];
function styleCommand(d) {
  return {
    id: d.id,
    label: d.label,
    icon: ed(d.icon),
    kind: "toggle",
    group: "styles",
    menuPath: ["Format", "Styles"],
    surfaces: S_RM,
    essential: d.essential,
    isActive: (a) => d.kind === "heading" ? a.kind === "heading" && a.level === d.level : a.kind === d.kind,
    run: (ctx) => {
      ctx.api.setBlockKind(d.kind === "code" ? "code" : d.kind, d.level);
      ctx.after();
    }
  };
}
var STYLE_DEFS = [
  {
    id: "style.paragraph",
    label: "Normal text",
    icon: "paragraph",
    kind: "paragraph",
    essential: true
  },
  { id: "style.h1", label: "Heading 1", icon: "heading-1", kind: "heading", level: 1 },
  { id: "style.h2", label: "Heading 2", icon: "heading-2", kind: "heading", level: 2 },
  { id: "style.h3", label: "Heading 3", icon: "heading-3", kind: "heading", level: 3 },
  { id: "style.h4", label: "Heading 4", icon: "heading-4", kind: "heading", level: 4 },
  { id: "style.h5", label: "Heading 5", icon: "heading-5", kind: "heading", level: 5 },
  { id: "style.h6", label: "Heading 6", icon: "heading-6", kind: "heading", level: 6 },
  { id: "block.blockquote", label: "Block quote", icon: "blockquote", kind: "blockquote" },
  { id: "block.code", label: "Code block", icon: "code-block", kind: "code" }
];
function tableCommand(id, label, icon, group, run, opts = {}) {
  return {
    id,
    label,
    icon: ed(icon),
    kind: "action",
    display: opts.display,
    group,
    role: opts.role,
    order: opts.order,
    cluster: opts.cluster,
    menuPath: ["Table", opts.menuLeaf ?? label],
    surfaces: S_RMC,
    requires: "tables",
    contextScope: "table",
    isEnabled: opts.isEnabled,
    run: (ctx) => {
      run(ctx);
      ctx.after();
    }
  };
}
var EDITOR_COMMANDS = [
  // History (host GAPS — undo/redo history is the host's fenced CAS write path).
  {
    id: "history.undo",
    label: "Undo",
    icon: lu("Undo"),
    shortcut: "\u2318Z",
    keybinding: { key: "z", mod: true },
    kind: "action",
    group: "history",
    role: "pair",
    order: 0,
    cluster: "undo-redo",
    menuPath: ["Edit"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "undo",
    run: (ctx) => {
      ctx.host.undo?.();
      ctx.after();
    }
  },
  {
    id: "history.redo",
    label: "Redo",
    icon: lu("Redo"),
    shortcut: "\u2318\u21E7Z",
    keybinding: { key: "z", mod: true, shift: true },
    kind: "action",
    group: "history",
    role: "pair",
    order: 1,
    cluster: "undo-redo",
    menuPath: ["Edit"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "redo",
    run: (ctx) => {
      ctx.host.redo?.();
      ctx.after();
    }
  },
  // ─── Clipboard ────────────────────────────────────────────────────────────────
  // These commands deliberately carry NO `keybinding`, and the shortcut dispatcher
  // SKIPS `group: 'clipboard'`. `ClipboardEvent.clipboardData` is reachable only
  // inside the native `copy`/`cut`/`paste` events — a keydown handler that
  // `preventDefault()`s ⌘V destroys the paste event outright, so the chord must
  // reach the browser. The `shortcut` strings below are DISPLAY hints; the real
  // gestures are the native events, and these buttons invoke the SAME host code
  // paths those events do (never a second implementation).
  {
    id: "clipboard.cut",
    label: "Cut",
    icon: lu("Scissors"),
    shortcut: "\u2318X",
    kind: "action",
    group: "clipboard",
    role: "action",
    order: 1,
    cluster: "cut-copy",
    menuPath: ["Edit"],
    surfaces: S_RM,
    requires: "clipboard",
    run: (ctx) => {
      ctx.host.cut?.();
      ctx.after();
    }
  },
  {
    id: "clipboard.copy",
    label: "Copy",
    icon: lu("Copy"),
    shortcut: "\u2318C",
    kind: "action",
    group: "clipboard",
    role: "action",
    order: 2,
    cluster: "cut-copy",
    menuPath: ["Edit"],
    surfaces: S_RM,
    requires: "clipboard",
    run: (ctx) => {
      ctx.host.copy?.();
      ctx.after();
    }
  },
  {
    id: "clipboard.paste",
    label: "Paste",
    icon: lu("ClipboardPaste"),
    shortcut: "\u2318V",
    kind: "action",
    group: "clipboard",
    role: "hero",
    order: 0,
    menuPath: ["Edit"],
    surfaces: S_RM,
    // `clipboardRead`, NOT `clipboard`: a browser may forbid a BUTTON-initiated
    // clipboard read even though cut/copy work. Paste then renders honestly
    // DISABLED with the "use ⌘V" tooltip instead of silently doing nothing.
    requires: "clipboardRead",
    run: (ctx) => {
      ctx.host.paste?.();
      ctx.after();
    }
  },
  {
    id: "clipboard.format-painter",
    label: "Format painter",
    icon: lu("Paintbrush"),
    // A TOGGLE, not an action: arming the painter is a visible sticky state the
    // ribbon must render pressed (Word/Docs parity). Same command id.
    kind: "toggle",
    group: "clipboard",
    role: "action",
    order: 3,
    cluster: "painter",
    menuPath: ["Edit"],
    surfaces: S_RM,
    requires: "painter",
    isActive: (a) => a.painterArmed === true,
    run: (ctx) => {
      ctx.host.formatPainter?.();
      ctx.after();
    }
  },
  // Font.
  {
    id: "font.family",
    label: "Font",
    icon: ed("font-family"),
    kind: "select",
    group: "font",
    role: "selector",
    order: 0,
    menuPath: ["Format", "Font"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "styleMarks",
    options: FONT_OPTIONS,
    getValue: (a) => a.fontFamily,
    run: (ctx) => {
      if (ctx.value != null) ctx.api.setFontFamily(String(ctx.value));
      ctx.after();
    }
  },
  {
    id: "font.size",
    label: "Font size",
    icon: ed("font-size"),
    kind: "value",
    group: "font",
    role: "selector",
    // THE MIDDLE OF THE STEPPER, not a selector that happens to sit near one.
    // Word stacks A^/Av beside the size box; Google Docs (and the owner's call
    // here) reads `−  [12]  +`, so the readout sits BETWEEN the two steps that
    // move it and all three share the `font-step` cluster — one tight control
    // whose two ends are opposite verbs and whose middle is the value they act
    // on. The grammar's "opposite pairs adjacent" is satisfied by the pair
    // bracketing its own operand; splitting them apart to keep the two glyphs
    // touching would put the value they edit somewhere else on the strip.
    order: 2,
    cluster: "font-step",
    menuPath: ["Format", "Font"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "styleMarks",
    options: SIZE_OPTIONS,
    getValue: (a) => a.fontSize,
    run: (ctx) => {
      const pt = Number(ctx.value);
      if (Number.isFinite(pt) && pt > 0) ctx.api.setFontSize(pt);
      ctx.after();
    }
  },
  {
    id: "font.grow",
    label: "Increase font size",
    icon: lu("Plus"),
    shortcut: "\u2318\u21E7.",
    // BOTH glyphs: '>' and '.' are the same physical key across layouts, and the
    // controller has always accepted either. An array key keeps that parity.
    keybinding: { key: [">", "."], mod: true, shift: true },
    kind: "action",
    group: "font",
    role: "pair",
    // The PLUS end of the stepper — after the readout it increments.
    order: 3,
    cluster: "font-step",
    menuPath: ["Format", "Font"],
    surfaces: S_RM,
    requires: "styleMarks",
    run: (ctx) => {
      ctx.api.setFontSize(stepFontSize(ctx.active.fontSize, 1));
      ctx.after();
    }
  },
  {
    id: "font.shrink",
    label: "Decrease font size",
    icon: lu("Minus"),
    shortcut: "\u2318\u21E7,",
    keybinding: { key: ["<", ","], mod: true, shift: true },
    kind: "action",
    group: "font",
    role: "pair",
    // The MINUS end of the stepper — before the readout it decrements.
    order: 1,
    cluster: "font-step",
    menuPath: ["Format", "Font"],
    surfaces: S_RM,
    requires: "styleMarks",
    run: (ctx) => {
      ctx.api.setFontSize(stepFontSize(ctx.active.fontSize, -1));
      ctx.after();
    }
  },
  // Character marks + valued marks.
  ...MARK_DEFS.map(markCommand),
  {
    id: "character.color",
    label: "Text color",
    icon: ed("color"),
    kind: "picker",
    group: "character",
    role: "action",
    order: 9,
    cluster: "ink",
    menuPath: ["Format", "Font"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "styleMarks",
    getValue: (a) => a.color,
    // Text always renders in SOME ink, so "no colour mark" is not "no colour" —
    // it is the inherited ink, and the chip says so rather than sitting empty.
    colorValue: { read: (a) => a.color, styleProp: "color", unset: "inherit" },
    run: (ctx) => {
      if (ctx.value != null) ctx.api.setColor(String(ctx.value));
      ctx.after();
    }
  },
  {
    id: "character.highlight",
    label: "Highlight color",
    icon: ed("highlight"),
    kind: "picker",
    group: "character",
    role: "action",
    order: 10,
    cluster: "ink",
    menuPath: ["Format", "Font"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "styleMarks",
    getValue: (a) => a.highlight,
    // Unhighlighted text is genuinely UNPAINTED (web-docz resolves an unmarked
    // run's highlight to `transparent`), so the empty chip is the true reading.
    colorValue: { read: (a) => a.highlight, styleProp: "highlight", unset: "none" },
    run: (ctx) => {
      if (ctx.value != null) ctx.api.setHighlight(String(ctx.value));
      ctx.after();
    }
  },
  {
    id: "character.underline-style",
    label: "Underline style",
    icon: ed("underline-style"),
    kind: "select",
    group: "character",
    role: "advanced",
    menuPath: ["Format", "Font"],
    surfaces: S_RM,
    requires: "styleMarks",
    options: UNDERLINE_OPTIONS,
    getValue: (a) => a.underlineStyle,
    run: (ctx) => {
      if (ctx.value != null)
        ctx.api.setUnderlineStyle(String(ctx.value));
      ctx.after();
    }
  },
  {
    // LETTER SPACING (tracking). The `letter_spacing` mark has been applyable
    // (`FormatApi.setLetterSpacing`) and readable (`ActiveFormat.letterSpacing`)
    // since W1, and the engine paints it — but the catalog never carried a
    // command for it, so an enumeration of every rendered ribbon element found
    // no way to reach it. A shipped capability with no control is not shipped.
    id: "character.letter-spacing",
    label: "Letter spacing",
    icon: lu("MoveHorizontal"),
    kind: "select",
    group: "character",
    role: "advanced",
    menuPath: ["Format", "Font"],
    surfaces: S_RM,
    requires: "styleMarks",
    options: LETTER_SPACING_OPTIONS,
    getValue: (a) => a.letterSpacing,
    run: (ctx) => {
      const pt = Number(ctx.value);
      if (Number.isFinite(pt)) ctx.api.setLetterSpacing(pt);
      ctx.after();
    }
  },
  {
    id: "character.change-case",
    label: "Change case",
    icon: ed("change-case"),
    kind: "select",
    group: "character",
    role: "advanced",
    menuPath: ["Format", "Text"],
    surfaces: S_RM,
    requires: "changeCase",
    options: CASE_OPTIONS,
    run: (ctx) => {
      if (ctx.value != null)
        ctx.api.changeCase(String(ctx.value));
      ctx.after();
    }
  },
  {
    id: "character.clear-format",
    label: "Clear formatting",
    icon: ed("clear-format"),
    shortcut: "\u2318\\",
    keybinding: { key: "\\", mod: true },
    kind: "action",
    group: "character",
    role: "action",
    order: 11,
    cluster: "clear",
    menuPath: ["Format"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "clearFormatting",
    run: (ctx) => {
      ctx.api.clearFormatting();
      ctx.after();
    }
  },
  // ── FEATURE BUNDLE 3 W2 — character DECORATION cluster (Home/Font group) ──────
  // The mark-SET form of the flat docz char decorations (dual-channel): each routes
  // to the docz-backed FormatApi (`setTextEffects`/`setCharBorder`/`setEmphasis`)
  // → the mark-general apply verb → docz.wasm. All gate on `styleMarks`.
  {
    id: "character.text-effects",
    label: "Text effects",
    icon: lu("Sparkles"),
    kind: "picker",
    group: "character",
    role: "action",
    order: 12,
    cluster: "deco",
    menuPath: ["Format", "Text"],
    surfaces: S_RM,
    requires: "styleMarks",
    getValue: (a) => a.textEffects ? JSON.stringify(a.textEffects) : null,
    run: (ctx) => {
      ctx.api.setTextEffects(parseTextEffects(ctx.value));
      ctx.after();
    }
  },
  {
    id: "character.border",
    label: "Character border",
    icon: ed("cell-border"),
    kind: "picker",
    display: "split",
    group: "character",
    role: "action",
    order: 13,
    cluster: "deco",
    menuPath: ["Format", "Text"],
    surfaces: S_RM,
    requires: "styleMarks",
    isActive: (a) => a.charBorder != null,
    run: (ctx) => {
      ctx.api.setCharBorder(parseBorderPayload(ctx.value));
      ctx.after();
    }
  },
  {
    id: "character.emphasis",
    label: "Emphasis mark",
    icon: lu("Dot"),
    kind: "select",
    group: "character",
    role: "action",
    order: 14,
    cluster: "deco",
    menuPath: ["Format", "Text"],
    surfaces: S_RM,
    requires: "styleMarks",
    options: EMPHASIS_OPTIONS,
    getValue: (a) => a.emphasis ?? "",
    run: (ctx) => {
      const v = ctx.value == null ? "" : String(ctx.value);
      ctx.api.setEmphasis(v === "" ? null : v);
      ctx.after();
    }
  },
  // Block styles.
  ...STYLE_DEFS.map(styleCommand),
  // Paragraph geometry.
  {
    id: "para.align",
    label: "Alignment",
    icon: ed("align-left"),
    kind: "radio",
    group: "paragraph",
    role: "action",
    order: 0,
    cluster: "align",
    menuPath: ["Format", "Paragraph"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "paraProps",
    options: ALIGN_OPTIONS,
    getValue: (a) => a.para.textAlign ?? "start",
    run: (ctx) => {
      ctx.api.setAlignment(ctx.value ?? "start");
      ctx.after();
    }
  },
  {
    id: "para.indent",
    label: "Increase indent",
    icon: ed("indent"),
    kind: "action",
    group: "paragraph",
    role: "pair",
    order: 1,
    cluster: "indent",
    menuPath: ["Format", "Paragraph"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "paraProps",
    run: (ctx) => {
      ctx.api.indent();
      ctx.after();
    }
  },
  {
    id: "para.outdent",
    label: "Decrease indent",
    icon: ed("outdent"),
    kind: "action",
    group: "paragraph",
    role: "pair",
    order: 2,
    cluster: "indent",
    menuPath: ["Format", "Paragraph"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "paraProps",
    run: (ctx) => {
      ctx.api.outdent();
      ctx.after();
    }
  },
  {
    id: "para.line-spacing",
    label: "Line spacing",
    icon: ed("line-spacing"),
    kind: "select",
    group: "paragraph",
    role: "selector",
    order: 3,
    menuPath: ["Format", "Paragraph"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "paraProps",
    options: LINE_SPACING_OPTIONS,
    getValue: (a) => a.para.lineSpacing?.rule === "multiple" ? a.para.lineSpacing.value : null,
    run: (ctx) => {
      const v = Number(ctx.value);
      if (Number.isFinite(v)) ctx.api.setLineSpacing({ rule: "multiple", value: v });
      ctx.after();
    }
  },
  // ── FEATURE BUNDLE 1 — paragraph Borders + Shading (`group: 'paragraph'`) ──
  // Two `kind:'picker'` commands mirroring the table Borders / Cell-fill pattern
  // (`table.borders` / `table.cell-fill`), but routed to the PARAGRAPH verbs on the
  // docz-backed FormatApi. "Borders" opens the SAME rich per-side / style / width /
  // colour `<BorderEditor>` popover and decodes the emitted {@link BorderApplyPayload}
  // via `parseBorderPayload`; "Shading" is a plain colour picker whose value is a
  // `#RRGGBB` (or '' → clear). Both gate on `paraProps` (they merge onto the block's
  // docz `ParaProps.borders` / `.shading` carrier — surface-agnostic, round-trips
  // through render + export).
  {
    id: "para.borders",
    label: "Borders",
    icon: ed("cell-border"),
    kind: "picker",
    display: "split",
    group: "paragraph",
    role: "action",
    order: 4,
    cluster: "para-appearance",
    menuPath: ["Format", "Paragraph"],
    surfaces: S_RM,
    requires: "paraProps",
    run: (ctx) => {
      ctx.api.setParaBorders(parseBorderPayload(ctx.value));
      ctx.after();
    }
  },
  {
    id: "para.shading",
    label: "Shading",
    icon: ed("cell-shade"),
    kind: "picker",
    group: "paragraph",
    role: "action",
    order: 5,
    cluster: "para-appearance",
    menuPath: ["Format", "Paragraph"],
    surfaces: S_RM,
    requires: "paraProps",
    // The shading of the paragraph the caret is in — the popover opens on it AND
    // the trigger paints it. Both were blind before: the control could set a
    // shading and then had no idea what it had set.
    getValue: (a) => a.para.shading ?? null,
    // NO `styleProp`: shading is a PARAGRAPH property and `ActiveFormat.para`
    // describes the ONE block the caret is in, so there is no second value for it
    // to disagree with. Claiming a `styleProp` here would wire it to a MIXED answer
    // about character marks, which is a different question entirely.
    colorValue: { read: (a) => a.para.shading ?? null, unset: "none" },
    run: (ctx) => {
      const v = ctx.value == null ? "" : String(ctx.value);
      ctx.api.setParaShading(v === "" ? null : v);
      ctx.after();
    }
  },
  // ── FEATURE BUNDLE 3 (3A) — paragraph DROP CAP (`group: 'paragraph'`) ─────────
  // Paragraph-level (no mark form) — routes to the docz-backed FormatApi
  // `setDropCap` → `ParaProps.dropCap` (present-mask bit6). Gates on `paraProps`.
  {
    id: "para.drop-cap",
    label: "Drop cap",
    icon: lu("Type"),
    kind: "select",
    group: "paragraph",
    role: "action",
    order: 6,
    cluster: "para-appearance",
    menuPath: ["Format", "Paragraph"],
    surfaces: S_RM,
    requires: "paraProps",
    options: DROP_CAP_OPTIONS,
    getValue: (a) => a.para.dropCap?.kind ?? "",
    run: (ctx) => {
      ctx.api.setDropCap(parseDropCap(ctx.value));
      ctx.after();
    }
  },
  // ── FEATURE BUNDLE 2 — Page setup (`layout` tab / `page-layout` group) ──────
  // Three commands for the docz SECTION/PAGE-level page decoration. They are NOT
  // WYSIWYG palette blocks (docz `verbs.zig` `BlockTag` excludes sections) — no
  // palette edit-verb — so they route to the FormatApi `setPage*` seam, which sets
  // the property on the document's active section; a re-render threads it to pdfz
  // layout → the web-page-layout canvas page-frame (page fill / border box / gutter).
  // The `layout` RibbonTab + `page-layout` group ("Page setup") already exist. All
  // three gate on `pageDecoration` (a document surface; hidden on a comment body).
  {
    id: "page.color",
    label: "Page Color",
    icon: ed("color"),
    kind: "picker",
    group: "page-layout",
    role: "action",
    order: 1,
    cluster: "page-appearance",
    menuPath: ["Format", "Page"],
    surfaces: S_RM,
    requires: "pageDecoration",
    // The ACTIVE SECTION's page colour, which the surface reports as context state
    // (`ActiveFormat.pageColor`) — a document-level property, so it has exactly one
    // value and no `styleProp`. A surface that cannot report it leaves the field
    // absent and the chip reads "none" instead of inventing white.
    getValue: (a) => a.pageColor ?? null,
    colorValue: { read: (a) => a.pageColor ?? null, unset: "none" },
    run: (ctx) => {
      const v = ctx.value == null ? "" : String(ctx.value);
      ctx.api.setPageColor(v === "" ? null : v);
      ctx.after();
    }
  },
  {
    id: "page.borders",
    label: "Page Borders",
    icon: ed("cell-border"),
    kind: "picker",
    display: "split",
    group: "page-layout",
    role: "action",
    order: 2,
    cluster: "page-appearance",
    menuPath: ["Format", "Page"],
    surfaces: S_RM,
    requires: "pageDecoration",
    run: (ctx) => {
      ctx.api.setPageBorders(parseBorderPayload(ctx.value));
      ctx.after();
    }
  },
  {
    id: "page.line-numbers",
    label: "Line Numbers",
    icon: ed("ordered-list"),
    kind: "picker",
    group: "page-layout",
    role: "action",
    order: 3,
    cluster: "page-appearance",
    menuPath: ["Format", "Page"],
    surfaces: S_RM,
    requires: "pageDecoration",
    run: (ctx) => {
      ctx.api.setLineNumbering(parseLineNumbering(ctx.value));
      ctx.after();
    }
  },
  // ── PAGE MARGINS — the fourth Page-setup command ────────────────────────────
  // Margins were a BUILD-TIME CONSTANT (elaborate `canvasPageConfig.ts`), never a
  // document property. They ride the SAME `SectionDecoration` carrier as the three
  // above — but a separate `page-geometry` cluster, because they change the TEXT
  // COLUMN rather than decorate the page frame. The popover VALIDATES before it
  // emits, so an unusable box never reaches the layout engine; `''` resets to the
  // page master's margins.
  {
    id: "page.margins",
    label: "Margins",
    icon: lu("SquareDashed"),
    kind: "picker",
    group: "page-layout",
    role: "action",
    order: 4,
    cluster: "page-geometry",
    menuPath: ["Format", "Page"],
    surfaces: S_RM,
    requires: "pageDecoration",
    run: (ctx) => {
      ctx.api.setPageMargins(parsePageMargins(ctx.value));
      ctx.after();
    }
  },
  // Lists.
  {
    id: "lists.bullet",
    label: "Bulleted list",
    icon: ed("bullet-list"),
    kind: "action",
    group: "lists",
    role: "action",
    order: 0,
    cluster: "list-type",
    menuPath: ["Format", "Lists"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "lists",
    run: (ctx) => {
      ctx.api.setListStyle("bullet");
      ctx.after();
    }
  },
  {
    id: "lists.ordered",
    label: "Numbered list",
    icon: ed("ordered-list"),
    kind: "select",
    group: "lists",
    role: "action",
    order: 1,
    cluster: "list-type",
    menuPath: ["Format", "Lists"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "lists",
    options: LIST_FORMAT_OPTIONS,
    // The select had NO `getValue`, so it could never show the format the caret
    // was standing in — it opened on whatever option happened to be first,
    // asserting "Decimal" over a lower-roman list. `bullet` and body text read
    // back as nothing at all rather than as one of the six ordered formats.
    getValue: (a) => orderedListStyle(a.list),
    run: (ctx) => {
      const style = ctx.value != null ? String(ctx.value) : "decimal";
      ctx.api.setListStyle(style);
      ctx.after();
    }
  },
  {
    id: "lists.checklist",
    label: "Checklist",
    icon: ed("task-list"),
    kind: "toggle",
    group: "lists",
    role: "toggle",
    order: 2,
    cluster: "list-type",
    menuPath: ["Format", "Lists"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "lists",
    // Declared `kind:'toggle'` with no `isActive` at all — structurally incapable
    // of ever rendering pressed, the same defect `table.header-toggle` had. The
    // tri-state is the crux and lives in `isChecklist`: an UNTICKED task item
    // (`checked === false`) is still a checklist, and reading only `=== true`
    // leaves a freshly-created one looking off.
    isActive: (a) => isChecklist(a.list),
    // A toggle has to be able to turn the thing OFF, and this one could not.
    // It called `toggleTaskList()` in both directions — the per-item TICK verb,
    // which cycles `null→false→true→false…` and never clears `checked`. So on
    // an already-checklist list, "un-pressing" Checklist ticked a box instead of
    // leaving the checklist, and the control stayed pressed forever. Combined
    // with `setListStyle` not clearing `checked` either (docz fa981f3), a list
    // that became a checklist could not be turned back into ANY other kind of
    // list — a one-way door onto the user's own content.
    //
    // Reading the CURRENT state and asking for the opposite is what makes it a
    // toggle rather than a ratchet. `isChecklist` is the same predicate the
    // pressed state renders from, so the button and the command can never
    // disagree about which direction a press means.
    run: (ctx) => {
      ctx.api.setChecklist(!isChecklist(ctx.active.list));
      ctx.after();
    }
  },
  {
    // docz offers THREE numbering modes — continue the previous run, restart at
    // 1, start at N — and this command hardcoded `restartList(1)`, so two of them
    // were unreachable from the ribbon and the third fired whatever the user
    // meant. It is a picker now: the value decodes through `parseListStart`, and a
    // value that names no mode leaves the document untouched rather than falling
    // back to 1.
    id: "lists.restart",
    label: "Restart numbering",
    icon: ed("list-format"),
    kind: "picker",
    group: "lists",
    role: "action",
    order: 3,
    cluster: "list-ops",
    menuPath: ["Format", "Lists"],
    surfaces: S_RM,
    requires: "lists",
    // Seeds from the ordinal the list REALLY restarts at. `null` (it continues)
    // must reach the control empty — "continues the previous list" and "restarts
    // at 1" are different documents.
    getValue: (a) => listStartValue(a.list),
    run: (ctx) => {
      const choice = parseListStart(ctx.value);
      if (!choice) return;
      ctx.api.restartList(choice.kind === "continue" ? null : choice.start);
      ctx.after();
    }
  },
  // ── LIST NESTING — the demote/promote pair. `setListItemLevel` was a FormatApi
  // verb with no command at all, so the only way to nest a list item was the
  // Tab/Shift-Tab keys; a pointer or touch user had no route to it whatsoever.
  // pdfz paints the real per-level indent now, so the gesture is visible.
  {
    id: "lists.demote",
    label: "Demote list item",
    description: "Nest this item one level deeper",
    icon: ed("indent"),
    kind: "action",
    group: "lists",
    role: "pair",
    order: 4,
    cluster: "list-nest",
    menuPath: ["Format", "Lists"],
    surfaces: S_ALL,
    requires: "lists",
    run: (ctx) => {
      ctx.api.setListItemLevel(1);
      ctx.after();
    }
  },
  {
    id: "lists.promote",
    label: "Promote list item",
    description: "Lift this item one level out",
    icon: ed("outdent"),
    kind: "action",
    group: "lists",
    role: "pair",
    order: 5,
    cluster: "list-nest",
    menuPath: ["Format", "Lists"],
    surfaces: S_ALL,
    requires: "lists",
    run: (ctx) => {
      ctx.api.setListItemLevel(-1);
      ctx.after();
    }
  },
  {
    // `setBulletGlyph` was the other verb with no command. The options ARE the
    // curated palette and the `run` re-checks membership: a glyph is written
    // straight into the document's marker, so an arbitrary string arriving from a
    // surface must be refused rather than trusted because it came from a control
    // that normally only offers eight.
    id: "lists.bullet-glyph",
    label: "Bullet character",
    icon: ed("bullet-list"),
    kind: "select",
    group: "lists",
    role: "advanced",
    order: 6,
    cluster: "list-ops",
    menuPath: ["Format", "Lists"],
    surfaces: S_RM,
    requires: "lists",
    options: BULLET_GLYPH_OPTIONS,
    // READOUT ≠ WRITE-MEMBERSHIP, and this is the pair that states it.
    //
    // `getValue` reports the glyph the DOCUMENT carries — including one outside
    // the curated eight, which an imported document may legitimately have. The
    // select renders such a value as a DISABLED option (CommandControl's `select`
    // branch), so `★` is visible as the value in force and is not re-selectable.
    //
    // `run` gates on the palette regardless of what arrives, because a glyph is
    // written straight into the document's marker and the surface handing it over
    // is not evidence about its contents. The two must be allowed to disagree:
    // suppressing the readout to match the write gate is what left an imported `★`
    // on the page with the control silent about it.
    //
    // `null` (the engine owns the marker) and MIXED stay EMPTY — seeding a
    // plausible '•' would make the select assert a choice nobody made, and the next
    // apply would write that invention into the document for real.
    getValue: (a) => bulletGlyphValue(a.list),
    run: (ctx) => {
      const glyph = ctx.value != null ? String(ctx.value) : "";
      if (!isCuratedBulletGlyph(glyph)) return;
      ctx.api.setBulletGlyph(glyph);
      ctx.after();
    }
  },
  // Insert.
  {
    id: "insert.table",
    label: "Table",
    icon: ed("table"),
    kind: "picker",
    group: "insert-table",
    role: "hero",
    order: 0,
    menuPath: ["Insert"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "blockPalette",
    run: (ctx) => {
      const [rows, cols] = parseGrid(ctx.value, 3, 3);
      ctx.host.insertBlock(ctx.api.insertTable(rows, cols));
      ctx.after();
    }
  },
  {
    id: "insert.image",
    label: "Image",
    icon: ed("image"),
    kind: "picker",
    group: "insert-media",
    role: "hero",
    order: 0,
    menuPath: ["Insert"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    // `images`, NOT `blockPalette`: table insert genuinely works on the canvas
    // surface while an image insert there is actively DESTRUCTIVE (a `path` src
    // fails the whole `pl_layout`, blanking the document). Sharing one key would
    // force a choice between disabling a working control and enabling a harmful
    // one. See the `FormatSupports.images` doc comment for the measurements.
    requires: "images",
    run: (ctx) => {
      const src = ctx.value != null ? String(ctx.value) : "";
      if (src) ctx.host.insertBlock(ctx.api.insertImage({ src }));
      ctx.after();
    }
  },
  {
    id: "insert.link",
    label: "Link",
    icon: ed("link"),
    shortcut: "\u2318K",
    keybinding: { key: "k", mod: true },
    // A `picker`, not a bare action: clicking Link opens a URL popover (the
    // generic `picker-insert.link` text field) and applies the link mark to the
    // selection on submit — a bare action button had NO way to supply the href,
    // so `setLink` was never called (dead button). `getValue` seeds the field
    // with the selection's current link when re-editing.
    kind: "picker",
    group: "insert-structure",
    role: "hero",
    order: 0,
    menuPath: ["Insert"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "selection",
    getValue: (a) => a.link,
    run: (ctx) => {
      if (ctx.value != null && String(ctx.value) !== "") ctx.api.setLink(String(ctx.value));
      ctx.after();
    }
  },
  {
    id: "insert.page-break",
    label: "Page break",
    icon: ed("para-spacing"),
    kind: "action",
    group: "insert-structure",
    role: "action",
    order: 1,
    cluster: "breaks",
    menuPath: ["Insert"],
    surfaces: S_RM,
    // Previously UNGATED, so it rendered permanently enabled on every surface —
    // including the canvas, where it cannot work at all (a `pageBreak` block does
    // not paginate through `pl_layout` and `toHtml()` renders it as the empty
    // string, so it survives neither layout nor save+reload). The DOM surface's
    // raw-HTML `<hr>` break DOES work, so the bit defaults true and only a
    // surface that cannot paginate narrows it — disabled-with-a-reason beats an
    // enabled button that silently does nothing.
    requires: "pageBreak",
    run: (ctx) => {
      ctx.host.insertPageBreak();
      ctx.after();
    }
  },
  // FEATURE BUNDLE 1 — Insert Horizontal Rule. Unlike the page-break (an opaque
  // raw-HTML `<hr>` hack via `host.insertPageBreak`), the rule is a REAL first-class
  // palette block (`{kind:'horizontalRule'}`, docz tag 9) inserted through
  // `host.insertBlock` — the SAME structured-insert path `insert.table` uses, so it
  // round-trips edit→save→reload and renders via the docz `renderBlockDto` verb.
  {
    id: "insert.horizontal-rule",
    label: "Horizontal rule",
    icon: ed("para-spacing"),
    kind: "action",
    group: "insert-structure",
    role: "action",
    order: 2,
    cluster: "breaks",
    menuPath: ["Insert"],
    surfaces: S_RM,
    requires: "blockPalette",
    run: (ctx) => {
      ctx.host.insertBlock(ctx.api.insertHorizontalRule());
      ctx.after();
    }
  },
  // ═══ Contextual TABLE tab (W2a) — labeled sub-clusters, mixed icon / icon+label.
  // The tab SHOWS/HIDES on `env.inTable` and auto-activates on caret-enter (the
  // show/hide + auto-activate lives in <RibbonToolbar>; the gate is the env).
  // The cluster ORDER in the ribbon = the order the groups first appear below.
  //
  // ── The table AS ONE OBJECT ──
  // Two commands the ribbon had NEITHER of, which together were the owner's
  // report: *"I can't select a newly inserted table!"* — and, following that
  // thread, no way to delete one at all. Every other `table.*` command edits the
  // grid's INSIDE; these two are the only ones that treat the table as a thing.
  //
  // "Select table" is the DISCOVERABLE affordance. A pointer gesture also exists
  // (a fourth click inside a cell escalates cell → table, `TABLE_CLICK_DETAIL`),
  // but a four-click gesture is not something a user finds — the Table tab is
  // already contextual on `inTable`, so the moment the caret is in a cell this
  // button is simply visible. Word's leading "Table" group works exactly this way.
  tableCommand(
    "table.select",
    "Select table",
    "table-select",
    "table-whole",
    (c) => c.handle?.table.selectTable(),
    { display: "icon-label", role: "hero", order: 0, cluster: "whole" }
  ),
  // Deliberately NOT beside "Delete row": one edits the grid, the other removes
  // the object, and adjacency is how a user loses a table they meant to trim.
  tableCommand(
    "table.delete",
    "Delete table",
    "table-delete",
    "table-whole",
    (c) => c.handle?.table.deleteTable(),
    {
      // Gated on the HOST, not on `inTable`: removing a whole block is a
      // capability a surface either has or does not, and the legacy DOM/palette
      // surface has no removal seam in its per-block DTO store. Without this the
      // button would render enabled there and silently do nothing.
      isEnabled: (env) => env.host?.deleteBlock === true,
      display: "icon-label",
      role: "action",
      order: 1,
      cluster: "whole"
    }
  ),
  // ── Rows cluster ── (all compact icon-only; the 2-row group flow stacks them)
  // Rows sentence: [Insert above · Insert below] opposite PAIR (adjacent) then Delete.
  tableCommand(
    "table.row-above",
    "Insert row above",
    "table-row-above",
    "table-rows",
    (c) => c.handle?.table.insertRow("above"),
    { role: "pair", order: 0, cluster: "row-insert" }
  ),
  tableCommand(
    "table.row-below",
    "Insert row below",
    "table-row-below",
    "table-rows",
    (c) => c.handle?.table.insertRow("below"),
    { role: "pair", order: 1, cluster: "row-insert" }
  ),
  tableCommand(
    "table.row-delete",
    "Delete row",
    "table-row-delete",
    "table-rows",
    (c) => c.handle?.table.deleteRow(),
    { role: "action", order: 2, cluster: "row-delete" }
  ),
  // ── Columns cluster ── [Insert left · Insert right] PAIR (adjacent) then Delete.
  tableCommand(
    "table.col-left",
    "Insert column left",
    "table-col-left",
    "table-columns",
    (c) => c.handle?.table.insertColumn("left"),
    { role: "pair", order: 0, cluster: "col-insert" }
  ),
  tableCommand(
    "table.col-right",
    "Insert column right",
    "table-col-right",
    "table-columns",
    (c) => c.handle?.table.insertColumn("right"),
    { role: "pair", order: 1, cluster: "col-insert" }
  ),
  tableCommand(
    "table.col-delete",
    "Delete column",
    "table-col-delete",
    "table-columns",
    (c) => c.handle?.table.deleteColumn(),
    { role: "action", order: 2, cluster: "col-delete" }
  ),
  // ── Merge cluster ── [Merge · Split] opposite PAIR; merge REQUIRES a >1-cell range.
  tableCommand(
    "table.merge",
    "Merge cells",
    "table-merge",
    "table-merge",
    (c) => c.handle?.table.mergeCells(),
    {
      isEnabled: (env) => env.hasCellRange === true,
      role: "pair",
      order: 0,
      cluster: "merge-split"
    }
  ),
  tableCommand(
    "table.split",
    "Split cell",
    "table-split",
    "table-merge",
    (c) => c.handle?.table.splitCell(1, 2),
    { role: "pair", order: 1, cluster: "merge-split" }
  ),
  // ── Align cluster ── vertical trio then horizontal trio (each an invisible
  // subgroup); all work on a single cell OR a range, so NO hasCellRange gate.
  tableCommand(
    "table.valign-top",
    "Align top",
    "valign-top",
    "table-align",
    (c) => c.handle?.table.setCellVAlign("top"),
    { role: "action", order: 0, cluster: "v-align" }
  ),
  tableCommand(
    "table.valign-middle",
    "Align middle",
    "valign-middle",
    "table-align",
    (c) => c.handle?.table.setCellVAlign("middle"),
    { role: "action", order: 1, cluster: "v-align" }
  ),
  tableCommand(
    "table.valign-bottom",
    "Align bottom",
    "valign-bottom",
    "table-align",
    (c) => c.handle?.table.setCellVAlign("bottom"),
    { role: "action", order: 2, cluster: "v-align" }
  ),
  tableCommand(
    "table.alignh-left",
    "Align left",
    "align-left",
    "table-align",
    (c) => c.handle?.table.setCellAlignH("start"),
    { role: "action", order: 3, cluster: "h-align" }
  ),
  tableCommand(
    "table.alignh-center",
    "Align center",
    "align-center",
    "table-align",
    (c) => c.handle?.table.setCellAlignH("center"),
    { role: "action", order: 4, cluster: "h-align" }
  ),
  tableCommand(
    "table.alignh-right",
    "Align right",
    "align-right",
    "table-align",
    (c) => c.handle?.table.setCellAlignH("end"),
    { role: "action", order: 5, cluster: "h-align" }
  ),
  // ── W2b — Borders & Colors cluster (`group: 'table-borders'`) ──
  // Two `kind:'picker'` commands: the labeled PRIMARY "Borders" opens the rich
  // per-side / style / width / border-colour + fill popover (<BorderEditor>); the
  // icon-only "Cell fill" opens a fill-colour popover. Both decode the emitted
  // {@link BorderApplyPayload} and route to the EXISTING range-aware handle verbs
  // (`table.setCellBorders` / `table.setCellShading`) — a single caret is a 1×1
  // range, a multi-cell selection the whole range; surface-agnostic (survives the
  // canvas-editor switch). They enable whenever `inTable` (no `hasCellRange` gate).
  {
    id: "table.borders",
    label: "Borders",
    icon: ed("cell-border"),
    kind: "picker",
    display: "split",
    group: "table-borders",
    role: "action",
    order: 0,
    cluster: "appearance",
    menuPath: ["Table", "Borders"],
    surfaces: S_RMC,
    requires: "tables",
    contextScope: "table",
    run: (ctx) => {
      if (ctx.handle) applyBorderPayload(parseBorderPayload(ctx.value), ctx.handle.table);
      ctx.after();
    }
  },
  {
    id: "table.cell-fill",
    label: "Cell fill",
    icon: ed("cell-shade"),
    kind: "picker",
    group: "table-borders",
    role: "action",
    order: 1,
    cluster: "appearance",
    menuPath: ["Table", "Cell fill"],
    surfaces: S_RMC,
    requires: "tables",
    contextScope: "table",
    // A pure FILL — one colour, no line style, no width — so it is colour-valued
    // and its trigger carries the chip instead of a caret. Until a surface reports
    // `cellFill` the read is `undefined` and the control keeps its plain trigger:
    // this used to paint the ACCENT for every cell whether or not it had a fill,
    // which is precisely the confident-wrong-answer this whole change removes.
    colorValue: { read: (a) => a.cellFill, unset: "none" },
    run: (ctx) => {
      if (ctx.handle) applyBorderPayload(parseBorderPayload(ctx.value), ctx.handle.table);
      ctx.after();
    }
  },
  //
  // ── W2c Table styles / Header / Row-style cluster (`group: 'table-styles'`) ──
  // Header-row toggle lives here today as the seed; W2c adds the style-preset
  // gallery + banding controls into this same group without restructuring.
  // The style-preset gallery + header-style + row-banding pickers. All apply via
  // PER-CELL CHANNEL EXPANSION (handle.table.applyTableStyle/setHeaderStyle/
  // setRowBanding write the existing per-cell shading/border channels across the
  // right cell set) so they round-trip through render + PDF export. Honest v1
  // limit: banding does NOT auto-restripe on row insert/delete (docz upstream FR).
  {
    // The Word-style Table Style picker — labeled primary (icon+label). Its popover
    // body is the <TableStyleGallery> preset thumbnails; selecting one emits the
    // preset id, which `run` routes to `applyTableStyle(preset)`.
    id: "table.style",
    label: "Table Style",
    icon: ed("table"),
    kind: "picker",
    display: "icon-label",
    group: "table-styles",
    role: "hero",
    order: 0,
    menuPath: ["Table", "Table style"],
    surfaces: S_RMC,
    requires: "tables",
    contextScope: "table",
    run: (ctx) => {
      if (ctx.value != null && ctx.value !== "") {
        ctx.handle?.table.applyTableStyle(String(ctx.value));
      }
      ctx.after();
    }
  },
  {
    // Header-style: sets the header-row fill (empty value → clear the fill). The
    // verb ensures a structural header row exists (the `<th>` emphasis).
    id: "table.header-style",
    label: "Header style",
    icon: ed("table-header"),
    kind: "picker",
    display: "split",
    group: "table-styles",
    role: "action",
    order: 2,
    cluster: "header",
    menuPath: ["Table", "Header style"],
    surfaces: S_RMC,
    requires: "tables",
    contextScope: "table",
    // NOT a style PRESET despite the name — its popover is a single "Header fill
    // colour" field and its `run` sets exactly one `{ fill }`. One colour in, one
    // colour out: colour-valued. (`table.style` next to it IS a preset gallery and
    // is correctly excluded.)
    colorValue: { read: (a) => a.tableHeaderFill, unset: "none" },
    run: (ctx) => {
      const v = ctx.value == null ? "" : String(ctx.value);
      ctx.handle?.table.setHeaderStyle(v === "" ? null : { fill: v });
      ctx.after();
    }
  },
  {
    // Row-banding: toggle alternating-row shading + choose the band colour.
    // Value convention: '' → OFF; a `#RRGGBB` → ON with that colour.
    id: "table.banding",
    label: "Banded rows",
    icon: ed("cell-shade"),
    kind: "picker",
    display: "split",
    group: "table-styles",
    role: "action",
    order: 3,
    cluster: "banding",
    menuPath: ["Table", "Banded rows"],
    surfaces: S_RMC,
    requires: "tables",
    contextScope: "table",
    // Colour-valued too: its value convention IS a colour (`'' → off`, `#RRGGBB →
    // on with that band colour`), so the chip doubles as the on/off readout — a
    // struck-through chip is banding OFF.
    colorValue: { read: (a) => a.tableBandColor, unset: "none" },
    run: (ctx) => {
      const v = ctx.value == null ? "" : String(ctx.value);
      ctx.handle?.table.setRowBanding(v !== "", v === "" ? void 0 : v);
      ctx.after();
    }
  },
  {
    id: "table.header-toggle",
    label: "Header row",
    icon: ed("table-header"),
    kind: "toggle",
    group: "table-styles",
    role: "toggle",
    order: 1,
    cluster: "header",
    menuPath: ["Table", "Header row"],
    surfaces: S_RMC,
    requires: "tables",
    contextScope: "table",
    // A TOGGLE, not a one-way switch. This used to be a hardcoded
    // `toggleHeader(true)` with no `isActive`, so it could only ever turn the
    // header ON and never rendered pressed — the "the button is there and does
    // nothing" failure class. The desired state is read from the LIVE handle
    // (the source of truth) rather than a snapshot, so a stale env can never
    // invert the click. A surface that cannot answer `tableHeaderOn` degrades to
    // `false` → `toggleHeader(true)`, i.e. exactly the old behaviour.
    isActive: (a) => a.tableHeaderOn === true,
    run: (ctx) => {
      const on = ctx.handle?.tableHeaderOn?.() ?? ctx.active.tableHeaderOn ?? false;
      ctx.handle?.table.toggleHeader(!on);
      ctx.after();
    }
  },
  // Review.
  {
    id: "review.new-comment",
    label: "New comment",
    icon: lu("MessageSquare"),
    kind: "action",
    group: "review",
    menuPath: ["Review"],
    surfaces: S_ALL,
    inSimpleToolbar: true,
    requires: "comment",
    run: (ctx) => {
      ctx.host.addComment?.();
      ctx.after();
    }
  },
  // Find (GAPS).
  {
    id: "find.find",
    label: "Find",
    icon: lu("Search"),
    shortcut: "\u2318F",
    kind: "action",
    group: "find",
    role: "action",
    order: 0,
    cluster: "find",
    menuPath: ["Edit"],
    surfaces: S_RM,
    requires: "find",
    run: (ctx) => {
      ctx.host.find?.();
      ctx.after();
    }
  },
  {
    id: "find.replace",
    label: "Find & replace",
    // NOT `Search` — `find.find` owns the magnifier, and two adjacent buttons
    // with the same glyph are indistinguishable at the ribbon's 18px render.
    // `Replace` is Lucide's canonical find-and-replace mark (curated 2026-07).
    icon: lu("Replace"),
    shortcut: "\u2318H",
    kind: "action",
    group: "find",
    role: "action",
    order: 1,
    cluster: "find",
    menuPath: ["Edit"],
    surfaces: S_RM,
    requires: "find",
    run: (ctx) => {
      ctx.host.find?.();
      ctx.after();
    }
  },
  // View.
  {
    id: "view.mode",
    label: "Toolbar mode",
    icon: lu("LayoutTemplate"),
    kind: "radio",
    group: "view",
    menuPath: ["View"],
    surfaces: S_RM,
    options: MODE_OPTIONS,
    run: (ctx) => {
      if (ctx.value === "ribbon" || ctx.value === "simple") ctx.host.setMode?.(ctx.value);
      ctx.after();
    }
  },
  {
    id: "view.customize",
    label: "Customize toolbar\u2026",
    icon: lu("Settings"),
    kind: "action",
    group: "view",
    menuPath: ["View"],
    surfaces: S_RM,
    requires: "settings",
    run: (ctx) => {
      ctx.host.openSettings?.();
      ctx.after();
    }
  },
  // File.
  {
    id: "file.export",
    label: "Export\u2026",
    icon: lu("Download"),
    kind: "action",
    group: "tools",
    menuPath: ["File"],
    surfaces: S_RM,
    requires: "export",
    run: (ctx) => {
      ctx.host.export?.();
      ctx.after();
    }
  },
  {
    id: "file.print",
    label: "Print\u2026",
    icon: lu("Printer"),
    shortcut: "\u2318P",
    kind: "action",
    group: "tools",
    menuPath: ["File"],
    surfaces: S_RM,
    requires: "print",
    run: (ctx) => {
      ctx.host.print?.();
      ctx.after();
    }
  },
  // Tools (GAPS).
  {
    id: "tools.word-count",
    label: "Word count",
    icon: lu("Hash"),
    kind: "action",
    group: "tools",
    menuPath: ["Tools"],
    surfaces: S_RM,
    requires: "wordCount",
    run: (ctx) => ctx.after()
  },
  {
    id: "tools.spell-check",
    label: "Spelling",
    icon: lu("Spellcheck"),
    kind: "action",
    group: "tools",
    menuPath: ["Tools"],
    surfaces: S_RM,
    requires: "spell",
    run: (ctx) => ctx.after()
  },
  {
    id: "tools.settings",
    label: "Settings\u2026",
    icon: lu("Settings"),
    kind: "action",
    group: "tools",
    menuPath: ["Tools"],
    surfaces: S_RM,
    requires: "settings",
    run: (ctx) => {
      ctx.host.openSettings?.();
      ctx.after();
    }
  },
  // ─── FEATURE BUNDLE 4 (1E) References — one mental model: "cite & list" ─────
  // The reference marks (index_entry / toa_citation) + generated regions (ToC /
  // ToF / ToA / index) now exist in the docz IR + the web-docz wire codec. The
  // FormatApi bridge verbs are the follow-up (the ribbon is frozen this bundle),
  // so every command is gated by the `references` support bit — present but
  // DISABLED with an honest tooltip, lighting up its pre-wired slot the moment
  // the verb lands (the catalog's established `requires` GAP pattern — RULE ZERO:
  // zero document logic here; `run` refocuses via `ctx.after()` until wired).
  {
    id: "references.citation",
    label: "Mark citation",
    description: "Mark the selection as a table-of-authorities citation.",
    icon: lu("Quote"),
    kind: "action",
    group: "references",
    role: "action",
    order: 1,
    cluster: "ref-mark",
    menuPath: ["References", "Mark citation"],
    surfaces: S_RM,
    requires: "references",
    run: (ctx) => {
      ctx.after();
    }
  },
  {
    id: "references.cross-reference",
    label: "Cross-reference",
    description: "Insert a cross-reference field to a figure, table, or heading.",
    icon: lu("CornerUpRight"),
    kind: "action",
    group: "references",
    role: "action",
    order: 2,
    cluster: "ref-insert",
    menuPath: ["References", "Cross-reference"],
    surfaces: S_RM,
    requires: "references",
    run: (ctx) => {
      ctx.after();
    }
  },
  {
    id: "references.index-entry",
    label: "Mark index entry",
    description: "Mark the selection as an index entry (main / sub / cross-reference).",
    icon: lu("Tag"),
    kind: "action",
    group: "references",
    role: "action",
    order: 3,
    cluster: "ref-mark",
    menuPath: ["References", "Mark index entry"],
    surfaces: S_RM,
    requires: "references",
    run: (ctx) => {
      ctx.after();
    }
  },
  {
    id: "references.insert-index",
    label: "Insert index",
    description: "Insert an index region enumerating the marked index entries.",
    icon: lu("ListTree"),
    kind: "action",
    group: "references",
    role: "action",
    order: 4,
    cluster: "ref-insert",
    menuPath: ["References", "Insert index"],
    surfaces: S_RM,
    requires: "references",
    run: (ctx) => {
      ctx.after();
    }
  },
  {
    id: "references.insert-toa",
    label: "Table of authorities",
    description: "Insert a table-of-authorities region enumerating the marked citations.",
    icon: lu("Scale"),
    kind: "action",
    group: "references",
    role: "action",
    order: 5,
    cluster: "ref-insert",
    menuPath: ["References", "Table of authorities"],
    surfaces: S_RM,
    requires: "references",
    run: (ctx) => {
      ctx.after();
    }
  },
  {
    id: "references.insert-tof",
    label: "Table of figures",
    description: "Insert a table-of-figures region from the figure/table/equation caption counters.",
    icon: lu("Images"),
    kind: "action",
    group: "references",
    role: "action",
    order: 6,
    cluster: "ref-insert",
    menuPath: ["References", "Table of figures"],
    surfaces: S_RM,
    requires: "references",
    run: (ctx) => {
      ctx.after();
    }
  },
  {
    id: "references.insert-toc",
    label: "Table of contents",
    description: "Insert a table-of-contents region from the document outline.",
    icon: lu("ListOrdered"),
    kind: "action",
    group: "references",
    role: "action",
    order: 7,
    cluster: "ref-insert",
    menuPath: ["References", "Table of contents"],
    surfaces: S_RM,
    requires: "references",
    run: (ctx) => {
      ctx.after();
    }
  }
];
function parseGrid(value, dr, dc) {
  if (typeof value === "string") {
    const m = value.match(/(\d+)\s*[x,]\s*(\d+)/i);
    if (m) return [Number(m[1]), Number(m[2])];
  }
  return [dr, dc];
}
var EDITOR_COMMAND_IDS = new Set(EDITOR_COMMANDS.map((c) => c.id));
var ESSENTIAL_COMMAND_IDS = new Set(
  EDITOR_COMMANDS.filter((c) => c.essential).map((c) => c.id)
);
var editorToolbarConfigSchema = z.object({
  mode: z.enum(["ribbon", "simple"]).default("ribbon"),
  allowUserOverride: z.boolean().default(true),
  density: z.enum(["comfortable", "compact"]).default("comfortable"),
  disabledCommandIds: z.array(z.string()).default([]),
  disabledGroups: z.array(z.string()).default([]),
  disabledTabs: z.array(z.string()).default([]),
  ribbon: z.object({
    tabOrder: z.array(z.string()).default([]),
    collapsed: z.boolean().default(false)
  }).default({}),
  simple: z.object({
    showToolbar: z.boolean().default(true),
    showMenuBar: z.boolean().default(true),
    toolbarCommandIds: z.array(z.string()).optional()
  }).default({}),
  quickAccess: z.array(z.string()).default([]),
  version: z.literal(1).default(1)
}).strict();
var DEFAULT_TOOLBAR_CONFIG = editorToolbarConfigSchema.parse({});
function parseToolbarConfig(input) {
  return editorToolbarConfigSchema.parse(input ?? {});
}
function parseToolbarConfigSafe(input) {
  const r = editorToolbarConfigSchema.safeParse(input ?? {});
  return r.success ? r.data : DEFAULT_TOOLBAR_CONFIG;
}
function resolveToolbarConfig(workspace, user) {
  const canOverride = workspace.allowUserOverride && user != null;
  const o = canOverride ? user : void 0;
  const disabledCommandIds = new Set(workspace.disabledCommandIds);
  if (o?.disabledCommandIds) for (const id of o.disabledCommandIds) disabledCommandIds.add(id);
  return {
    mode: o?.mode ?? workspace.mode,
    density: o?.density ?? workspace.density,
    allowUserOverride: workspace.allowUserOverride,
    disabledCommandIds,
    disabledGroups: new Set(workspace.disabledGroups),
    disabledTabs: new Set(workspace.disabledTabs),
    ribbon: {
      tabOrder: workspace.ribbon.tabOrder,
      collapsed: o?.ribbonCollapsed ?? workspace.ribbon.collapsed
    },
    simple: {
      showToolbar: o?.simpleShowToolbar ?? workspace.simple.showToolbar,
      showMenuBar: workspace.simple.showMenuBar,
      toolbarCommandIds: workspace.simple.toolbarCommandIds
    },
    quickAccess: o?.quickAccess ?? workspace.quickAccess
  };
}
function resolveToolbarConfigFrom(workspaceRaw, user) {
  return resolveToolbarConfig(parseToolbarConfigSafe(workspaceRaw), user);
}
var HOME_TAB = "home";
function validateToolbarConfig(config, universe) {
  const droppedCmds = [];
  const droppedGroups = [];
  const droppedTabs = [];
  const cmds = config.disabledCommandIds.filter((id) => {
    if (!universe.commandIds.has(id)) {
      droppedCmds.push(id);
      return false;
    }
    if (universe.essentialIds?.has(id)) {
      droppedCmds.push(id);
      return false;
    }
    return true;
  });
  const groups = config.disabledGroups.filter((g) => {
    if (!universe.groups.has(g)) {
      droppedGroups.push(g);
      return false;
    }
    return true;
  });
  const tabs = config.disabledTabs.filter((t) => {
    if (!universe.tabs.has(t) || t === HOME_TAB) {
      droppedTabs.push(t);
      return false;
    }
    return true;
  });
  const tabOrder = config.ribbon.tabOrder.filter((t) => universe.tabs.has(t));
  const quickAccess = config.quickAccess.filter((id) => universe.commandIds.has(id));
  const toolbarCommandIds = config.simple.toolbarCommandIds?.filter(
    (id) => universe.commandIds.has(id)
  );
  return {
    config: {
      ...config,
      disabledCommandIds: cmds,
      disabledGroups: groups,
      disabledTabs: tabs,
      ribbon: { ...config.ribbon, tabOrder },
      simple: { ...config.simple, toolbarCommandIds },
      quickAccess
    },
    dropped: { commandIds: droppedCmds, groups: droppedGroups, tabs: droppedTabs }
  };
}

// src/commands/group-model.ts
var ed2 = (name) => ({ set: "editor", name });
var lu2 = (name) => ({ set: "lucide", name });
var GROUP_MODELS = {
  // ─── Home ──────────────────────────────────────────────────────────────────
  font: {
    id: "font",
    label: "Font",
    mentalModel: "How do I change the characters?",
    icon: ed2("font-family"),
    reductionPriority: 100
  },
  character: {
    id: "character",
    label: "Text",
    mentalModel: "How do I emphasise the text?",
    icon: ed2("bold"),
    reductionPriority: 95
  },
  styles: {
    id: "styles",
    label: "Styles",
    mentalModel: "What KIND of paragraph is this?",
    icon: ed2("heading-1"),
    reductionPriority: 85
  },
  paragraph: {
    id: "paragraph",
    label: "Paragraph",
    mentalModel: "How is the paragraph laid out?",
    icon: ed2("align-left"),
    reductionPriority: 90
  },
  lists: {
    id: "lists",
    label: "Lists",
    mentalModel: "How is this a list?",
    icon: ed2("bullet-list"),
    reductionPriority: 60
  },
  history: {
    id: "history",
    label: "History",
    mentalModel: "Undo / redo my last change.",
    icon: lu2("Undo"),
    reductionPriority: 78
  },
  clipboard: {
    id: "clipboard",
    label: "Clipboard",
    mentalModel: "Move text via the clipboard.",
    icon: lu2("ClipboardPaste"),
    reductionPriority: 74
  },
  find: {
    id: "find",
    label: "Editing",
    mentalModel: "Find / replace text.",
    icon: lu2("Search"),
    reductionPriority: 24
  },
  // ─── Insert ──────────────────────────────────────────────────────────────────
  "insert-table": {
    id: "insert-table",
    label: "Tables",
    mentalModel: "Insert a table.",
    icon: ed2("table"),
    reductionPriority: 70
    // protected on the sparse Insert tab (its hero)
  },
  "insert-media": {
    id: "insert-media",
    label: "Illustrations",
    mentalModel: "Insert a picture.",
    icon: ed2("image"),
    reductionPriority: 55
  },
  "insert-structure": {
    id: "insert-structure",
    label: "Links",
    mentalModel: "Insert a link or a break.",
    icon: ed2("link"),
    reductionPriority: 50
  },
  "page-layout": {
    id: "page-layout",
    label: "Page setup",
    mentalModel: "Lay out the page.",
    icon: ed2("para-spacing"),
    reductionPriority: 32
  },
  // ─── Contextual Table Tools ────────────────────────────────────────────────
  // FIRST in the tab, as Word's leading "Table" group is: before you can reason
  // about a row you have to be able to talk about the table. It is also the only
  // group whose commands act on the table AS A WHOLE, which is why "Delete table"
  // lives here and not beside "Delete row" — one is a grid edit, the other
  // removes the object, and putting them adjacent is how a user loses a table.
  "table-whole": {
    id: "table-whole",
    label: "Table",
    mentalModel: "The table as one object.",
    icon: ed2("table-select"),
    reductionPriority: 70
  },
  "table-rows": {
    id: "table-rows",
    label: "Rows",
    mentalModel: "Add / remove rows.",
    icon: ed2("table-row-below"),
    reductionPriority: 66
  },
  "table-columns": {
    id: "table-columns",
    label: "Columns",
    mentalModel: "Add / remove columns.",
    icon: ed2("table-col-right"),
    reductionPriority: 64
  },
  "table-merge": {
    id: "table-merge",
    label: "Merge",
    mentalModel: "Merge / split cells.",
    icon: ed2("table-merge"),
    reductionPriority: 50
  },
  "table-align": {
    id: "table-align",
    label: "Align",
    mentalModel: "Align cell content.",
    icon: ed2("valign-middle"),
    reductionPriority: 48
  },
  "table-borders": {
    id: "table-borders",
    label: "Borders",
    mentalModel: "How does the cell look?",
    icon: ed2("cell-border"),
    reductionPriority: 46
  },
  "table-styles": {
    id: "table-styles",
    label: "Table Styles",
    mentalModel: "Style the whole table.",
    icon: ed2("table"),
    reductionPriority: 44
  },
  // ─── Review / View / Tools ─────────────────────────────────────────────────
  review: {
    id: "review",
    label: "Comments",
    mentalModel: "Comment on the document.",
    icon: lu2("MessageSquare"),
    reductionPriority: 45
  },
  view: {
    id: "view",
    label: "View",
    mentalModel: "Change the toolbar view.",
    icon: lu2("LayoutTemplate"),
    reductionPriority: 18
  },
  tools: {
    id: "tools",
    label: "Tools",
    mentalModel: "Document tools.",
    icon: lu2("Settings"),
    reductionPriority: 12
  },
  // ─── FEATURE BUNDLE 4 (1E) References ────────────────────────────────────────
  references: {
    id: "references",
    label: "References",
    mentalModel: "Cite sources & list them.",
    icon: lu2("BookMarked"),
    reductionPriority: 22
  }
};
var DEFAULT_REDUCTION_PRIORITY = 20;
function groupModel(group) {
  return GROUP_MODELS[group] ?? {
    id: group,
    label: group,
    mentalModel: group,
    icon: lu2("LayoutTemplate"),
    reductionPriority: DEFAULT_REDUCTION_PRIORITY
  };
}
function groupReductionPriority(group) {
  return groupModel(group).reductionPriority;
}
function effectiveRole(cmd) {
  if (cmd.role) return cmd.role;
  if (cmd.display === "icon-label") return "hero";
  if (cmd.kind === "select" || cmd.kind === "value") return "selector";
  if (cmd.kind === "toggle") return "toggle";
  return "action";
}
function deriveArchetype(cmd) {
  const r = effectiveRole(cmd);
  if (r === "hero") return "large";
  if (r === "selector") return "medium";
  return "small";
}
function sortKey(cmd, index) {
  const role = effectiveRole(cmd);
  const bucket = role === "advanced" ? 1 : 0;
  const effectiveOrder = cmd.order ?? ROLE_ORDER[role];
  return [bucket, effectiveOrder, index];
}
function layoutGroup(commands) {
  const sorted = commands.map((command, index) => ({ command, index })).sort((a, b) => {
    const ka = sortKey(a.command, a.index);
    const kb = sortKey(b.command, b.index);
    return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2];
  }).map(({ command }) => command);
  const clusters = [];
  let current = null;
  sorted.forEach((command, i) => {
    const clusterId = command.cluster ?? `__solo_${command.id}_${i}`;
    const laid = {
      command,
      archetype: deriveArchetype(command),
      role: effectiveRole(command)
    };
    if (current && current.id === clusterId) {
      current.commands.push(laid);
    } else {
      current = { id: clusterId, commands: [laid] };
      clusters.push(current);
    }
  });
  return {
    clusters,
    hero: sorted.find((c) => effectiveRole(c) === "hero")
  };
}
function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
function menuEntry(cmd, active, env, onRun) {
  const enabled = isCommandEnabled(cmd, env);
  const itemId = `menubar-item-${cmd.id}`;
  if (cmd.kind === "toggle") {
    return /* @__PURE__ */ jsx(
      MenuItemCheckbox,
      {
        id: itemId,
        label: cmd.label,
        shortcut: cmd.shortcut,
        checked: cmd.isActive?.(active) ?? false,
        disabled: !enabled,
        onCheckedChange: () => onRun(cmd)
      },
      cmd.id
    );
  }
  const isChooser = cmd.kind === "select" || cmd.kind === "radio" || cmd.kind === "value" && (cmd.options?.length ?? 0) > 0;
  if (isChooser) {
    const value = String(cmd.getValue?.(active) ?? cmd.options?.[0]?.value ?? "");
    return /* @__PURE__ */ jsx(
      MenuSubMenu,
      {
        label: cmd.label,
        disabled: !enabled,
        className: `ela-menusub ela-menusub--cmd-${slug(cmd.id)}`,
        children: /* @__PURE__ */ jsx(MenuRadioGroup, { value, onValueChange: (v) => onRun(cmd, v), children: (cmd.options ?? []).map((o) => /* @__PURE__ */ jsx(
          MenuRadioItem,
          {
            id: `menubar-item-${cmd.id}.${o.value}`,
            label: o.sample ? `${o.label}  ${o.sample}` : o.label,
            value: String(o.value)
          },
          String(o.value)
        )) })
      },
      cmd.id
    );
  }
  if (cmd.kind === "value" || cmd.kind === "picker") return null;
  return /* @__PURE__ */ jsx(
    MenuItem,
    {
      id: itemId,
      label: cmd.label,
      shortcut: cmd.shortcut,
      disabled: !enabled,
      onSelect: () => onRun(cmd)
    },
    cmd.id
  );
}
function renderMenuLevel(topMenu, items, depth, active, env, onRun) {
  const order = [];
  const groups = /* @__PURE__ */ new Map();
  const loose = [];
  for (const cmd of items) {
    const seg = cmd.menuPath[depth];
    if (seg == null) {
      loose.push(cmd);
      continue;
    }
    let bucket = groups.get(seg);
    if (!bucket) {
      bucket = [];
      groups.set(seg, bucket);
      order.push(seg);
    }
    bucket.push(cmd);
  }
  const out = [];
  for (const seg of order) {
    const bucket = groups.get(seg);
    if (bucket.length <= 1) continue;
    const anyEnabled = bucket.some((c) => isCommandEnabled(c, env));
    out.push(
      /* @__PURE__ */ jsx(
        MenuSubMenu,
        {
          label: seg,
          disabled: !anyEnabled,
          className: `ela-menusub ela-menusub--${slug(topMenu)}-${slug(seg)}`,
          children: renderMenuLevel(topMenu, bucket, depth + 1, active, env, onRun)
        },
        `sub:${seg}`
      )
    );
  }
  const flat = [];
  for (const seg of order) {
    const bucket = groups.get(seg);
    if (bucket.length === 1 && bucket[0]) flat.push(bucket[0]);
  }
  for (const c of loose) flat.push(c);
  flat.sort((a, b) => items.indexOf(a) - items.indexOf(b));
  for (const c of flat) {
    const e = menuEntry(c, active, env, onRun);
    if (e) out.push(e);
  }
  return out;
}
function MenuBar(props) {
  const { resolved, active, env, onRun } = props;
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-testid": props.testId ?? "menubar",
      className: "ela-menubar",
      role: "menubar",
      "aria-label": "Editor menu bar",
      children: resolved.menubarMenus.map((menuId) => {
        const items = resolved.menubar.get(menuId) ?? [];
        return /* @__PURE__ */ jsx("span", { className: "ela-menubar__menu", "data-testid": `menubar-menu-${menuId}`, children: /* @__PURE__ */ jsxs(Menu, { children: [
          /* @__PURE__ */ jsx(MenuTrigger, { ariaLabel: menuId, children: menuId }),
          /* @__PURE__ */ jsx(MenuContent, { ariaLabel: menuId, children: renderMenuLevel(menuId, items, 1, active, env, onRun) })
        ] }) }, menuId);
      })
    }
  );
}
var VARIANT_LADDER = ["large", "medium", "small"];
function nextVariant(v) {
  const i = VARIANT_LADDER.indexOf(v);
  return i >= 0 && i < VARIANT_LADDER.length - 1 ? VARIANT_LADDER[i + 1] ?? null : null;
}
function computeGroupVariants(order, variantWidth, containerWidth, priority, options = {}) {
  const gap = options.gapPx ?? 0;
  const chosen = /* @__PURE__ */ new Map();
  for (const g of order) chosen.set(g, "large");
  if (containerWidth <= 0 || order.length === 0) return chosen;
  const total = () => {
    let w = 0;
    for (const g of order) w += variantWidth(g, chosen.get(g));
    if (order.length > 1) w += gap * (order.length - 1);
    return w;
  };
  let guard = order.length * VARIANT_LADDER.length + 1;
  while (total() > containerWidth && guard-- > 0) {
    const candidates = order.map((group, index) => ({ group, index })).filter(({ group }) => {
      const cur = chosen.get(group);
      const nxt = nextVariant(cur);
      return nxt != null && variantWidth(group, nxt) < variantWidth(group, cur);
    }).sort(
      (a, b) => VARIANT_LADDER.indexOf(chosen.get(a.group)) - VARIANT_LADDER.indexOf(chosen.get(b.group)) || priority(a.group) - priority(b.group) || b.index - a.index
    );
    const first = candidates[0];
    if (!first) break;
    const g = first.group;
    chosen.set(g, nextVariant(chosen.get(g)));
  }
  return chosen;
}
function reductionOrderFor(order, priority, depth = VARIANT_LADDER.length - 1) {
  const steps = [];
  const used = /* @__PURE__ */ new Map();
  for (const g of order) used.set(g, 0);
  let guard = order.length * depth + 1;
  while (guard-- > 0) {
    const candidates = order.map((group, index) => ({ group, index })).filter(({ group }) => used.get(group) < depth).sort((a, b) => priority(a.group) - priority(b.group) || b.index - a.index);
    const first = candidates[0];
    if (!first) break;
    const g = first.group;
    steps.push(g);
    used.set(g, used.get(g) + 1);
  }
  return steps;
}
function freshState(key, groups) {
  return { key, variants: allLarge(groups) };
}
function allLarge(groups) {
  return new Map(groups.map((g) => [g, "large"]));
}
function useRibbonScaling(orderedGroups, priority, _options = {}) {
  const key = orderedGroups.join("|");
  const [state, setState] = useState(() => freshState(key, orderedGroups));
  const panelElRef = useRef(null);
  const groupEls = useRef(/* @__PURE__ */ new Map());
  const orderRef = useRef(orderedGroups);
  orderRef.current = orderedGroups;
  const priorityRef = useRef(priority);
  priorityRef.current = priority;
  const stepsRef = useRef(0);
  if (state.key !== key) {
    stepsRef.current = 0;
    setState(freshState(key, orderedGroups));
  }
  const panelRef = useCallback((node) => {
    panelElRef.current = node;
  }, []);
  const registerGroup = useCallback(
    (group) => (node) => {
      if (node) groupEls.current.set(group, node);
      else groupEls.current.delete(group);
    },
    []
  );
  useLayoutEffect(() => {
    const panel = panelElRef.current;
    if (!panel) return;
    if (panel.clientWidth <= 0) return;
    if (panel.scrollWidth <= panel.clientWidth + 1) return;
    if (stepsRef.current > orderRef.current.length * (VARIANT_LADDER.length + 1)) return;
    const candidate = orderRef.current.map((group, index) => ({ group, index })).filter(({ group }) => {
      const cur2 = state.variants.get(group) ?? "large";
      return nextVariant(cur2) != null;
    }).sort(
      (a, b) => VARIANT_LADDER.indexOf(state.variants.get(a.group) ?? "large") - VARIANT_LADDER.indexOf(state.variants.get(b.group) ?? "large") || priorityRef.current(a.group) - priorityRef.current(b.group) || b.index - a.index
    )[0];
    if (!candidate) {
      return;
    }
    stepsRef.current += 1;
    const next = new Map(state.variants);
    const cur = next.get(candidate.group) ?? "large";
    next.set(candidate.group, nextVariant(cur));
    setState((s) => ({ ...s, variants: next }));
  }, [state]);
  useEffect(() => {
    const panel = panelElRef.current;
    if (!panel || typeof ResizeObserver === "undefined") return;
    let timer = null;
    const ro = new ResizeObserver(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        stepsRef.current = 0;
        setState((s) => ({ ...s, variants: allLarge(orderRef.current) }));
      }, 120);
    });
    ro.observe(panel);
    return () => {
      ro.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, []);
  return { panelRef, registerGroup, variants: state.variants, phase: "ready" };
}
function headingLevel(cmd) {
  const byId = /h([1-6])\b/i.exec(cmd.id);
  if (byId) return Number(byId[1]);
  if (cmd.icon?.set === "editor") {
    const byIcon = /^heading-([1-6])$/.exec(cmd.icon.name);
    if (byIcon) return Number(byIcon[1]);
  }
  return null;
}
function styleSlug(cmd) {
  const level = headingLevel(cmd);
  if (level) return `heading-${level}`;
  return cmd.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
var HEADING_SIZE = {
  1: "1.6rem",
  2: "1.4rem",
  3: "1.22rem",
  4: "1.1rem",
  5: "1rem",
  6: "0.9rem"
};
function stylePreview(cmd) {
  const level = headingLevel(cmd);
  if (level) {
    return { fontSize: HEADING_SIZE[level] ?? "1rem", fontWeight: level <= 2 ? 700 : 600 };
  }
  if (/blockquote|quote/.test(cmd.id)) {
    return { fontSize: "0.95rem", fontWeight: 400, fontStyle: "italic" };
  }
  if (/code/.test(cmd.id)) {
    return {
      fontSize: "0.9rem",
      fontWeight: 500,
      fontFamily: "var(--norkron-font-mono, ui-monospace, SFMono-Regular, monospace)"
    };
  }
  return { fontSize: "0.95rem", fontWeight: 400 };
}
function currentStyle(commands, active) {
  return commands.find((c) => c.isActive?.(active)) ?? commands.find((c) => c.id === "style.paragraph") ?? commands[0];
}
function Caret() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      className: "ela-style-gallery__caret",
      viewBox: "0 0 12 12",
      width: "12",
      height: "12",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.6",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": true,
      focusable: "false",
      children: /* @__PURE__ */ jsx("path", { d: "m3 4.5 3 3 3-3" })
    }
  );
}
function CurrentMark() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      className: "ela-style-gallery__check",
      viewBox: "0 0 16 16",
      width: "14",
      height: "14",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": true,
      focusable: "false",
      children: /* @__PURE__ */ jsx("polyline", { points: "3 8 7 12 13 4" })
    }
  );
}
function StyleGallery(props) {
  const { commands, active, env, onRun } = props;
  const current = currentStyle(commands, active);
  const label = current?.label ?? "Normal text";
  return /* @__PURE__ */ jsx("span", { className: "ela-style-gallery", "data-testid": props.testId ?? "toolbar-style-gallery", children: /* @__PURE__ */ jsxs(Menu, { placement: props.placement ?? "bottom-start", children: [
    /* @__PURE__ */ jsxs(MenuTrigger, { ariaLabel: `Paragraph style: ${label}`, children: [
      /* @__PURE__ */ jsx("span", { className: "ela-style-gallery__value", children: label }),
      /* @__PURE__ */ jsx(Caret, {})
    ] }),
    /* @__PURE__ */ jsx(MenuContent, { ariaLabel: "Paragraph styles", className: "ela-style-gallery__menu", children: commands.map((c) => {
      const slug2 = styleSlug(c);
      const isCurrent = c.id === current?.id;
      return /* @__PURE__ */ jsxs(
        MenuItem,
        {
          id: `style-gallery-item-${c.id}`,
          label: c.label,
          disabled: !isCommandEnabled(c, env),
          onSelect: () => onRun(c),
          className: isCurrent ? "ela-style-gallery__row is-current" : "ela-style-gallery__row",
          children: [
            /* @__PURE__ */ jsx("span", { className: "ela-style-gallery__mark", children: isCurrent ? /* @__PURE__ */ jsx(CurrentMark, {}) : null }),
            /* @__PURE__ */ jsx(
              "span",
              {
                className: "ela-style-gallery__preview",
                "data-testid": `style-item-${slug2}`,
                style: stylePreview(c),
                children: c.label
              }
            )
          ]
        },
        c.id
      );
    }) })
  ] }) });
}

// src/commands/surface.ts
var TOOLBAR_ROOT = ".ela-toolbar";
function toolbarHoldsFocus() {
  if (typeof document === "undefined") return false;
  const el = document.activeElement;
  return el instanceof Element && el.closest(TOOLBAR_ROOT) != null;
}
function createRunner(x) {
  const hostAfter = x.after ?? (() => {
  });
  const after = () => {
    if (toolbarHoldsFocus()) x.handle?.focus?.();
    hostAfter();
  };
  return (command, value) => {
    const ctx = {
      api: x.api,
      handle: x.handle,
      active: x.active,
      after,
      host: x.host,
      value
    };
    command.run(ctx);
  };
}
var RIBBON_TAB_LABELS = Object.fromEntries(
  RIBBON_TABS.map((t) => [t.id, t.label])
);
var GROUP_LABELS = {
  clipboard: "Clipboard",
  history: "History",
  font: "Font",
  character: "Text",
  styles: "Styles",
  paragraph: "Paragraph",
  lists: "Lists",
  "insert-table": "Tables",
  "insert-media": "Illustrations",
  "insert-structure": "Insert",
  "page-layout": "Page setup",
  // Contextual TABLE tab clusters (W2a).
  "table-whole": "Table",
  "table-rows": "Rows",
  "table-columns": "Columns",
  "table-merge": "Merge",
  "table-align": "Align",
  "table-borders": "Borders",
  // short title keeps the compact cluster tight
  "table-styles": "Styles",
  // short title keeps the compact cluster tight
  review: "Comments",
  view: "View",
  find: "Editing",
  tools: "Tools"
};
function controlSize(archetype, variant) {
  if (variant === "large") return archetype;
  if (variant === "medium") return archetype === "large" ? "medium" : archetype;
  return "small";
}
var RIBBON_ID_PREFIX = "ela-ribbon";
function RibbonToolbar(props) {
  return /* @__PURE__ */ jsx(TabsRoot, { idPrefix: RIBBON_ID_PREFIX, children: /* @__PURE__ */ jsx(RibbonBody, { ...props }) });
}
function RibbonBody(props) {
  const { resolved, active, env, onRun } = props;
  const allTabs = resolved.ribbonTabs;
  const tabs = visibleRibbonTabs(allTabs, env);
  const firstNonCtx = allTabs.find((t) => !isContextualTab(t)) ?? allTabs[0] ?? "home";
  const [selected, setSelected] = useState(firstNonCtx);
  const lastNonCtx = useRef(firstNonCtx);
  const prevInTable = useRef(Boolean(env.inTable));
  useEffect(() => {
    const now = Boolean(env.inTable);
    if (now === prevInTable.current) return;
    prevInTable.current = now;
    if (now) setSelected("table");
    else setSelected((cur) => isContextualTab(cur) ? lastNonCtx.current : cur);
  }, [env.inTable]);
  const onTabChange = (value) => {
    const next = value;
    if (!isContextualTab(next)) lastNonCtx.current = next;
    setSelected(next);
  };
  const activeTab = tabs.includes(selected) ? selected : tabs[0] ?? "home";
  const registerPanel = useTabsRootContext()?.registerPanel;
  useEffect(() => registerPanel?.(activeTab), [registerPanel, activeTab]);
  const groups = resolved.ribbon.get(activeTab);
  const groupEntries = groups ? [...groups.entries()] : [];
  const groupOrder = groupEntries.map(([group]) => group);
  const cmdsByGroup = new Map(groupEntries);
  const scaling = useRibbonScaling(groupOrder, groupReductionPriority);
  const groupBody = (group, cmds, variant) => {
    const title = GROUP_LABELS[group] ?? group;
    if (group === "styles") {
      return /* @__PURE__ */ jsx(StyleGallery, { commands: cmds, active, env, onRun });
    }
    const layout = layoutGroup(cmds);
    return /* @__PURE__ */ jsx(ButtonGroup, { "aria-label": title, children: layout.clusters.flatMap(
      (cluster, ci) => cluster.commands.map((laid, i) => /* @__PURE__ */ jsx(
        CommandControl,
        {
          command: laid.command,
          active,
          env,
          onRun,
          size: controlSize(laid.archetype, variant),
          clusterStart: ci > 0 && i === 0,
          testIdPrefix: "ribbon-cmd"
        },
        laid.command.id
      ))
    ) });
  };
  const groupCell = (group, cmds, groupRef) => {
    const title = GROUP_LABELS[group] ?? group;
    const variant = scaling.variants.get(group) ?? "large";
    const stylesMod = group === "styles" ? " ela-ribbon__group--styles" : "";
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref: groupRef,
        className: `ela-ribbon__group${stylesMod}`,
        "data-testid": `ribbon-group-${group}`,
        "data-variant": variant,
        children: [
          groupBody(group, cmds, variant),
          /* @__PURE__ */ jsx("span", { className: "ela-ribbon__group-title", children: title })
        ]
      },
      group
    );
  };
  return /* @__PURE__ */ jsxs("div", { "data-testid": props.testId ?? "ribbon", className: "ela-ribbon", children: [
    /* @__PURE__ */ jsx(
      Tabs,
      {
        testId: "ribbon-tablist",
        ariaLabel: "Ribbon tabs",
        variant: "underline",
        value: activeTab,
        onChange: onTabChange,
        tabs: tabs.map((t) => ({
          value: t,
          label: RIBBON_TAB_LABELS[t] ?? t,
          testId: `ribbon-tab-${t}`
        }))
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        ref: scaling.panelRef,
        className: "ela-ribbon__panel",
        "data-testid": `ribbon-tabpanel-${activeTab}`,
        role: "tabpanel",
        id: `${RIBBON_ID_PREFIX}-panel-${activeTab}`,
        "aria-labelledby": `${RIBBON_ID_PREFIX}-tab-${activeTab}`,
        "aria-label": RIBBON_TAB_LABELS[activeTab] ?? activeTab,
        children: groupOrder.map(
          (group) => groupCell(group, cmdsByGroup.get(group) ?? [], scaling.registerGroup(group))
        )
      }
    )
  ] });
}
function SimpleToolbar(props) {
  const { commands, active, env, onRun } = props;
  const styleCommands = props.styleCommands ?? [];
  const max = props.maxVisible ?? commands.length;
  const visible = commands.slice(0, max);
  const overflow = commands.slice(max);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "data-testid": props.testId ?? "simple-toolbar",
      className: "ela-simple-toolbar",
      role: "toolbar",
      "aria-label": "Formatting",
      children: [
        styleCommands.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            StyleGallery,
            {
              commands: styleCommands,
              active,
              env,
              onRun,
              testId: "simple-style-gallery"
            }
          ),
          /* @__PURE__ */ jsx("span", { className: "ela-simple-toolbar__divider", "aria-hidden": "true" })
        ] }),
        /* @__PURE__ */ jsx(ButtonGroup, { "aria-label": "Formatting controls", children: visible.map((c, i) => {
          const boundary = i > 0 && visible[i - 1]?.group !== c.group;
          return /* @__PURE__ */ jsxs(Fragment$1, { children: [
            boundary && /* @__PURE__ */ jsx("span", { className: "ela-simple-toolbar__divider", "aria-hidden": "true" }),
            /* @__PURE__ */ jsx(
              CommandControl,
              {
                command: c,
                active,
                env,
                onRun,
                testIdPrefix: "simple-cmd"
              }
            )
          ] }, c.id);
        }) }),
        overflow.length > 0 && /* @__PURE__ */ jsx("span", { className: "ela-simple-toolbar__overflow", "data-testid": "simple-overflow", children: /* @__PURE__ */ jsxs(Menu, { children: [
          /* @__PURE__ */ jsx(MenuTrigger, { ariaLabel: "More commands", children: "\u22EF" }),
          /* @__PURE__ */ jsx(MenuContent, { ariaLabel: "More commands", children: overflow.map((c) => /* @__PURE__ */ jsx(
            MenuItem,
            {
              id: `simple-overflow-item-${c.id}`,
              label: c.label,
              shortcut: c.shortcut,
              disabled: !isCommandEnabled(c, env),
              onSelect: () => onRun(c)
            },
            c.id
          )) })
        ] }) })
      ]
    }
  );
}

// src/commands/shortcuts.ts
var CARET_GESTURE_CHORDS = [
  { key: "Tab" },
  { key: "Tab", shiftKey: true },
  { key: "Enter", shiftKey: true },
  { key: "Backspace" },
  { key: "Enter", metaKey: true },
  { key: "Enter", ctrlKey: true }
];
function createShortcutDispatcher(opts) {
  const skip = new Set(opts.skipGroups ?? ["clipboard"]);
  const all = (opts.commands ?? EDITOR_COMMANDS).filter((c) => !skip.has(c.group));
  return (ev) => {
    const outcome = runShortcut(all, ev, opts.getEnv(), opts.getContext);
    switch (outcome.status) {
      case "dispatched":
        return true;
      case "needsValue":
        return opts.onPicker?.(outcome.command) === true;
      default:
        return false;
    }
  };
}
var DEFAULT_RESOLVED = resolveToolbarConfig(DEFAULT_TOOLBAR_CONFIG);
function ToolbarShell(props) {
  const config = props.config ?? DEFAULT_RESOLVED;
  const commands = props.commands ?? EDITOR_COMMANDS;
  const mode = props.mode ?? config.mode;
  const env = props.env ?? { supports: props.api.supports };
  const resolved = resolveCommands(commands, config);
  const onRun = createRunner({
    api: props.api,
    handle: props.handle,
    active: props.active,
    host: props.host,
    after: props.after
  });
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-testid": props.testId ?? "editor-toolbar",
      "data-mode": mode,
      className: "ela-toolbar ela-toolbar--shell",
      children: mode === "ribbon" ? /* @__PURE__ */ jsx(RibbonToolbar, { resolved, active: props.active, env, onRun }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        config.simple.showMenuBar && /* @__PURE__ */ jsx(MenuBar, { resolved, active: props.active, env, onRun }),
        config.simple.showToolbar && /* @__PURE__ */ jsx(
          SimpleToolbar,
          {
            commands: resolved.simpleToolbar,
            styleCommands: resolved.styleGallery,
            active: props.active,
            env,
            onRun,
            maxVisible: props.simpleMaxVisible
          }
        )
      ] })
    }
  );
}
var UTF84 = new TextEncoder();
var PAGE_BREAK_HTML = '<hr class="docz-page-break">';
function createBlockStructural(deps) {
  function mintBlockId() {
    const g = globalThis;
    const rnd = g.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `ela-b-${rnd}`;
  }
  function caretTarget() {
    const container = deps.getContainer();
    const sel = container.ownerDocument.getSelection?.();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!container.contains(range.startContainer)) return null;
    const blockRoot = findBlockRoot(range.startContainer);
    if (!blockRoot || !blockRoot.classList.contains(DOCZ_BLOCK_CLASS)) return null;
    if (!container.contains(blockRoot)) return null;
    const blockId = blockRoot.getAttribute("data-block-id");
    if (!blockId) return null;
    const plain = blockRoot.textContent ?? "";
    const startChar = charIndexWithin(blockRoot, range.startContainer, range.startOffset);
    const endChar = charIndexWithin(blockRoot, range.endContainer, range.endOffset);
    const lo = Math.min(startChar, endChar);
    const hi = Math.max(startChar, endChar);
    return {
      blockId,
      start: UTF84.encode(plain.slice(0, lo)).length,
      end: UTF84.encode(plain.slice(0, hi)).length
    };
  }
  function caretAtMergeableBlockStart() {
    const t = caretTarget();
    if (!t || t.start !== 0 || t.end !== 0) return false;
    return deps.getHandle().blocks.findIndex((b) => b.id === t.blockId) > 0;
  }
  function splitBlockAt(blockId, at, deleteBytes) {
    if (deps.isReadOnlyBlock(blockId)) return null;
    let handle = deps.getHandle();
    const baseVersionId = handle.baseVersionId;
    if (deleteBytes > 0) {
      handle = handle.editBlockText(blockId, { at, deleteBytes, insert: "" });
    }
    deps.dropOverride(blockId);
    const newBlockId = mintBlockId();
    handle = handle.splitBlock(blockId, at, newBlockId);
    deps.setHandle(handle);
    deps.rerender();
    deps.restoreCaret(newBlockId, 0);
    const patch = { blockId, baseVersionId };
    deps.onChange(patch);
    return patch;
  }
  function insertSoftBreakAtCaret() {
    const target = caretTarget();
    if (!target) return;
    deps.applyTextEdit({
      blockId: target.blockId,
      at: target.start,
      deleteBytes: target.end - target.start,
      insert: "\n"
    });
  }
  function mergeBlockBackward() {
    const target = caretTarget();
    if (!target) return null;
    const handle = deps.getHandle();
    const idx = handle.blocks.findIndex((b) => b.id === target.blockId);
    if (idx <= 0) return null;
    const prev = handle.blocks[idx - 1];
    if (!prev?.id) return null;
    if (deps.isReadOnlyBlock(target.blockId) || deps.isHtmlBlock(prev.id) || deps.isHtmlBlock(target.blockId)) {
      return null;
    }
    const baseVersionId = handle.baseVersionId;
    const joinByte = UTF84.encode(prev.text).length;
    deps.dropOverride(prev.id);
    deps.dropOverride(target.blockId);
    deps.setHandle(handle.mergeBlock(target.blockId));
    deps.rerender();
    deps.restoreCaret(prev.id, joinByte);
    const patch = { blockId: prev.id, baseVersionId };
    deps.onChange(patch);
    return patch;
  }
  function insertPageBreak() {
    if (!deps.canEdit()) return null;
    const target = caretTarget();
    const handle = deps.getHandle();
    const blocks = handle.blocks;
    const afterId = target?.blockId ?? blocks[blocks.length - 1]?.id;
    if (!afterId) return null;
    const baseVersionId = handle.baseVersionId;
    const pageBreakId = mintBlockId();
    deps.setHandle(
      handle.insertBlockAfter(afterId, { id: pageBreakId, text: PAGE_BREAK_HTML, kind: "html" })
    );
    deps.rerender();
    const patch = { blockId: pageBreakId, baseVersionId };
    deps.onChange(patch);
    return patch;
  }
  return {
    caretTarget,
    caretAtMergeableBlockStart,
    splitBlockAt,
    insertSoftBreakAtCaret,
    mergeBlockBackward,
    insertPageBreak
  };
}

// src/controller-keys.ts
function createKeyHandler(deps) {
  return (ev) => {
    if (deps.getMode() !== "edit") return;
    const e = ev;
    deps.setSuppress(null);
    const plain = !e.metaKey && !e.ctrlKey && !e.altKey;
    if (e.key === "Tab" && plain) {
      if (deps.gestures.tab(e.shiftKey)) e.preventDefault();
      return;
    }
    if (e.key === "Enter" && e.shiftKey && plain) {
      e.preventDefault();
      deps.setSuppress("insertLineBreak");
      deps.gestures.softBreak();
      return;
    }
    if (e.key === "Backspace" && plain && !e.shiftKey) {
      if (deps.gestures.mergeBackward()) {
        e.preventDefault();
        deps.setSuppress("deleteContentBackward");
      }
      return;
    }
    if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
    if (e.key === "Enter") {
      e.preventDefault();
      deps.setSuppress("insertParagraph");
      deps.gestures.pageBreak();
      return;
    }
    if (deps.dispatch(e)) e.preventDefault();
  };
}

// src/table-transforms.ts
function ext3(module) {
  return module;
}
function supportsTables(module) {
  return ext3(module).supportsTableVerbs === true;
}

// src/document-format.ts
function createDocumentFormatApi(handle, module) {
  const removeDecoration = (kind) => {
    const cur = activeDecorationMark(handle.activeFormat(), kind);
    if (!cur) return null;
    return handle.toggleStyleMark?.(cur) ?? null;
  };
  const capable = (key, wasmBit) => wasmBit && handle.capabilities?.[key] !== false;
  return {
    toggleMark(type) {
      return handle.toggleMark(type);
    },
    setLink(href) {
      if (!href) return null;
      return handle.setMark({ type: "link", href });
    },
    // ─── The link popover's contract (document surface) ──────────────────────
    //
    // HONEST PARTIAL: the href half works end to end; the TEXT half does not.
    // Replacing a document block's text needs the caret's block + byte range,
    // which only `controller.ts`'s `selectionTarget()` can resolve —
    // `ElaborateEditorHandle` exposes `applyTextEdit` (needs a `blockId`) and
    // `buildSelector` (returns null for a collapsed range) but no selection
    // target. So `canEditText` is FALSE here: the popover disables its text field
    // with a truthful tooltip rather than pretending an edit will land.
    linkTarget() {
      const sel = typeof document === "undefined" ? null : document.getSelection?.();
      const text = sel && sel.rangeCount > 0 ? sel.toString() : "";
      const href = handle.activeFormat().link;
      if (!text && href == null) return null;
      return { text, href, canEditText: false };
    },
    applyLink(opts) {
      const href = opts.href.trim();
      if (!href) return null;
      return handle.setMark({ type: "link", href });
    },
    restoreFocus() {
      handle.root?.focus?.();
    },
    setColor(color) {
      return handle.setMark({ type: "color", color });
    },
    setHighlight(color) {
      return handle.setMark({ type: "highlight", color });
    },
    setFontFamily(name) {
      if (!name) return null;
      return handle.setMark({ type: "font_family", font: name });
    },
    setFontSize(pt) {
      if (!Number.isFinite(pt) || pt <= 0) return null;
      return handle.setMark({ type: "font_size", sizePt: pt });
    },
    setLetterSpacing(pt) {
      if (!Number.isFinite(pt)) return null;
      return handle.setMark({ type: "letter_spacing", spacingPt: pt });
    },
    setUnderlineStyle(style, color) {
      return handle.setMark({ type: "underline", style, color });
    },
    // FEATURE BUNDLE 3 W2 — char-decoration marks route through handle.setMark;
    // a NULL (or all-default) value is a CLEAR, never an "ignore" — it removes the
    // mark currently on the selection through the native docz toggle verb. Without
    // this, "None" on the Emphasis dropdown / "Clear" on the effects + border
    // pickers were dead controls: the mark could be set but never taken off.
    setTextEffects(effects) {
      if (effects == null || textEffectsIsDefault(effects)) return removeDecoration("text_effects");
      return handle.setMark({ type: "text_effects", effects });
    },
    setCharBorder(payload) {
      const border = charBorderFromPayload(payload);
      if (!border) return removeDecoration("char_border");
      return handle.setMark({ type: "char_border", border });
    },
    setEmphasis(emphasis) {
      if (emphasis == null) return removeDecoration("emphasis");
      return handle.setMark({ type: "emphasis", emphasis });
    },
    clearFormatting() {
      return handle.clearFormatting();
    },
    changeCase(mode) {
      return handle.changeCase(mode);
    },
    setBlockKind(kind, level) {
      return handle.setBlockKind(kind, level);
    },
    // ─── W2 paragraph geometry — route to the document handle's setParaProps ────
    setAlignment(align) {
      return handle.setParaProps({ textAlign: align });
    },
    indent() {
      const cur = currentLeftIndentPt(handle.activeFormat().para);
      return handle.setParaProps({ indent: { leftPt: cur + INDENT_STEP_PT } });
    },
    outdent() {
      const cur = currentLeftIndentPt(handle.activeFormat().para);
      return handle.setParaProps({ indent: { leftPt: Math.max(0, cur - INDENT_STEP_PT) } });
    },
    setLineSpacing(spacing) {
      return handle.setParaProps({ lineSpacing: spacing });
    },
    setSpaceBefore(pt) {
      return handle.setParaProps({ spaceBeforePt: pt });
    },
    setSpaceAfter(pt) {
      return handle.setParaProps({ spaceAfterPt: pt });
    },
    setFirstLineIndent(pt) {
      return handle.setParaProps({ indent: { firstLinePt: pt } });
    },
    setHangingIndent(pt) {
      return handle.setParaProps({ indent: { hangingPt: pt } });
    },
    setTabStops(stops) {
      return handle.setParaProps({ tabStops: stops });
    },
    // ─── FEATURE BUNDLE 1 — paragraph borders + shading route to setParaProps ────
    // The borders picker emits the shared BorderEditor payload (per-cell edges); we
    // map those box edges → the paragraph `ParaBorders` DTO and merge onto the
    // block's docz `ParaProps.borders` carrier (a `fill` payload routes to shading).
    setParaBorders(payload) {
      if (!payload) return null;
      if (payload.kind === "borders") {
        return handle.setParaProps({
          borders: payload.borders ? cellBordersToParaBorders(payload.borders) : null
        });
      }
      return handle.setParaProps({ shading: payload.fill });
    },
    setParaShading(hex) {
      return handle.setParaProps({ shading: hex });
    },
    setDropCap(dropCap) {
      return handle.setParaProps({ dropCap });
    },
    // ─── FEATURE BUNDLE 2 — page decoration routes to setPageDecoration ─────────
    // The page color / border box / line-numbering set the docz Section/Page
    // properties on the document's active section; the borders picker reuses the
    // shared BorderEditor payload (box edges → the page border box; a `fill`
    // payload routes to the page color). A re-render threads them to pdfz layout →
    // the web-page-layout canvas page-frame.
    setPageColor(hex) {
      return handle.setPageDecoration({ pageColor: hex });
    },
    setPageBorders(payload) {
      if (!payload) return handle.setPageDecoration({ pageBorders: null });
      if (payload.kind === "borders") {
        return handle.setPageDecoration({
          pageBorders: payload.borders ? cellBordersToPageBorders(payload.borders) : null
        });
      }
      return handle.setPageDecoration({ pageColor: payload.fill });
    },
    setLineNumbering(config) {
      return handle.setPageDecoration({ lineNumbering: config });
    },
    setPageMargins(margins) {
      return handle.setPageDecoration({ pageMargins: margins });
    },
    // ─── W4 lists — route to the document handle's real docz list verbs ─────────
    setListStyle(style) {
      return handle.setListStyle(style);
    },
    setListItemLevel(delta) {
      return handle.setListItemLevel(delta);
    },
    restartList(start) {
      return handle.restartList(start);
    },
    toggleTaskList() {
      return handle.toggleTaskList();
    },
    setChecklist(on) {
      return handle.setChecklist(on);
    },
    setBulletGlyph(glyph) {
      return handle.setBulletGlyph(glyph);
    },
    insertList(ordered, items) {
      const texts = items && items.length > 0 ? items : ["List item"];
      return {
        kind: "list",
        style: ordered ? "decimal" : "bullet",
        items: texts.map((t) => [{ text: t, marks: [] }])
      };
    },
    insertTable(rows, cols) {
      return emptyTable(Math.max(1, rows), Math.max(1, cols));
    },
    insertHorizontalRule() {
      return { kind: "horizontalRule" };
    },
    insertImage(opts) {
      return { kind: "image", src: opts.src, srcKind: "path", alt: opts.alt };
    },
    renderBlockHtml() {
      return "";
    },
    active() {
      return handle.activeFormat();
    },
    supports: {
      styleMarks: true,
      // The block/insert palette reads its OWN wasm bit (`supportsBlockPalette` —
      // the same probe the fragment API uses), NOT the table bit. Gating the whole
      // insert lane on editable-TABLE support hid the image / list / rule inserts
      // on every wasm without the `dz_table_*` verbs.
      blockPalette: capable("blockPalette", module?.supportsBlockPalette === true),
      toggleOff: true,
      clearFormatting: module ? supportsClearMarks(module) : false,
      changeCase: module ? supportsChangeCase(module) : false,
      paraProps: module ? supportsParaProps(module) : false,
      tables: capable("tables", module ? supportsTables(module) : false),
      lists: capable("lists", module ? supportsLists(module) : false),
      // FEATURE BUNDLE 2 — page decoration (page color / borders / line numbering)
      // is a document-surface capability: pdfz emits the page-frame records on the
      // EXISTING records with NO wire change, so it needs no wasm verb probe — a
      // document surface always threads the docz Section/Page properties to canvas.
      // A host that cannot perform the verb declares `capabilities.pageDecoration:
      // false` and gets a DISABLED button instead of an ENABLED no-op.
      pageDecoration: capable("pageDecoration", true),
      // FEATURE BUNDLE 4 (1E) — the reference apply/insert verbs + FormatApi
      // bridges have not landed (the ribbon is frozen this bundle), so the
      // References lane stays honestly DISABLED even on a document surface.
      references: false,
      // The DOM surface's page break DOES work: it carries an opaque `kind:'html'`
      // `<hr>` the server ingest preserves as raw_html, so it survives
      // edit -> save -> reload. Default TRUE and let a surface that cannot
      // paginate NARROW it (`handle.capabilities.pageBreak = false`) — the canvas
      // does, on two measured upstream gaps. Never regress this to disabled here.
      pageBreak: capable("pageBreak", true),
      // Image insert rides the same wasm probe as the rest of the insert palette
      // on the DOM surface, but gets its OWN key so a surface can refuse images
      // while keeping table/rule inserts — which is exactly the canvas's shape.
      images: capable("images", module?.supportsBlockPalette === true)
    }
  };
}

// src/format-painter.ts
var VALUELESS = [
  "bold",
  "italic",
  "strikeout",
  "smallcaps",
  "superscript",
  "subscript",
  "code",
  "all_caps"
];
function applyValued(api, a) {
  if (a.link) api.setLink(a.link);
  if (a.color) api.setColor(a.color);
  if (a.highlight) api.setHighlight(a.highlight);
  if (a.fontFamily) api.setFontFamily(a.fontFamily);
  if (a.fontSize != null) api.setFontSize(a.fontSize);
  if (a.letterSpacing != null) api.setLetterSpacing(a.letterSpacing);
  if (a.underlineStyle) api.setUnderlineStyle(a.underlineStyle);
  if (a.textEffects) api.setTextEffects(a.textEffects);
  if (a.emphasis) api.setEmphasis(a.emphasis);
  if (a.charBorder) {
    api.setCharBorder({
      kind: "borders",
      borders: {
        top: a.charBorder,
        left: a.charBorder,
        bottom: a.charBorder,
        right: a.charBorder
      }
    });
  }
}
function applyParagraph(api, a) {
  const p = a.para;
  if (p.textAlign) api.setAlignment(p.textAlign);
  if (p.lineSpacing) api.setLineSpacing(p.lineSpacing);
  if (p.spaceBeforePt != null) api.setSpaceBefore(p.spaceBeforePt);
  if (p.spaceAfterPt != null) api.setSpaceAfter(p.spaceAfterPt);
  if (p.indent?.firstLinePt != null) api.setFirstLineIndent(p.indent.firstLinePt);
  if (p.indent?.hangingPt != null) api.setHangingIndent(p.indent.hangingPt);
  if (p.tabStops && p.tabStops.length > 0) api.setTabStops(p.tabStops);
  if (p.shading) api.setParaShading(p.shading);
  if (p.dropCap) api.setDropCap(p.dropCap);
  if (p.borders) api.setParaBorders({ kind: "borders", borders: p.borders });
}
function createFormatPainter(api) {
  let painted = null;
  let isSticky = false;
  const disarm = () => {
    painted = null;
    isSticky = false;
  };
  return {
    arm(sticky = false) {
      painted = { active: api.active() };
      isSticky = sticky;
    },
    disarm,
    armed: () => painted !== null,
    sticky: () => isSticky,
    painted: () => painted,
    apply() {
      const p = painted;
      if (!p) return false;
      const a = p.active;
      api.clearFormatting();
      for (const type of VALUELESS) {
        if (a.marks.has(type)) api.toggleMark(type);
      }
      applyValued(api, a);
      api.setBlockKind(a.kind, a.level ?? void 0);
      applyParagraph(api, a);
      if (!isSticky) disarm();
      return true;
    }
  };
}

// src/controller-services.ts
function createEditorServices(deps) {
  const formatApi = createDocumentFormatApi(deps.api, deps.getModule());
  const painter = createFormatPainter(formatApi);
  function selectionBlocks() {
    const t = deps.selectionTarget();
    if (!t || t.start >= t.end) return [];
    const frag = deps.blockFragment(t.blockId);
    if (!frag) return [];
    const slice = sliceFragment(deps.getModule(), frag, { start: t.start, end: t.end });
    if (!slice.ok) return [];
    return [{ id: t.blockId, kind: "paragraph", runs: [...slice.fragment.runs] }];
  }
  function deleteSelection() {
    const t = deps.selectionTarget();
    if (!t || t.start >= t.end) return;
    deps.api.applyTextEdit({
      blockId: t.blockId,
      at: t.start,
      deleteBytes: t.end - t.start,
      insert: ""
    });
  }
  function insertBlocks(blocks) {
    const t = deps.caretTarget() ?? deps.selectionTarget();
    if (!t) return;
    const first = blocks[0];
    if (blocks.length === 1 && first?.kind === "paragraph") {
      deps.api.applyTextEdit({
        blockId: t.blockId,
        at: t.start,
        deleteBytes: Math.max(0, t.end - t.start),
        insert: first.runs.map((r) => r.text).join("")
      });
      return;
    }
    let anchorId = t.blockId;
    for (const b of blocks) {
      const id = `ela-paste-${Math.random().toString(36).slice(2)}`;
      const text = b.kind === "paragraph" || b.kind === "heading" ? b.runs.map((r) => r.text).join("") : "";
      deps.setHandle(
        deps.getHandle().insertBlockAfter(anchorId, {
          id,
          text,
          kind: b.kind === "heading" ? "heading" : "paragraph",
          ...b.kind === "heading" ? { level: b.level } : {}
        })
      );
      anchorId = id;
    }
    deps.render();
    deps.onChange({ blockId: t.blockId, baseVersionId: deps.getHandle().baseVersionId });
  }
  const clipboard = createClipboardController({
    container: deps.container,
    getModule: deps.getModule,
    selectionBlocks,
    deleteSelection,
    insertBlocks,
    beforeMutate: deps.bank,
    canEdit: deps.canEdit
  });
  function env() {
    const t = deps.selectionTarget();
    return {
      supports: formatApi.supports,
      hasSelection: t != null && t.start < t.end,
      inTable: deps.inTable(),
      hasCellRange: deps.hasCellRange(),
      tableHeaderOn: deps.tableHeaderOn?.() ?? false,
      host: {
        undo: deps.canUndo(),
        redo: deps.canRedo(),
        clipboard: true,
        clipboardRead: clipboard.canPaste(),
        painter: true
      }
    };
  }
  const selfDispatch = createShortcutDispatcher({
    getEnv: env,
    getContext: () => ({
      api: formatApi,
      handle: deps.api,
      active: {
        ...deps.api.activeFormat(),
        painterArmed: painter.armed(),
        tableHeaderOn: deps.tableHeaderOn?.() ?? false
      },
      after: () => {
      },
      host: {
        insertBlock: (b) => {
          deps.api.insertBlock(b);
        },
        insertPageBreak: () => {
          deps.api.insertPageBreak();
        },
        undo: () => {
          deps.api.undo();
        },
        redo: () => {
          deps.api.redo();
        },
        cut: () => clipboard.cut(),
        copy: () => clipboard.copy(),
        paste: () => clipboard.paste(),
        formatPainter: () => deps.api.formatPainter(),
        canUndo: deps.canUndo,
        canRedo: deps.canRedo,
        canPaste: () => clipboard.canPaste()
      }
    })
  });
  const dispatch = (ev) => deps.onShortcut ? deps.onShortcut(ev) : selfDispatch(ev);
  return {
    formatApi,
    clipboard,
    painter,
    env,
    dispatch,
    keyHandler: createKeyHandler({
      getMode: deps.getMode,
      gestures: deps.gestures,
      setSuppress: deps.setSuppress,
      dispatch
    })
  };
}
function createDocHistory(opts = {}) {
  const ring = new SnapshotHistory({
    ...opts.limit !== void 0 ? { limit: opts.limit } : {},
    ...opts.coalesceWindowMs !== void 0 ? { coalesceWindowMs: opts.coalesceWindowMs } : {},
    ...opts.now !== void 0 ? { now: opts.now } : {}
  });
  return {
    push: (snapshot, coalesceKey) => ring.push(snapshot, coalesceKey),
    undo: (current) => ring.undo(current),
    redo: (current) => ring.redo(current),
    canUndo: () => ring.canUndo(),
    canRedo: () => ring.canRedo(),
    breakCoalesce: () => ring.breakCoalesce(),
    barrier: () => ring.barrier()
  };
}
function createControllerHistory(deps, opts = {}) {
  const history = createDocHistory(opts);
  const snapshot = () => ({
    handle: deps.getHandle(),
    overrides: new Map(deps.overrides),
    orderedLists: new Map(deps.orderedLists),
    sectionDecoration: deps.getDecoration(),
    caret: deps.caret()
  });
  const restore = (snap) => {
    deps.setHandle(snap.handle);
    deps.overrides.clear();
    for (const [k, v] of snap.overrides) deps.overrides.set(k, v);
    deps.orderedLists.clear();
    for (const [k, v] of snap.orderedLists) deps.orderedLists.set(k, v);
    deps.setDecoration(snap.sectionDecoration);
    deps.render();
    if (snap.caret) deps.restoreCaret(snap.caret);
    deps.onChange(snap.caret?.blockId ?? "");
  };
  return {
    bank: (coalesceKey = null) => history.push(snapshot(), coalesceKey),
    undo() {
      const snap = history.undo(snapshot());
      if (!snap) return false;
      restore(snap);
      return true;
    },
    redo() {
      const snap = history.redo(snapshot());
      if (!snap) return false;
      restore(snap);
      return true;
    },
    canUndo: () => history.canUndo(),
    canRedo: () => history.canRedo(),
    barrier: () => history.barrier()
  };
}
var SELECTED_CELL_CLASS = "ela-cell--selected";
function setTableCellText(table, row, col, text) {
  const hasHeader = table.header != null;
  const isHeaderRow = hasHeader && row === 0;
  const bodyRowIdx = hasHeader ? row - 1 : row;
  const srcRow = isHeaderRow ? table.header : table.rows[bodyRowIdx];
  if (!srcRow) return null;
  let acc = 0;
  let cellIdx = -1;
  for (let i = 0; i < srcRow.cells.length; i += 1) {
    const cell = srcRow.cells[i];
    const span2 = cell.colSpan && cell.colSpan > 0 ? cell.colSpan : 1;
    if (col < acc + span2) {
      cellIdx = i;
      break;
    }
    acc += span2;
  }
  if (cellIdx < 0) return null;
  const runs = [{ text, marks: [] }];
  const cells = srcRow.cells.map((c, i) => i === cellIdx ? { ...c, runs } : c);
  const nextRow = { ...srcRow, cells };
  if (isHeaderRow) return { ...table, header: nextRow };
  return { ...table, rows: table.rows.map((r, i) => i === bodyRowIdx ? nextRow : r) };
}
function resolveCellAt(table, row, col) {
  const hasHeader = table.header != null;
  const isHeaderRow = hasHeader && row === 0;
  const bodyRowIdx = hasHeader ? row - 1 : row;
  const srcRow = isHeaderRow ? table.header : table.rows[bodyRowIdx];
  if (!srcRow) return null;
  let acc = 0;
  for (let i = 0; i < srcRow.cells.length; i += 1) {
    const cell = srcRow.cells[i];
    const span2 = cell.colSpan && cell.colSpan > 0 ? cell.colSpan : 1;
    if (col < acc + span2) return { srcRow, cellIdx: i, cell, isHeaderRow, bodyRowIdx };
    acc += span2;
  }
  return null;
}
function setTableCellAlignH(table, row, col, alignH) {
  const hit = resolveCellAt(table, row, col);
  if (!hit) return null;
  const cells = hit.srcRow.cells.map((c, i) => {
    if (i !== hit.cellIdx) return c;
    if (alignH === void 0) {
      const { alignH: _drop, ...rest } = c;
      return rest;
    }
    return { ...c, alignH };
  });
  const nextRow = { ...hit.srcRow, cells };
  if (hit.isHeaderRow) return { ...table, header: nextRow };
  return { ...table, rows: table.rows.map((r, i) => i === hit.bodyRowIdx ? nextRow : r) };
}
function tableColCount(table) {
  if (table.columns && table.columns.length > 0) return table.columns.length;
  let max = 0;
  const rows = table.header ? [table.header, ...table.rows] : [...table.rows];
  for (const r of rows) {
    let acc = 0;
    for (const cell of r.cells) acc += cell.colSpan && cell.colSpan > 0 ? cell.colSpan : 1;
    if (acc > max) max = acc;
  }
  return max;
}
function foldWholeTable(module, table, perCell) {
  const cols = tableColCount(table);
  const hasHeader = table.header != null;
  const visualRows = (hasHeader ? 1 : 0) + table.rows.length;
  let cur = table;
  let touched = false;
  for (let row = 0; row < visualRows; row += 1) {
    const isHeaderRow = hasHeader && row === 0;
    const bodyRowIndex = isHeaderRow ? -1 : hasHeader ? row - 1 : row;
    for (let col = 0; col < cols; col += 1) {
      if (resolveCellAt(cur, row, col)?.cell.vMerge === "continue") continue;
      const next = perCell(module, cur, row, col, isHeaderRow, bodyRowIndex);
      if (next) {
        cur = next;
        touched = true;
      }
    }
  }
  return { table: cur, touched };
}
function runsText(runs) {
  return runs.map((r) => r.text).join("");
}
function listPlainText(list) {
  return list.items.map((it) => runsText(Array.isArray(it) ? it : it.runs)).join("\n");
}
function parseCell(coord) {
  const parts = coord.split(":");
  const rowPart = parts[0];
  const colPart = parts[1];
  if (rowPart === void 0 || colPart === void 0) return null;
  const row = Number.parseInt(rowPart, 10);
  const col = Number.parseInt(colPart, 10);
  if (!Number.isInteger(row) || !Number.isInteger(col)) return null;
  return { row, col };
}
function closestBlock(node) {
  const el = node?.nodeType === 1 ? node : node?.parentElement;
  return el?.closest("[data-block-id]") ?? null;
}
function stampRow(rowEl, rowKey) {
  let col = 0;
  for (const cell of Array.from(rowEl.children)) {
    cell.setAttribute("data-cell", `${rowKey}:${col}`);
    const span2 = Number.parseInt(cell.getAttribute("colspan") ?? "1", 10);
    col += Number.isInteger(span2) && span2 > 0 ? span2 : 1;
  }
}
function stampTable(tableEl) {
  const thead = tableEl.querySelector("thead");
  const headRow = thead?.querySelector("tr");
  if (headRow) stampRow(headRow, "0");
  const offset = headRow ? 1 : 0;
  const tbody = tableEl.querySelector("tbody");
  const bodyRows = tbody ? Array.from(tbody.querySelectorAll("tr")) : Array.from(tableEl.querySelectorAll("tr")).filter((tr) => !headRow || tr !== headRow);
  bodyRows.forEach((tr, r) => stampRow(tr, String(r + offset)));
}
function stampList(listEl) {
  Array.from(listEl.querySelectorAll("li")).forEach(
    (li, i) => li.setAttribute("data-item", String(i))
  );
}
function createPaletteEditor(deps) {
  const entries = [];
  let counter = 0;
  function indexOf(blockId) {
    return entries.findIndex((e) => e.id === blockId);
  }
  function get(blockId) {
    return entries.find((e) => e.id === blockId)?.block ?? null;
  }
  function tableFor(blockId) {
    const b = get(blockId);
    return b && b.kind === "table" ? b : null;
  }
  function listFor(blockId) {
    const b = get(blockId);
    return b && b.kind === "list" ? b : null;
  }
  function store(blockId, block) {
    const i = indexOf(blockId);
    if (i >= 0) entries[i] = { id: blockId, block, hydrated: entries[i]?.hydrated };
  }
  function selection() {
    const container = deps.getContainer();
    const sel = container?.ownerDocument.getSelection?.();
    if (!container || !sel || sel.rangeCount === 0) return null;
    if (!container.contains(sel.getRangeAt(0).startContainer)) return null;
    return {
      anchorNode: sel.anchorNode,
      focusNode: sel.focusNode,
      start: sel.getRangeAt(0).startContainer
    };
  }
  function cellTarget() {
    const s = selection();
    if (!s) return null;
    const block = closestBlock(s.start);
    const cellEl = (s.start?.nodeType === 1 ? s.start : s.start?.parentElement)?.closest("[data-cell]");
    const id = block?.getAttribute("data-block-id");
    const coord = cellEl?.getAttribute("data-cell");
    if (!id || !coord || !tableFor(id)) return null;
    const parsed = parseCell(coord);
    if (!parsed) return null;
    return { blockId: id, ...parsed };
  }
  function cellOf(node) {
    const el = node?.nodeType === 1 ? node : node?.parentElement;
    const coord = el?.closest("[data-cell]")?.getAttribute("data-cell");
    return coord ? parseCell(coord) : null;
  }
  function cellRange() {
    const s = selection();
    if (!s) return null;
    const block = closestBlock(s.start);
    const id = block?.getAttribute("data-block-id");
    if (!id || !tableFor(id)) return null;
    const a = cellOf(s.anchorNode);
    const f = cellOf(s.focusNode);
    if (!a || !f) return null;
    return {
      blockId: id,
      r0: Math.min(a.row, f.row),
      c0: Math.min(a.col, f.col),
      r1: Math.max(a.row, f.row),
      c1: Math.max(a.col, f.col)
    };
  }
  let rememberedRange = null;
  function resolveRange3() {
    return cellRange() ?? rememberedRange;
  }
  function spansMultiple(r) {
    return r.r1 > r.r0 || r.c1 > r.c0;
  }
  function paintCellRange() {
    const container = deps.getContainer();
    if (!container) return;
    for (const el of Array.from(container.querySelectorAll(`.${SELECTED_CELL_CLASS}`))) {
      el.classList.remove(SELECTED_CELL_CLASS);
    }
    const r = rememberedRange;
    if (!r || !spansMultiple(r)) return;
    const tableEl = container.querySelector(`[data-block-id="${r.blockId}"]`);
    if (!tableEl) return;
    for (const cellEl of Array.from(tableEl.querySelectorAll("[data-cell]"))) {
      const parsed = parseCell(cellEl.getAttribute("data-cell") ?? "");
      if (!parsed) continue;
      if (parsed.row >= r.r0 && parsed.row <= r.r1 && parsed.col >= r.c0 && parsed.col <= r.c1) {
        cellEl.classList.add(SELECTED_CELL_CLASS);
      }
    }
  }
  function syncCellRange() {
    if (!selection()) return;
    rememberedRange = cellRange();
    paintCellRange();
  }
  function cellPointOf(node, offset) {
    if (!node) return null;
    const el = node.nodeType === 1 ? node : node.parentElement;
    const cellEl = el?.closest("[data-cell]");
    const parsed = cellEl ? parseCell(cellEl.getAttribute("data-cell") ?? "") : null;
    if (!cellEl || !parsed) return null;
    return { ...parsed, byte: byteOffsetWithin(cellEl, node, offset) };
  }
  function captureCellSelection() {
    const container = deps.getContainer();
    const sel = container?.ownerDocument.getSelection?.();
    if (!container || !sel || sel.rangeCount === 0) return null;
    if (!container.contains(sel.getRangeAt(0).startContainer)) return null;
    const id = closestBlock(sel.getRangeAt(0).startContainer)?.getAttribute(
      "data-block-id"
    );
    if (!id || !tableFor(id)) return null;
    const anchor = cellPointOf(sel.anchorNode, sel.anchorOffset);
    const focus = cellPointOf(sel.focusNode, sel.focusOffset);
    if (!anchor || !focus) return null;
    return { blockId: id, anchor, focus };
  }
  function restoreCellSelection(snap) {
    const container = deps.getContainer();
    if (!container) return false;
    const cellFor = (p) => container.querySelector(
      `[data-block-id="${snap.blockId}"] [data-cell="${p.row}:${p.col}"]`
    );
    const a = cellFor(snap.anchor);
    const f = cellFor(snap.focus);
    if (!a || !f) return false;
    selectAcrossSurfaces(a, snap.anchor.byte, f, snap.focus.byte);
    return true;
  }
  function applyOverRange(mutate) {
    if (!tableGuard()) return false;
    const r = resolveRange3();
    const base = r && tableFor(r.blockId);
    if (!r || !base) return false;
    const snap = captureCellSelection();
    let table = base;
    let touched = false;
    for (let row = r.r0; row <= r.r1; row += 1) {
      for (let col = r.c0; col <= r.c1; col += 1) {
        if (resolveCellAt(table, row, col)?.cell.vMerge === "continue") continue;
        const next = mutate(table, row, col);
        if (next) {
          table = next;
          touched = true;
        }
      }
    }
    if (!touched) return false;
    store(r.blockId, table);
    deps.rerender();
    reseatAfterChannelWrite(r, snap);
    deps.onChange(r.blockId);
    return true;
  }
  function reseatAfterChannelWrite(r, snap) {
    if (snap && restoreCellSelection(snap)) ; else if (selection()) {
      focusCell(r.blockId, r.r0, r.c0);
    }
    rememberedRange = r;
    paintCellRange();
  }
  function commitWholeTableChannel(blockId, next, snap) {
    store(blockId, next);
    deps.rerender();
    if (snap) restoreCellSelection(snap);
    paintCellRange();
    deps.onChange(blockId);
    return true;
  }
  function tableBlockId() {
    const s = selection();
    if (!s) return null;
    const block = closestBlock(s.start);
    const id = block?.getAttribute("data-block-id");
    return id && tableFor(id) ? id : null;
  }
  function listTarget() {
    const s = selection();
    if (!s) return null;
    const el = s.start?.nodeType === 1 ? s.start : s.start?.parentElement;
    const listEl = el?.closest('[data-block-kind="list"][data-block-id]');
    const id = listEl?.getAttribute("data-block-id");
    if (!id || !listFor(id)) return null;
    const itemAttr = el?.closest("[data-item]")?.getAttribute("data-item");
    const parsed = itemAttr != null ? Number.parseInt(itemAttr, 10) : Number.NaN;
    return { blockId: id, itemIndex: Number.isInteger(parsed) ? parsed : null };
  }
  function focusCell(blockId, row, col) {
    const container = deps.getContainer();
    if (!container) return;
    const cell = container.querySelector(
      `[data-block-id="${blockId}"] [data-cell="${row}:${col}"]`
    );
    if (!cell) return;
    const doc = container.ownerDocument;
    const sel = doc.getSelection?.();
    if (!sel) return;
    const range = doc.createRange();
    range.selectNodeContents(cell);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }
  function commitTable(blockId, res, reseat) {
    if (!res.ok) return false;
    store(blockId, res.value);
    deps.rerender();
    if (reseat) focusCell(blockId, reseat.row, reseat.col);
    deps.onChange(blockId);
    return true;
  }
  function commitList(blockId, res) {
    if (!res.ok) return null;
    const mutated = res.value[0];
    if (!mutated || mutated.kind !== "list") return null;
    store(blockId, mutated);
    deps.rerender();
    deps.onChange(blockId);
    return textFragment(listPlainText(mutated), {
      kind: "list_item",
      baseVersionId: deps.baseVersionId()
    });
  }
  function tableGuard() {
    return deps.canEdit() && deps.getModule().supportsTableVerbs === true;
  }
  function listGuard() {
    return deps.canEdit() && deps.getModule().supportsListVerbs === true;
  }
  const NOT_A_LIST = { kind: "paragraph", runs: [] };
  const tableVerbs = {
    /** Select the caret's whole table — a DOM Range over the table element, the
     *  same mechanism `focusCell` uses one level down. */
    selectTable() {
      if (!deps.canEdit()) return false;
      const t = cellTarget();
      const container = deps.getContainer();
      if (!t || !container) return false;
      const el = container.querySelector(`[data-block-id="${t.blockId}"]`);
      if (!el) return false;
      const doc = container.ownerDocument;
      const sel = doc.getSelection?.();
      if (!sel) return false;
      const range = doc.createRange();
      range.selectNodeContents(el);
      sel.removeAllRanges();
      sel.addRange(range);
      return true;
    },
    /**
     * NOT IMPLEMENTED on this surface, and it says so rather than pretending.
     *
     * This editor holds a per-block DTO `store` keyed by block id and re-renders
     * from it; there is no seam that removes a block from the DOCUMENT, so a
     * fabricated `true` here would leave the table on screen and on disk. The
     * canvas surface routes the same verb through `DoczHandle.deleteBlock`.
     *
     * The ribbon does not offer the command here at all — `table.delete` gates on
     * the `deleteBlock` HOST capability, which this surface does not declare — so
     * this is the belt to that braces, not the user-visible behaviour.
     * TODO(dom-surface): wire a document-level block removal into `deps` if this
     * surface is ever remounted.
     */
    deleteTable() {
      return false;
    },
    insertRow(where) {
      if (!tableGuard()) return false;
      const t = cellTarget();
      const table = t && tableFor(t.blockId);
      if (!t || !table) return false;
      return commitTable(t.blockId, deps.getModule().tableInsertRow(table, t.row, where), {
        row: where === "below" ? t.row + 1 : t.row,
        col: t.col
      });
    },
    deleteRow() {
      if (!tableGuard()) return false;
      const t = cellTarget();
      const table = t && tableFor(t.blockId);
      if (!t || !table) return false;
      return commitTable(t.blockId, deps.getModule().tableDeleteRow(table, t.row));
    },
    insertColumn(where) {
      if (!tableGuard()) return false;
      const t = cellTarget();
      const table = t && tableFor(t.blockId);
      if (!t || !table) return false;
      return commitTable(t.blockId, deps.getModule().tableInsertColumn(table, t.col, where), {
        row: t.row,
        col: where === "right" ? t.col + 1 : t.col
      });
    },
    deleteColumn() {
      if (!tableGuard()) return false;
      const t = cellTarget();
      const table = t && tableFor(t.blockId);
      if (!t || !table) return false;
      return commitTable(t.blockId, deps.getModule().tableDeleteColumn(table, t.col));
    },
    mergeCells() {
      if (!tableGuard()) return false;
      const r = cellRange();
      const table = r && tableFor(r.blockId);
      if (!r || !table) return false;
      return commitTable(
        r.blockId,
        deps.getModule().tableMergeCells(table, r.r0, r.c0, r.r1, r.c1),
        { row: r.r0, col: r.c0 }
      );
    },
    splitCell(intoRows, intoCols) {
      if (!tableGuard()) return false;
      const t = cellTarget();
      const table = t && tableFor(t.blockId);
      if (!t || !table) return false;
      return commitTable(
        t.blockId,
        deps.getModule().tableSplitCell(table, t.row, t.col, intoRows, intoCols),
        { row: t.row, col: t.col }
      );
    },
    setCellShading(hex) {
      const module = deps.getModule();
      return applyOverRange((t, row, col) => {
        const res = module.tableSetCellShading(t, row, col, hex ?? void 0);
        return res.ok ? res.value : null;
      });
    },
    setCellVAlign(v) {
      const module = deps.getModule();
      return applyOverRange((t, row, col) => {
        const res = module.tableSetCellVAlign(t, row, col, v ?? void 0);
        return res.ok ? res.value : null;
      });
    },
    setCellBorders(b) {
      const module = deps.getModule();
      return applyOverRange((t, row, col) => {
        const res = module.tableSetCellBorders(t, row, col, b ?? void 0);
        return res.ok ? res.value : null;
      });
    },
    setCellAlignH(a) {
      return applyOverRange((t, row, col) => setTableCellAlignH(t, row, col, a ?? void 0));
    },
    toggleHeader(on) {
      if (!tableGuard()) return false;
      const id = tableBlockId();
      const table = id && tableFor(id);
      if (!id || !table) return false;
      return commitTable(id, deps.getModule().tableToggleHeader(table, on));
    },
    setColumnWidth(col, pt) {
      if (!tableGuard()) return false;
      const id = tableBlockId();
      const table = id && tableFor(id);
      if (!id || !table) return false;
      return commitTable(id, deps.getModule().tableSetColumnWidth(table, col, pt, "fixed"));
    },
    setRowHeadColumns(n) {
      if (!tableGuard()) return false;
      const id = tableBlockId();
      const table = id && tableFor(id);
      if (!id || !table) return false;
      return commitTable(id, deps.getModule().tableSetRowHeadColumns(table, n));
    },
    // ─── W2c table-style verbs — per-cell channel expansion (see table-styles.ts).
    applyTableStyle(preset) {
      if (!tableGuard()) return false;
      const id = tableBlockId();
      const table = id && tableFor(id);
      if (!id || !table) return false;
      const scheme = schemeForPreset(preset);
      if (!scheme) return false;
      const module = deps.getModule();
      const snap = captureCellSelection();
      const { table: next, touched } = foldWholeTable(
        module,
        table,
        (m, running, row, col, isHeaderRow, bodyRowIndex) => {
          const fill = shadingForCell(scheme, isHeaderRow, bodyRowIndex);
          const rs = m.tableSetCellShading(running, row, col, fill);
          if (!rs.ok) return null;
          const rb = m.tableSetCellBorders(rs.value, row, col, borderForCell(scheme, isHeaderRow));
          return rb.ok ? rb.value : null;
        }
      );
      if (!touched) return false;
      return commitWholeTableChannel(id, next, snap);
    },
    setHeaderStyle(style) {
      if (!tableGuard()) return false;
      const id = tableBlockId();
      let table = id && tableFor(id);
      if (!id || !table) return false;
      const module = deps.getModule();
      const shapeStable = table.header != null;
      const snap = shapeStable ? captureCellSelection() : null;
      if (table.header == null) {
        const th = module.tableToggleHeader(table, true);
        if (!th.ok) return false;
        table = th.value;
      }
      const fill = style?.fill == null ? void 0 : style.fill;
      const cols = tableColCount(table);
      let cur = table;
      let touched = false;
      for (let col = 0; col < cols; col += 1) {
        if (resolveCellAt(cur, 0, col)?.cell.vMerge === "continue") continue;
        const rs = module.tableSetCellShading(cur, 0, col, fill);
        if (rs.ok) {
          cur = rs.value;
          touched = true;
        }
      }
      if (!touched) return false;
      return commitWholeTableChannel(id, cur, snap);
    },
    setRowBanding(on, color) {
      if (!tableGuard()) return false;
      const id = tableBlockId();
      const table = id && tableFor(id);
      if (!id || !table) return false;
      const band = on ? color ?? DEFAULT_BAND_COLOR : void 0;
      const module = deps.getModule();
      const snap = captureCellSelection();
      const { table: next, touched } = foldWholeTable(
        module,
        table,
        (m, running, row, col, isHeaderRow, bodyRowIndex) => {
          if (isHeaderRow) return null;
          const fill = on ? bodyRowIndex % 2 === 0 ? band : void 0 : void 0;
          const rs = m.tableSetCellShading(running, row, col, fill);
          return rs.ok ? rs.value : null;
        }
      );
      if (!touched) return false;
      return commitWholeTableChannel(id, next, snap);
    }
  };
  function listBlockId() {
    return listTarget()?.blockId ?? null;
  }
  function makePaletteEl(container, id, block) {
    const module = deps.getModule();
    const res = module.renderBlockDto(block);
    if (!res.ok) return null;
    const tpl = container.ownerDocument.createElement("template");
    tpl.innerHTML = bodyInnerHtml(res.html);
    const el = tpl.content.firstElementChild;
    if (!el) return null;
    el.classList.add(DOCZ_BLOCK_CLASS);
    el.setAttribute("data-block-id", id);
    el.setAttribute("data-block-kind", block.kind);
    el.setAttribute("data-palette", "true");
    if (block.kind === "table") stampTable(el);
    else if (block.kind === "list") stampList(el);
    return el;
  }
  return {
    insert(block) {
      counter += 1;
      const id = `ela-palette-${counter}`;
      entries.push({ id, block });
      return id;
    },
    registerHydratedTable(blockId, table) {
      if (indexOf(blockId) >= 0) return;
      entries.push({ id: blockId, block: table, hydrated: true });
    },
    isHydrated(blockId) {
      return entries.find((e) => e.id === blockId)?.hydrated === true;
    },
    htmlFor(blockId) {
      const block = get(blockId);
      if (!block) return "";
      const res = deps.getModule().renderBlockDto(block);
      return res.ok ? bodyInnerHtml(res.html) : "";
    },
    paintHydratedInPlace(container) {
      for (const { id, block, hydrated } of entries) {
        if (!hydrated) continue;
        const existing = container.querySelector(`[data-block-id="${id}"]`);
        if (!existing) continue;
        const el = makePaletteEl(container, id, block);
        if (!el) continue;
        existing.replaceWith(el);
      }
      paintCellRange();
    },
    get,
    hasListTarget() {
      return listGuard() && listTarget() !== null;
    },
    listBlock() {
      if (!selection()) return null;
      const id = listBlockId();
      return id && listFor(id) || NOT_A_LIST;
    },
    hasTableTarget() {
      return tableBlockId() !== null;
    },
    tableSelection() {
      return resolveRange3();
    },
    hasCellRange() {
      const r = resolveRange3();
      return r !== null && spansMultiple(r);
    },
    hasTableHeader() {
      const id = tableBlockId();
      if (!id) return false;
      return tableFor(id)?.header != null;
    },
    syncCellRange,
    syncCellText(cellEl) {
      const block = closestBlock(cellEl);
      const id = block?.getAttribute("data-block-id");
      const table = id && tableFor(id);
      if (!id || !table) return;
      const coord = cellEl.getAttribute("data-cell");
      const parsed = coord ? parseCell(coord) : null;
      if (!parsed) return;
      const next = setTableCellText(table, parsed.row, parsed.col, cellEl.textContent ?? "");
      if (!next) return;
      store(id, next);
      deps.onChange(id);
    },
    toHtml() {
      const module = deps.getModule();
      const parts = [];
      for (const { block, hydrated } of entries) {
        if (hydrated) continue;
        const res = module.renderBlockDto(block);
        if (res.ok) parts.push(bodyInnerHtml(res.html));
      }
      return parts.join("\n");
    },
    paint(container) {
      for (const { id, block, hydrated } of entries) {
        if (hydrated) continue;
        const el = makePaletteEl(container, id, block);
        if (!el) continue;
        container.appendChild(el);
      }
      paintCellRange();
    },
    tableVerbs,
    setListStyle(style) {
      if (!listGuard()) return null;
      const id = listBlockId();
      const list = id && listFor(id);
      if (!id || !list) return null;
      return commitList(id, deps.getModule().setListStyle([list], 0, style));
    },
    setListItemLevel(delta) {
      if (!listGuard()) return null;
      const t = listTarget();
      const list = t && listFor(t.blockId);
      if (!t || !list || t.itemIndex == null) return null;
      return commitList(t.blockId, deps.getModule().setListItemLevel([list], 0, t.itemIndex, delta));
    },
    restartList(start) {
      if (!listGuard()) return null;
      const id = listBlockId();
      const list = id && listFor(id);
      if (!id || !list) return null;
      const res = start == null ? deps.getModule().setStartOverride([list], 0, void 0) : deps.getModule().restartList([list], 0, { start });
      return commitList(id, res);
    },
    toggleTaskList() {
      if (!listGuard()) return null;
      const t = listTarget();
      const list = t && listFor(t.blockId);
      if (!t || !list) return null;
      const itemIndex = t.itemIndex ?? 0;
      return commitList(t.blockId, deps.getModule().toggleListItemChecked([list], 0, itemIndex));
    },
    setChecklist(on) {
      if (!listGuard()) return null;
      const id = listBlockId();
      const list = id && listFor(id);
      if (!id || !list) return null;
      const module = deps.getModule();
      if (!module.supportsChecklistOff) {
        if (on) return null;
        return commitList(id, module.setListStyle([list], 0, list.style));
      }
      return commitList(id, module.setListChecklist([list], 0, on));
    },
    setBulletGlyph(glyph) {
      if (!listGuard()) return null;
      const id = listBlockId();
      const list = id && listFor(id);
      if (!id || !list) return null;
      return commitList(id, deps.getModule().setListBulletChar([list], 0, glyph));
    }
  };
}

// src/proposal.ts
var PROPOSAL_OVERLAY_CLASS = "ela-proposal";
function proposalTestId(proposalId) {
  return `proposal-${proposalId}`;
}
function blockEl(container, blockId) {
  return container.querySelector(`[data-block-id="${cssEscape(blockId)}"]`);
}
function showProposalOverlay(container, proposal) {
  const block = blockEl(container, proposal.blockId);
  if (!block) return null;
  clearProposalOverlay(container, proposal.blockId);
  block.setAttribute("data-proposal", "agent");
  block.setAttribute("data-proposal-id", proposal.proposalId);
  block.toggleAttribute("data-readonly", true);
  const overlay = container.ownerDocument.createElement("div");
  overlay.className = PROPOSAL_OVERLAY_CLASS;
  overlay.setAttribute("data-proposal", "agent");
  overlay.setAttribute("data-proposal-id", proposal.proposalId);
  overlay.setAttribute("data-block-id", proposal.blockId);
  overlay.setAttribute("data-testid", proposalTestId(proposal.proposalId));
  overlay.textContent = proposal.proposedText;
  block.insertAdjacentElement("afterend", overlay);
  return overlay;
}
function clearProposalOverlay(container, blockId) {
  const block = blockEl(container, blockId);
  if (block) {
    block.removeAttribute("data-proposal");
    block.removeAttribute("data-proposal-id");
    block.removeAttribute("data-readonly");
  }
  for (const el of container.querySelectorAll(
    `.${PROPOSAL_OVERLAY_CLASS}[data-block-id="${cssEscape(blockId)}"]`
  )) {
    el.remove();
  }
}
function cssEscape(value) {
  const g = globalThis;
  if (typeof g.CSS?.escape === "function") return g.CSS.escape(value);
  return value.replace(/["\\]/g, "\\$&");
}

// src/raw-html-table.ts
function textRuns(text) {
  return [{ text, marks: [] }];
}
function span(el, attr) {
  const raw = el.getAttribute(attr);
  const n = raw == null ? 1 : Number.parseInt(raw, 10);
  return Number.isInteger(n) && n > 0 ? n : 1;
}
function htmlContainsTable(html, doc) {
  if (!/\<table[\s>]/i.test(html)) return false;
  const tpl = doc.createElement("template");
  tpl.innerHTML = html;
  return tpl.content.querySelector("table") != null;
}
function placeCells(rows) {
  const occupied = [];
  const placed = [];
  let width = 0;
  const reserve = (r, c) => {
    while (occupied.length <= r) occupied.push([]);
    occupied[r][c] = true;
  };
  const isFree = (r, c) => !(occupied[r]?.[c] === true);
  rows.forEach((tr, r) => {
    let c = 0;
    for (const cellEl of Array.from(tr.children)) {
      const tag = cellEl.tagName.toLowerCase();
      if (tag !== "td" && tag !== "th") continue;
      while (!isFree(r, c)) c += 1;
      const colSpan = span(cellEl, "colspan");
      const rowSpan = span(cellEl, "rowspan");
      placed.push({
        r0: r,
        c0: c,
        rowSpan,
        colSpan,
        runs: textRuns(cellEl.textContent ?? ""),
        isTh: tag === "th"
      });
      for (let dr = 0; dr < rowSpan; dr += 1) {
        for (let dc = 0; dc < colSpan; dc += 1) reserve(r + dr, c + dc);
      }
      c += colSpan;
      if (c > width) width = c;
    }
  });
  return { placed, width };
}
function buildRow(r, width, placed) {
  const cells = [];
  let c = 0;
  while (c < width) {
    const origin = placed.find((p) => p.r0 === r && p.c0 === c);
    if (origin) {
      const cell = {
        runs: origin.runs,
        ...origin.colSpan > 1 ? { colSpan: origin.colSpan } : {},
        ...origin.rowSpan > 1 ? { vMerge: "restart" } : {}
      };
      cells.push(cell);
      c += origin.colSpan;
      continue;
    }
    const cover = placed.find(
      (p) => p.r0 < r && p.r0 + p.rowSpan > r && p.c0 <= c && p.c0 + p.colSpan > c
    );
    if (cover) {
      if (cover.c0 === c) {
        cells.push({
          runs: textRuns(""),
          ...cover.colSpan > 1 ? { colSpan: cover.colSpan } : {},
          vMerge: "continue"
        });
        c += cover.colSpan;
        continue;
      }
      c += 1;
      continue;
    }
    cells.push({ runs: textRuns("") });
    c += 1;
  }
  return { cells };
}
function explodeRawHtmlTable(html, doc) {
  const tpl = doc.createElement("template");
  tpl.innerHTML = html;
  const table = tpl.content.querySelector("table");
  if (!table) return null;
  const theadRows = Array.from(table.querySelectorAll("thead > tr"));
  const bodyRowEls = Array.from(table.querySelectorAll("tbody > tr"));
  const looseRows = Array.from(table.children).filter((el) => el.tagName.toLowerCase() === "tr").filter((tr) => tr.parentElement === table);
  let headerRowEl = theadRows[0] ?? null;
  const orderedBody = [];
  if (theadRows.length > 0) {
    orderedBody.push(...theadRows.slice(1), ...bodyRowEls, ...looseRows);
  } else {
    const candidates = bodyRowEls.length > 0 ? bodyRowEls : looseRows;
    const first = candidates[0];
    const firstIsAllTh = first != null && Array.from(first.children).some((el) => el.tagName.toLowerCase() === "th") && Array.from(first.children).filter((el) => ["td", "th"].includes(el.tagName.toLowerCase())).every((el) => el.tagName.toLowerCase() === "th");
    if (firstIsAllTh) {
      headerRowEl = first;
      orderedBody.push(...candidates.slice(1));
    } else {
      orderedBody.push(...candidates);
    }
  }
  const allRowEls = headerRowEl ? [headerRowEl, ...orderedBody] : orderedBody;
  if (allRowEls.length === 0) return null;
  const { placed, width } = placeCells(allRowEls);
  if (width === 0) return null;
  const visualRows = allRowEls.map((_, r) => buildRow(r, width, placed));
  const header = headerRowEl ? visualRows[0] : void 0;
  const bodyRows = headerRowEl ? visualRows.slice(1) : visualRows;
  const columns = Array.from({ length: width }, () => ({}));
  return { kind: "table", columns, ...header ? { header } : {}, rows: bodyRows };
}

// src/rebase.ts
function toWasmSelector(sel) {
  return {
    kind: "text_range",
    exact: sel.exactQuote,
    prefix: sel.prefix,
    suffix: sel.suffix,
    start: sel.start,
    end: sel.end,
    quoteHash: sel.quoteHash ?? "",
    baseVersionId: sel.baseVersionId ?? ""
  };
}
function rebaseStateToHitState(state) {
  return state;
}
function rebaseOutcomeToHit(outcome, commentId, spans) {
  return { state: rebaseStateToHitState(outcome.state), commentId, spans };
}
function listStateOfBlock(block) {
  return resolveListState([{ blockOrd: 0, start: 0, end: 0 }], () => block);
}
var STYLE_PROP_NAMES = Object.keys(STYLE_PROPS);
function mixedStyleProps(slices, fragmentAt) {
  const out = /* @__PURE__ */ new Set();
  for (const name of STYLE_PROP_NAMES) {
    const prop = STYLE_PROPS[name];
    if (resolveStyle(slices, fragmentAt, prop) === MIXED) out.add(name);
  }
  return out;
}
function mixedStylePropsInFragment(fragment, start, end) {
  return mixedStyleProps([{ blockOrd: 0, start, end }], () => fragment);
}

// src/controller.ts
var UTF85 = new TextEncoder();
function cssEscape2(value) {
  const g = globalThis;
  if (typeof g.CSS?.escape === "function") return g.CSS.escape(value);
  return value.replace(/["\\\]]/g, "\\$&");
}
function toDomState(state) {
  if (state === "exact") return "exact";
  if (state === "orphaned") return "orphan";
  return "moved";
}
function fragmentMarksInRange(frag, lo, hi) {
  return marksInRange(frag, lo, hi);
}
function activeFormatOf(frag, lo, hi) {
  const marks = fragmentMarksInRange(frag, lo, hi);
  const link = marks.find((m) => m.type === "link");
  const color = marks.find((m) => m.type === "color");
  const highlight = marks.find((m) => m.type === "highlight");
  const fontFamily = marks.find((m) => m.type === "font_family");
  const fontSize = marks.find((m) => m.type === "font_size");
  const letterSpacing = marks.find((m) => m.type === "letter_spacing");
  const underline = marks.find((m) => m.type === "underline");
  const textEffects = marks.find((m) => m.type === "text_effects");
  const charBorder = marks.find((m) => m.type === "char_border");
  const emphasis = marks.find((m) => m.type === "emphasis");
  return {
    marks: new Set(marks.map((m) => m.type)),
    link: link && link.type === "link" ? link.href : null,
    color: color && color.type === "color" ? color.color : null,
    highlight: highlight && highlight.type === "highlight" ? highlight.color : null,
    fontFamily: fontFamily && fontFamily.type === "font_family" ? fontFamily.font : null,
    fontSize: fontSize && fontSize.type === "font_size" ? fontSize.sizePt : null,
    underlineStyle: underline && underline.type === "underline" ? underline.style ?? "single" : null,
    letterSpacing: letterSpacing && letterSpacing.type === "letter_spacing" ? letterSpacing.spacingPt : null,
    textEffects: textEffects && textEffects.type === "text_effects" ? textEffects.effects : null,
    charBorder: charBorder && charBorder.type === "char_border" ? charBorder.border : null,
    emphasis: emphasis && emphasis.type === "emphasis" ? emphasis.emphasis : null,
    kind: frag.kind ?? "paragraph",
    level: frag.level ?? null,
    para: fragmentParaProps(frag),
    // …and the THIRD state every valued field above throws away. The fields are
    // `T | null` and `null` has to mean both "unset" and "several different
    // values"; a readout that cannot tell them apart paints one of three colours
    // as if it were THE colour. web-docz's `resolveStyle` distinguishes them, so
    // ask it — once per property, over this same range, with no second traversal.
    mixed: mixedStylePropsInFragment(frag, lo, hi)
  };
}
function createEditorController(container, initial) {
  let handle = initial.handle;
  let opts = initial;
  let sectionDecoration = emptySectionDecoration();
  const proposals = /* @__PURE__ */ new Map();
  const overrides = /* @__PURE__ */ new Map();
  const orderedLists = /* @__PURE__ */ new Map();
  const history = createControllerHistory({
    getHandle: () => handle,
    setHandle: (h) => {
      handle = h;
    },
    overrides,
    orderedLists,
    getDecoration: () => sectionDecoration,
    setDecoration: (d) => {
      sectionDecoration = d;
    },
    caret: () => caretTarget(),
    render: () => render(),
    restoreCaret: (c) => {
      const el = container.querySelector(`[data-block-id="${cssEscape2(c.blockId)}"]`);
      if (!el) return;
      el.focus?.();
      restoreSelection(el, c.start, c.end);
      if (el.ownerDocument.activeElement !== el) {
        queueMicrotask(() => {
          if (!el.isConnected || el.ownerDocument.activeElement === el) return;
          el.focus?.();
          restoreSelection(el, c.start, c.end);
        });
      }
    },
    onChange: (blockId) => opts.onChange?.({ blockId, baseVersionId: handle.baseVersionId })
  });
  let suppressNextBeforeInput = null;
  let onKeyDown = () => {
  };
  const paletteEditor = createPaletteEditor({
    getModule: () => docz(),
    getContainer: () => container,
    rerender: () => render(),
    canEdit: () => opts.mode === "edit",
    onChange: (blockId) => opts.onChange?.({ blockId, baseVersionId: handle.baseVersionId }),
    baseVersionId: () => handle.baseVersionId
  });
  function readOnlySet() {
    const s = new Set(opts.readOnlyBlocks ?? []);
    for (const p of proposals.values()) s.add(p.blockId);
    return s;
  }
  function isHtmlBlock(blockId) {
    return handle.blocks.find((b) => b.id === blockId)?.kind === "html";
  }
  function isReadOnlyBlock(blockId) {
    return opts.mode === "read" || isHtmlBlock(blockId) || readOnlySet().has(blockId);
  }
  function applyModeAndReadOnly() {
    container.setAttribute("data-mode", opts.mode);
    container.classList.add("docz-canvas");
    const readOnly = readOnlySet();
    for (const el of container.querySelectorAll("[data-block-id]")) {
      const id = el.getAttribute("data-block-id");
      const opaque = el.getAttribute("data-block-kind") === "html";
      const palette = el.hasAttribute("data-palette");
      const paletteTable = palette && el.getAttribute("data-block-kind") === "table";
      const ro = opts.mode === "read" || opaque || id != null && readOnly.has(id);
      el.toggleAttribute("data-readonly", ro);
      if (paletteTable && opts.mode === "edit" && !ro) {
        el.setAttribute("data-editable", "true");
        el.removeAttribute("contenteditable");
        for (const cell of el.querySelectorAll("td, th")) {
          cell.setAttribute("contenteditable", "true");
        }
      } else {
        const editable = opts.mode === "edit" && !ro && !palette;
        el.setAttribute("data-editable", String(editable));
        if (editable) {
          el.setAttribute("contenteditable", "true");
          ensureTrailingBreakSentinel(el);
        } else {
          el.removeAttribute("contenteditable");
        }
      }
    }
  }
  function ensureTrailingBreakSentinel(el) {
    const last = el.lastChild;
    const hasSentinel = last != null && last.nodeType === 1 && last.tagName === "BR" && last.hasAttribute("data-caret-sentinel");
    const endsWithNewline = (el.textContent ?? "").endsWith("\n");
    if (endsWithNewline && !hasSentinel) {
      const br = el.ownerDocument.createElement("br");
      br.setAttribute("data-caret-sentinel", "");
      el.appendChild(br);
    } else if (!endsWithNewline && hasSentinel) {
      last.remove();
    }
  }
  function restoreCaret(blockId, byteOffset) {
    const blockEl2 = container.querySelector(`[data-block-id="${cssEscape2(blockId)}"]`);
    if (!blockEl2) return;
    blockEl2.focus?.();
    placeCaretAtByte(blockEl2, byteOffset);
  }
  function reapplyProposals() {
    for (const p of proposals.values()) showProposalOverlay(container, p);
  }
  const structural = createBlockStructural({
    getContainer: () => container,
    getHandle: () => handle,
    setHandle: (next) => {
      history.bank(null);
      handle = next;
    },
    dropOverride: (blockId) => {
      overrides.delete(blockId);
      orderedLists.delete(blockId);
    },
    rerender: () => render(),
    restoreCaret: (blockId, byteOffset) => restoreCaret(blockId, byteOffset),
    isReadOnlyBlock: (blockId) => isReadOnlyBlock(blockId),
    isHtmlBlock: (blockId) => isHtmlBlock(blockId),
    canEdit: () => opts.mode === "edit",
    onChange: (patch) => opts.onChange?.(patch),
    applyTextEdit: (edit) => api.applyTextEdit(edit)
  });
  function docz() {
    return handle.module;
  }
  function blockFragment(blockId) {
    const existing = overrides.get(blockId);
    if (existing) return existing;
    const block = handle.blocks.find((b) => b.id === blockId);
    if (!block) return null;
    let frag = textFragment(block.text, {
      kind: block.kind,
      level: block.level,
      baseVersionId: handle.baseVersionId
    });
    for (const c of block.comments ?? []) {
      const marked = applyCommentMark(docz(), frag, {
        commentId: c.commentId,
        start: c.start,
        end: c.end
      });
      if (marked.ok) frag = marked.fragment;
    }
    return frag;
  }
  function orderedStartFor(blockId, style) {
    let run = 0;
    for (const b of handle.blocks) {
      const bid = b.id;
      if (orderedLists.get(bid) === style) {
        run += 1;
        if (bid === blockId) return run;
      } else {
        if (bid === blockId) return 1;
        run = 0;
      }
    }
    return run || 1;
  }
  function orderedListHtml(blockId, frag, style) {
    const start = orderedStartFor(blockId, style);
    const block = {
      kind: "list",
      style,
      items: [frag.runs],
      ...start > 1 ? { startOverride: start } : {}
    };
    const res = docz().renderBlockDto(block);
    return res.ok ? bodyInnerHtml(res.html) : "";
  }
  function repaintOverrides() {
    if (overrides.size === 0) return;
    for (const [blockId, frag] of overrides) {
      const el = container.querySelector(`[data-block-id="${cssEscape2(blockId)}"]`);
      if (!el) continue;
      const orderedStyle = orderedLists.get(blockId);
      const html = orderedStyle ? orderedListHtml(blockId, frag, orderedStyle) : renderFragmentToHtml(docz(), frag);
      const tpl = container.ownerDocument.createElement("template");
      tpl.innerHTML = html;
      const next = tpl.content.firstElementChild;
      if (!next) continue;
      next.classList.add(DOCZ_BLOCK_CLASS);
      next.setAttribute("data-block-id", blockId);
      if (frag.kind) next.setAttribute("data-block-kind", frag.kind);
      if (frag.kind === "heading" && frag.level) next.setAttribute("data-level", String(frag.level));
      el.replaceWith(next);
    }
  }
  function hydrateLoadedTables() {
    if (!supportsTables(docz()) || !docz().supportsBlockPalette) return;
    const doc = container.ownerDocument;
    for (const block of handle.blocks) {
      if (block.kind !== "html" || !block.id) continue;
      const id = block.id;
      if (paletteEditor.isHydrated(id)) continue;
      if (!htmlContainsTable(block.text, doc)) continue;
      const table = explodeRawHtmlTable(block.text, doc);
      if (table) paletteEditor.registerHydratedTable(id, table);
    }
    paletteEditor.paintHydratedInPlace(container);
  }
  function render() {
    handle.renderInto(container);
    repaintOverrides();
    hydrateLoadedTables();
    paletteEditor.paint(container);
    applyModeAndReadOnly();
    opts.renderMarks?.({
      root: container,
      commentSpans: (id) => handle.commentSpans(id),
      allCommentIds: () => allCommentIds(container)
    });
    reapplyProposals();
  }
  let rememberedSelection = null;
  function liveSelectionRange() {
    const sel = container.ownerDocument.getSelection?.();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!container.contains(range.startContainer)) return null;
    return range;
  }
  function liveSelectionTarget() {
    const range = liveSelectionRange();
    if (!range) return null;
    const selector = buildSelector(range, { baseVersionId: handle.baseVersionId });
    if (!selector) return null;
    return { blockId: selector.blockId, start: selector.start, end: selector.end };
  }
  function selectionTarget() {
    return liveSelectionTarget() ?? rememberedSelection;
  }
  function caretTarget() {
    const sel = container.ownerDocument.getSelection?.();
    if (!sel || sel.rangeCount === 0) return selectionTarget();
    const range = sel.getRangeAt(0);
    if (!container.contains(range.startContainer)) return selectionTarget();
    const blockRoot = findBlockRoot(range.startContainer);
    const blockId = blockRoot?.getAttribute("data-block-id");
    if (!blockRoot || !blockId) return selectionTarget();
    const bytes = selectionByteRange(blockRoot);
    if (!bytes) return selectionTarget();
    return { blockId, start: bytes.start, end: bytes.end };
  }
  function onSelectionChange() {
    const t = liveSelectionTarget();
    if (t) rememberedSelection = t;
    paletteEditor.syncCellRange();
  }
  container.ownerDocument.addEventListener("selectionchange", onSelectionChange);
  function paletteCellOf(node) {
    const el = node instanceof Element ? node : null;
    return el?.closest('[data-palette="true"] td, [data-palette="true"] th') ?? null;
  }
  function onCellInput(ev) {
    if (opts.mode !== "edit") return;
    const cell = paletteCellOf(ev.target);
    if (cell) paletteEditor.syncCellText(cell);
  }
  container.addEventListener("input", onCellInput);
  function commitFormat(blockId, frag, range) {
    history.bank(null);
    overrides.set(blockId, frag);
    render();
    const el = container.querySelector(`[data-block-id="${cssEscape2(blockId)}"]`);
    if (el) {
      el.focus?.();
      restoreSelection(el, range.start, range.end);
    }
    const patch = { blockId, baseVersionId: handle.baseVersionId };
    opts.onChange?.(patch);
  }
  function applyBlockKind(kind, level) {
    const target = selectionTarget();
    if (!target || isReadOnlyBlock(target.blockId)) return null;
    const frag = blockFragment(target.blockId);
    if (!frag) return null;
    if (kind !== "list_item") orderedLists.delete(target.blockId);
    const next = setFragmentKind(frag, kind, level);
    commitFormat(target.blockId, next, target);
    return next;
  }
  function ensureListItem() {
    const target = selectionTarget();
    if (!target || isReadOnlyBlock(target.blockId)) return null;
    const frag = blockFragment(target.blockId);
    if (!frag) return null;
    if (frag.kind === "list_item") return frag;
    return applyBlockKind("list_item");
  }
  function toggleListItem() {
    const target = selectionTarget();
    if (!target || isReadOnlyBlock(target.blockId)) return null;
    const frag = blockFragment(target.blockId);
    if (!frag) return null;
    return applyBlockKind(frag.kind === "list_item" ? "paragraph" : "list_item");
  }
  function restoreSelection(el, startByte, endByte) {
    if (startByte === endByte) {
      placeCaretAtByte(el, startByte);
      return;
    }
    placeCaretAtByte(el, startByte);
    const sel = el.ownerDocument.getSelection?.();
    if (!sel || sel.rangeCount === 0) return;
    const anchorNode = sel.anchorNode;
    const anchorOffset = sel.anchorOffset;
    placeCaretAtByte(el, endByte);
    if (sel.rangeCount === 0 || !anchorNode) return;
    try {
      sel.extend(sel.anchorNode, sel.anchorOffset);
      const r = el.ownerDocument.createRange();
      r.setStart(anchorNode, anchorOffset);
      r.setEnd(sel.focusNode, sel.focusOffset);
      sel.removeAllRanges();
      sel.addRange(r);
    } catch {
    }
  }
  function onBeforeInput(ev) {
    if (opts.mode !== "edit") return;
    if (paletteCellOf(ev.target)) return;
    ev.preventDefault();
    const ie = ev;
    if (suppressNextBeforeInput !== null) {
      const matched = suppressNextBeforeInput === ie.inputType;
      suppressNextBeforeInput = null;
      if (matched) return;
    }
    if (ie.inputType === "deleteContentBackward" && structural.caretAtMergeableBlockStart()) {
      structural.mergeBlockBackward();
      return;
    }
    const edit = textEditFromBeforeInput(ie, container);
    if (!edit) return;
    if (ie.inputType === "insertParagraph") {
      structural.splitBlockAt(edit.blockId, edit.at, edit.deleteBytes);
      return;
    }
    api.applyTextEdit(edit);
  }
  container.addEventListener("beforeinput", onBeforeInput);
  const api = {
    render,
    setHandle(next) {
      handle = next;
      render();
    },
    setOptions(patch) {
      opts = { ...opts, ...patch };
      if (patch.handle) handle = patch.handle;
      render();
    },
    destroy() {
      container.removeEventListener("beforeinput", onBeforeInput);
      container.removeEventListener("keydown", onKeyDown);
      container.removeEventListener("input", onCellInput);
      container.ownerDocument.removeEventListener("selectionchange", onSelectionChange);
      clipboard.destroy();
    },
    get root() {
      return container;
    },
    // Omitting `range` means "this surface's own live selection" — the
    // surface-agnostic form (see `ElaborateEditorHandle.buildSelector`). On the
    // DOM surface that is the live in-canvas `Range`; a caller that already
    // holds a Range still has it honoured verbatim.
    buildSelector(range) {
      const r = range ?? liveSelectionRange();
      if (!r) return null;
      return buildSelector(r, { baseVersionId: handle.baseVersionId });
    },
    // The DOM surface's selection rect: the live in-canvas Range's client box.
    // `null` when the selection is absent or outside the canvas — the host then
    // places its own UI rather than parking a popover at the origin.
    selectionViewportRect() {
      const r = liveSelectionRange();
      if (!r || r.collapsed) return null;
      return r.getBoundingClientRect();
    },
    placeCommentMark(commentId, range) {
      if (!commentId) {
        warn({
          fn: "placeCommentMark",
          code: "WEB_ELABORATE_EDITOR_INVALID_INPUT",
          message: `${LIB}: placeCommentMark requires a non-empty commentId`
        });
        return null;
      }
      const live = range ?? liveSelectionRange();
      const selector = live && buildSelector(live, { baseVersionId: handle.baseVersionId });
      if (!selector) {
        warn({
          fn: "placeCommentMark",
          code: "WEB_ELABORATE_EDITOR_NO_BLOCK",
          message: `${LIB}: placeCommentMark \u2014 selection is not inside a rendered block`
        });
        return null;
      }
      if (isHtmlBlock(selector.blockId)) {
        warn({
          fn: "placeCommentMark",
          code: "WEB_ELABORATE_EDITOR_NO_BLOCK",
          message: `${LIB}: placeCommentMark \u2014 cannot anchor a comment inside an opaque html block`
        });
        return null;
      }
      history.barrier();
      handle = handle.withBlockComment(selector.blockId, {
        commentId,
        start: selector.start,
        end: selector.end
      });
      render();
      const patch = {
        blockId: selector.blockId,
        baseVersionId: handle.baseVersionId,
        addComment: { commentId, selector }
      };
      opts.onChange?.(patch);
      return patch;
    },
    resolveAnchor(selector, commentId, outcome) {
      if (outcome) {
        return rebaseOutcomeToHit(outcome, commentId, handle.commentSpans(commentId));
      }
      const spans = handle.commentSpans(commentId);
      if (spans.length === 0) return { state: "orphaned", commentId, spans: [] };
      const rendered = spans.map((s) => s.textContent ?? "").join("");
      const state = rendered === selector.exactQuote ? "exact" : "shifted";
      return { state, commentId, spans };
    },
    rebaseAnchor(_oldText, newText, selector) {
      const r = handle.verbs.anchorRebase({
        selector: toWasmSelector(selector),
        newText
      });
      return r.ok ? r.value : null;
    },
    applyTextEdit(edit) {
      const block = handle.blocks.find((b) => b.id === edit.blockId);
      if (!block) return null;
      if (isReadOnlyBlock(edit.blockId)) return null;
      const baseVersionId = handle.baseVersionId;
      history.bank(
        edit.deleteBytes === 0 && isCoalescibleText(edit.insert) ? `insertText:${edit.blockId}` : null
      );
      const override = overrides.get(edit.blockId);
      if (override) {
        const edited = editFragmentText(docz(), override, {
          start: edit.at,
          end: edit.at + edit.deleteBytes,
          replacement: edit.insert
        });
        if (edited.ok) {
          overrides.set(edit.blockId, edited.fragment);
          handle = handle.withBlockText(edit.blockId, fragmentText(edited.fragment));
        } else {
          overrides.delete(edit.blockId);
          handle = handle.editBlockText(edit.blockId, {
            at: edit.at,
            deleteBytes: edit.deleteBytes,
            insert: edit.insert
          });
        }
      } else {
        handle = handle.editBlockText(edit.blockId, {
          at: edit.at,
          deleteBytes: edit.deleteBytes,
          insert: edit.insert
        });
      }
      render();
      restoreCaret(edit.blockId, edit.at + UTF85.encode(edit.insert).length);
      const patch = {
        blockId: edit.blockId,
        baseVersionId,
        textEdit: { at: edit.at, deleteBytes: edit.deleteBytes, insert: edit.insert }
      };
      opts.onChange?.(patch);
      return patch;
    },
    toggleMark(type) {
      const target = selectionTarget();
      if (!target || target.start >= target.end) {
        return { fragment: null, applied: false, toggledOff: false };
      }
      if (isReadOnlyBlock(target.blockId)) {
        return { fragment: null, applied: false, toggledOff: false };
      }
      const frag = blockFragment(target.blockId);
      if (!frag) return { fragment: null, applied: false, toggledOff: false };
      const wasActive = fragmentMarksInRange(frag, target.start, target.end).some(
        (m) => m.type === type
      );
      if (wasActive && !docz().supportsMarkToggle) {
        return { fragment: null, applied: false, toggledOff: false, pending: "toggle-off" };
      }
      const res = toggleStyleMark(docz(), frag, { type }, {
        start: target.start,
        end: target.end
      });
      if (!res.ok) return { fragment: null, applied: false, toggledOff: false };
      commitFormat(target.blockId, res.fragment, target);
      return { fragment: res.fragment, applied: !wasActive, toggledOff: wasActive };
    },
    setMark(mark) {
      const target = selectionTarget();
      if (!target || target.start >= target.end || isReadOnlyBlock(target.blockId)) return null;
      const frag = blockFragment(target.blockId);
      if (!frag) return null;
      const res = applyStyleMark(docz(), frag, mark, {
        start: target.start,
        end: target.end
      });
      if (!res.ok) return null;
      commitFormat(target.blockId, res.fragment, target);
      return res.fragment;
    },
    clearFormatting() {
      const target = selectionTarget();
      if (!target || target.start >= target.end || isReadOnlyBlock(target.blockId)) return null;
      const frag = blockFragment(target.blockId);
      if (!frag) return null;
      const next = clearMarksOp(docz(), frag, { start: target.start, end: target.end });
      if (!next) return null;
      commitFormat(target.blockId, next, target);
      return next;
    },
    changeCase(mode) {
      const target = selectionTarget();
      if (!target || target.start >= target.end || isReadOnlyBlock(target.blockId)) return null;
      const frag = blockFragment(target.blockId);
      if (!frag) return null;
      const next = changeCaseOp(docz(), frag, { start: target.start, end: target.end }, mode);
      if (!next) return null;
      commitFormat(target.blockId, next, target);
      return next;
    },
    setBlockKind(kind, level) {
      return applyBlockKind(kind, level);
    },
    setParaProps(patch) {
      const target = selectionTarget();
      if (!target || isReadOnlyBlock(target.blockId)) return null;
      const frag = blockFragment(target.blockId);
      if (!frag) return null;
      const next = setParaPropsOp(docz(), frag, patch);
      if (!next) return null;
      commitFormat(target.blockId, next, target);
      return next;
    },
    setPageDecoration(patch) {
      history.bank(null);
      sectionDecoration = mergeSectionDecoration(sectionDecoration, patch);
      render();
      opts.onChange?.({ blockId: "", baseVersionId: handle.baseVersionId });
      return sectionDecoration;
    },
    pageDecoration() {
      return sectionDecoration;
    },
    insertBlock(block) {
      if (opts.mode !== "edit") return false;
      if (block.kind === "table" && !supportsTables(docz())) return false;
      if (block.kind === "list" && !supportsLists(docz())) return false;
      if (!docz().supportsBlockPalette) return false;
      history.bank(null);
      const id = paletteEditor.insert(block);
      render();
      opts.onChange?.({ blockId: id, baseVersionId: handle.baseVersionId });
      return true;
    },
    insertPageBreak() {
      return structural.insertPageBreak();
    },
    table: paletteEditor.tableVerbs,
    /**
     * True when this surface holds a RANGED (non-collapsed) selection — the
     * authority behind `ToolbarEnv.hasSelection` (see the doc on
     * `ElaborateEditorHandle.hasSelection`).
     *
     * Answered from {@link selectionTarget} — the LIVE in-canvas range, falling
     * back to the REMEMBERED one — not from `window.getSelection()` directly.
     * That matters twice: a host must never read the DOM (the canvas sibling has
     * no DOM selection at all), and clicking the Link button moves focus into
     * the URL popover, which collapses the live DOM selection. Gating on the raw
     * DOM would therefore disable Link the instant it was opened — the cached
     * range is the same one every formatting verb applies to.
     */
    hasSelection() {
      const t = selectionTarget();
      return t != null && t.start < t.end;
    },
    /**
     * Give the keyboard back to this surface — see the contract on
     * `ElaborateEditorHandle.focus`. The toolbar calls it after a command that
     * ended up holding the focus (a ribbon button, a popover trigger); without
     * it the next chord is delivered to that control and the document never
     * sees it (measured live: ⌘Z immediately after a ribbon Bold did nothing).
     *
     * The DOM surface has no single focusable root — every block is its own
     * contentEditable — so it re-focuses the block the selection is in and
     * REPLAYS that byte range. Focusing a contentEditable without replaying
     * would collapse the caret to the block start, which is exactly the
     * selection the command just acted on. With no known selection there is
     * nothing safe to focus, so it does nothing rather than guess a block.
     */
    focus() {
      const t = selectionTarget();
      if (!t) return;
      const el = container.querySelector(`[data-block-id="${cssEscape2(t.blockId)}"]`);
      if (!el) return;
      el.focus?.();
      restoreSelection(el, t.start, t.end);
    },
    /** True when the caret is inside an inserted (palette) table — gates the
     *  contextual Table ribbon tab (`env.inTable`). */
    inTable() {
      return paletteEditor.hasTableTarget();
    },
    /** The active rectangular cell selection in the caret's palette table (a
     *  single caret == a 1×1 range), or null outside a table. */
    tableSelection() {
      return paletteEditor.tableSelection();
    },
    /** True when the active cell selection spans more than one cell — the host
     *  reads this into `ToolbarEnv.hasCellRange`. */
    hasCellRange() {
      return paletteEditor.hasCellRange();
    },
    /** True when the caret's palette table HAS a structural header row. The DOM
     *  surface CAN answer this cheaply — the stored `TableBlock` carries
     *  `header` — so it does, which is what makes the Header-row toggle able to
     *  turn a header OFF and render pressed. */
    tableHeaderOn() {
      return paletteEditor.hasTableHeader();
    },
    // W4 lists — a structured palette list (caret inside one) is styled through
    // its real docz list verb; a plain paragraph FALLS BACK to a `list_item`
    // block kind (the documented pre-W4 fallback) so the toolbar list buttons
    // turn ANY block into a list item that renders `<li>` and PERSISTS in
    // `toHtml()`. (`paletteEditor.*` returns null when the caret is not in a
    // palette list, which is exactly when the fallback applies.)
    //
    // BULLET vs ORDERED: the docz `list_item` fragment kind renders ONLY a bullet
    // `<ul>` (its single marker family), so a NUMBERED-list button on a plain
    // paragraph would otherwise collapse to the identical bullet — the live
    // "ordered → bullet" defect. The fallback therefore records an ORDERED style
    // in `orderedLists` (cleared for bullet), and `repaintOverrides`/`toHtml`
    // render that block as a real `<ol>` via `renderBlockDto` — docz owns the
    // numbering; the block keeps its `list_item` override for editing + anchoring.
    setListStyle(style) {
      const viaPalette = paletteEditor.setListStyle(style);
      if (viaPalette) return viaPalette;
      const target = selectionTarget();
      if (!target || isReadOnlyBlock(target.blockId)) return ensureListItem();
      const alreadyList = blockFragment(target.blockId)?.kind === "list_item";
      if (style === "bullet") orderedLists.delete(target.blockId);
      else orderedLists.set(target.blockId, style);
      const frag = ensureListItem();
      if (frag && alreadyList) render();
      return frag;
    },
    setListItemLevel(delta) {
      return paletteEditor.setListItemLevel(delta);
    },
    restartList(start) {
      return paletteEditor.restartList(start) ?? ensureListItem();
    },
    toggleTaskList() {
      return paletteEditor.toggleTaskList() ?? toggleListItem();
    },
    // The whole-list checklist switch. The DOM fallback has no per-item
    // `checked` model at all (a `list_item` block kind is the whole of it), so
    // `toggleListItem()` is the closest honest answer there: it moves the block
    // in/out of list-hood, which is what "stop being a checklist" means on a
    // surface that cannot express a checkbox. Turning one ON there has no
    // meaning and refuses rather than pretending.
    setChecklist(on) {
      return paletteEditor.setChecklist(on) ?? (on ? null : toggleListItem());
    },
    setBulletGlyph(glyph) {
      return paletteEditor.setBulletGlyph(glyph);
    },
    activeFormat() {
      const target = selectionTarget();
      const tableHeaderOn = paletteEditor.hasTableHeader();
      const pageColor = sectionDecoration.pageColor ?? null;
      const list = listStateOfBlock(paletteEditor.listBlock());
      const empty = {
        marks: /* @__PURE__ */ new Set(),
        link: null,
        color: null,
        highlight: null,
        fontFamily: null,
        fontSize: null,
        underlineStyle: null,
        letterSpacing: null,
        textEffects: null,
        charBorder: null,
        emphasis: null,
        kind: "paragraph",
        level: null,
        para: emptyParaProps(),
        tableHeaderOn,
        pageColor,
        list
      };
      if (!target) return empty;
      const frag = blockFragment(target.blockId);
      if (!frag) return empty;
      return {
        ...activeFormatOf(frag, target.start, target.end),
        tableHeaderOn,
        pageColor,
        list
      };
    },
    highlightAnchor(commentId, state = "exact") {
      return handle.highlight(commentId, toDomState(state));
    },
    showProposal(proposal) {
      const overlay = showProposalOverlay(container, proposal);
      if (!overlay) return false;
      proposals.set(proposal.proposalId, proposal);
      applyModeAndReadOnly();
      return true;
    },
    acceptProposal(proposalId) {
      return decideProposal(proposalId, "accept");
    },
    rejectProposal(proposalId) {
      return decideProposal(proposalId, "reject");
    },
    getInsertMenu(_context) {
      return [];
    },
    validateBlock(_patch) {
      return [];
    },
    // History (undo/redo over the FULL controller state — see `doc-history.ts`).
    undo: () => history.undo(),
    redo: () => history.redo(),
    canUndo: () => history.canUndo(),
    canRedo: () => history.canRedo(),
    historyBarrier: () => history.barrier(),
    // Clipboard + painter — the ribbon buttons run the SAME code as the gestures.
    cut: () => clipboard.cut(),
    copy: () => clipboard.copy(),
    paste: () => clipboard.paste(),
    pasteFromSystem: () => clipboard.pasteFromSystem(),
    canPaste: () => clipboard.canPaste(),
    formatPainter(sticky = false) {
      if (painter.armed()) return painter.apply();
      painter.arm(sticky);
      return true;
    },
    painterArmed: () => painter.armed(),
    handleShortcut: (ev) => services.dispatch(ev),
    toHtml() {
      const hasHydrated = handle.blocks.some(
        (b) => b.id != null && paletteEditor.isHydrated(b.id)
      );
      const base = overrides.size === 0 && !hasHydrated ? handle.toHtml() : handle.blocks.map((b) => {
        if (b.id != null && paletteEditor.isHydrated(b.id)) {
          return paletteEditor.htmlFor(b.id);
        }
        const override = b.id ? overrides.get(b.id) : void 0;
        if (override) {
          const orderedStyle = b.id ? orderedLists.get(b.id) : void 0;
          return orderedStyle ? orderedListHtml(b.id, override, orderedStyle) : renderFragmentToHtml(docz(), override);
        }
        const r = docz().renderBlockToHtml(b.text, b.comments, b.kind, b.level);
        return r.ok ? bodyInnerHtml(r.html) : "";
      }).join("\n");
      const palette = paletteEditor.toHtml();
      if (!palette) return base;
      return base ? `${base}
${palette}` : palette;
    }
  };
  function decideProposal(proposalId, decision) {
    const p = proposals.get(proposalId);
    if (!p) return null;
    clearProposalOverlay(container, p.blockId);
    proposals.delete(proposalId);
    applyModeAndReadOnly();
    const d = {
      proposalId,
      blockId: p.blockId,
      decision,
      addressesCommentIds: p.addressesCommentIds
    };
    opts.onProposalDecision?.(d);
    return d;
  }
  const services = createEditorServices({
    container,
    getModule: () => docz(),
    api,
    getHandle: () => handle,
    setHandle: (next) => {
      handle = next;
    },
    selectionTarget,
    caretTarget,
    blockFragment,
    render: () => render(),
    onChange: (patch) => opts.onChange?.(patch),
    canEdit: () => opts.mode === "edit",
    bank: () => history.bank(null),
    canUndo: () => history.canUndo(),
    canRedo: () => history.canRedo(),
    inTable: () => paletteEditor.hasTableTarget(),
    hasCellRange: () => paletteEditor.hasCellRange(),
    tableHeaderOn: () => paletteEditor.hasTableHeader(),
    getMode: () => opts.mode,
    // Caret gestures the surface owns — they run BEFORE catalog dispatch, so a
    // keybinding can never steal list nesting / the soft break / the merge.
    gestures: {
      tab: (shift) => {
        if (!paletteEditor.hasListTarget()) return false;
        api.setListItemLevel(shift ? -1 : 1);
        return true;
      },
      softBreak: () => {
        structural.insertSoftBreakAtCaret();
        return true;
      },
      mergeBackward: () => {
        if (!structural.caretAtMergeableBlockStart()) return false;
        structural.mergeBlockBackward();
        return true;
      },
      pageBreak: () => {
        api.insertPageBreak();
        return true;
      }
    },
    setSuppress: (inputType) => {
      suppressNextBeforeInput = inputType;
    },
    ...initial.onShortcut ? { onShortcut: initial.onShortcut } : {}
  });
  const clipboard = services.clipboard;
  const painter = services.painter;
  onKeyDown = services.keyHandler;
  container.addEventListener("keydown", onKeyDown);
  return api;
}
var UNSAFE_SCHEME = /^\s*(javascript|data|vbscript|file)\s*:/i;
function isCommittableHref(href) {
  const trimmed = href.trim();
  return trimmed.length > 0 && !UNSAFE_SCHEME.test(trimmed);
}
function disabledReason(api) {
  if (typeof api.linkTarget !== "function") return null;
  return api.linkTarget() == null ? "Select some text (or put the caret in a link) to add a link" : null;
}
function LinkPopover(props) {
  const { api, after } = props;
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [text, setText] = useState("");
  const [href, setHref] = useState("");
  const textId = useId();
  const urlId = useId();
  const textRef = useRef(null);
  const focusArmed = useRef(false);
  const restoreRef = useRef(void 0);
  const reason = open ? null : disabledReason(api);
  const active = api.active();
  const canEditText = api.applyLink != null && (target?.canEditText ?? true);
  const canSave = isCommittableHref(href) && (!canEditText || text.length > 0);
  const attachText = useCallback((node) => {
    textRef.current = node;
    if (!node || !focusArmed.current) return;
    focusArmed.current = false;
    node.focus();
    node.select();
  }, []);
  function onOpenChange(next) {
    if (!next) {
      restoreRef.current = target?.range;
      setOpen(false);
      return;
    }
    const t = api.linkTarget?.() ?? null;
    setTarget(t);
    setText(t?.text ?? "");
    setHref(t?.href ?? "");
    restoreRef.current = t?.range;
    focusArmed.current = true;
    setOpen(true);
  }
  function commit() {
    if (!canSave) return;
    if (api.applyLink) {
      api.applyLink({
        href: href.trim(),
        text: canEditText ? text : void 0,
        range: target?.range
      });
    } else {
      api.setLink(href.trim());
    }
    restoreRef.current = void 0;
    setOpen(false);
    after();
  }
  function onFieldKeyDown(e) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    commit();
  }
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange, placement: "bottom-start", children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-testid": "toolbar-link",
        title: reason ?? (active.link != null ? "Edit link" : "Link"),
        "aria-label": "Link",
        "aria-pressed": active.link != null,
        "data-active": active.link != null,
        disabled: reason != null,
        children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "link" })
      }
    ) }),
    /* @__PURE__ */ jsx(
      PopoverContent,
      {
        role: "dialog",
        onOpenAutoFocus: (e) => {
          e.preventDefault();
        },
        onCloseAutoFocus: (e) => {
          e.preventDefault();
          api.restoreFocus?.(restoreRef.current);
        },
        children: /* @__PURE__ */ jsxs("div", { className: "ela-link-popover", "data-testid": "link-popover", children: [
          /* @__PURE__ */ jsxs("div", { className: "ela-link-popover__row", children: [
            /* @__PURE__ */ jsx("label", { className: "ela-link-popover__label", htmlFor: textId, children: "Text" }),
            /* @__PURE__ */ jsx(
              TextInput,
              {
                ref: attachText,
                id: textId,
                size: "sm",
                "data-testid": "link-popover-text",
                value: text,
                disabled: !canEditText,
                title: canEditText ? void 0 : "This surface can set the link but not rewrite the linked text yet",
                placeholder: "Link text",
                onChange: (e) => setText(e.target.value),
                onKeyDown: onFieldKeyDown
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "ela-link-popover__row", children: [
            /* @__PURE__ */ jsx("label", { className: "ela-link-popover__label", htmlFor: urlId, children: "Link" }),
            /* @__PURE__ */ jsx(
              TextInput,
              {
                id: urlId,
                size: "sm",
                "data-testid": "link-popover-url",
                value: href,
                placeholder: "https://\u2026",
                onChange: (e) => setHref(e.target.value),
                onKeyDown: onFieldKeyDown
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "ela-link-popover__actions", children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                size: "sm",
                variant: "ghost",
                "data-testid": "link-popover-cancel",
                onClick: () => onOpenChange(false),
                children: "Cancel"
              }
            ),
            /* @__PURE__ */ jsx(
              Button,
              {
                size: "sm",
                variant: "primary",
                "data-testid": "link-popover-save",
                disabled: !canSave,
                onClick: commit,
                children: "Save"
              }
            )
          ] })
        ] })
      }
    )
  ] });
}
function ListControls({ api, after }) {
  const [formatOpen, setFormatOpen] = useState(false);
  const [restartOpen, setRestartOpen] = useState(false);
  const [startAt, setStartAt] = useState("1");
  if (!api.supports.lists) return null;
  return /* @__PURE__ */ jsxs("div", { className: "ela-toolbar-group", "data-group": "list-format", children: [
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-testid": "toolbar-list-format",
        title: "List number format",
        "aria-label": "List number format",
        "aria-expanded": formatOpen,
        "aria-haspopup": "menu",
        onClick: () => setFormatOpen((o) => !o),
        children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "list-format" })
      }
    ),
    formatOpen && /* @__PURE__ */ jsx("div", { className: "ela-list-format-menu", role: "menu", "data-testid": "list-format-menu", children: LIST_FORMAT_ENTRIES.map((e) => /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        role: "menuitem",
        "data-testid": `toolbar-list-format-${e.style}`,
        "aria-label": e.label,
        onClick: () => {
          api.setListStyle(e.style);
          setFormatOpen(false);
          after();
        },
        children: [
          /* @__PURE__ */ jsx("span", { className: "ela-list-format-sample", children: e.sample }),
          e.label
        ]
      },
      e.style
    )) }),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-testid": "toolbar-task-list",
        title: "Checklist",
        "aria-label": "Checklist",
        onClick: () => {
          api.toggleTaskList();
          after();
        },
        children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "task-list" })
      }
    ),
    /* @__PURE__ */ jsxs(
      "select",
      {
        "data-testid": "toolbar-bullet-glyph",
        title: "Bullet glyph",
        "aria-label": "Bullet glyph",
        value: "",
        onChange: (e) => {
          if (e.target.value) api.setBulletGlyph(e.target.value);
          after();
        },
        children: [
          /* @__PURE__ */ jsx("option", { value: "", children: "Bullet\u2026" }),
          BULLET_GLYPHS.map((g) => /* @__PURE__ */ jsx("option", { value: g, children: g }, g))
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-testid": "toolbar-list-restart",
        title: "Restart / continue numbering",
        "aria-label": "Restart or continue numbering",
        "aria-expanded": restartOpen,
        "aria-haspopup": "dialog",
        onClick: () => setRestartOpen((o) => !o),
        children: "#"
      }
    ),
    restartOpen && /* @__PURE__ */ jsxs(
      "div",
      {
        className: "ela-list-restart",
        role: "dialog",
        "aria-label": "Restart numbering",
        "data-testid": "list-restart-dialog",
        children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "toolbar-list-restart-1",
              onClick: () => {
                api.restartList(1);
                setRestartOpen(false);
                after();
              },
              children: "Restart at 1"
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "toolbar-list-continue",
              onClick: () => {
                api.restartList(null);
                setRestartOpen(false);
                after();
              },
              children: "Continue previous"
            }
          ),
          /* @__PURE__ */ jsxs("label", { children: [
            "Start at",
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "number",
                "data-testid": "toolbar-list-start-input",
                "aria-label": "Start numbering at",
                min: 1,
                value: startAt,
                onChange: (e) => setStartAt(e.target.value)
              }
            )
          ] }),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "toolbar-list-start-apply",
              onClick: () => {
                const n = Number.parseInt(startAt, 10);
                if (Number.isInteger(n) && n >= 1) api.restartList(n);
                setRestartOpen(false);
                after();
              },
              children: "Set"
            }
          )
        ]
      }
    )
  ] });
}
var ALIGN_BUTTONS = [
  { align: "start", icon: "align-left", testid: "toolbar-align-left", label: "Align left" },
  { align: "center", icon: "align-center", testid: "toolbar-align-center", label: "Align center" },
  { align: "end", icon: "align-right", testid: "toolbar-align-right", label: "Align right" },
  { align: "justify", icon: "align-justify", testid: "toolbar-align-justify", label: "Justify" }
];
var TAB_ALIGN_LABELS = {
  left: "Left",
  center: "Center",
  right: "Right",
  decimal: "Decimal",
  bar: "Bar"
};
var TAB_LEADER_LABELS = {
  none: "None",
  dot: "Dots \u2026",
  hyphen: "Hyphens - -",
  underscore: "Line ___",
  middle_dot: "Mid-dots \xB7"
};
function spacingSelectValue(spacing) {
  if (!spacing) return "";
  if (spacing.rule === "multiple" && LINE_SPACING_MULTIPLES.includes(spacing.value)) {
    return String(spacing.value);
  }
  return "custom";
}
function ParagraphGroup({ api, after }) {
  const active = api.active();
  const supported = api.supports.paraProps;
  const para = active.para;
  const align = para.textAlign ?? "start";
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [tabsOpen, setTabsOpen] = useState(false);
  const [customSpacing, setCustomSpacing] = useState(false);
  const selectValue = customSpacing ? "custom" : spacingSelectValue(para.lineSpacing);
  const onSpacingChange = (value) => {
    if (value === "") {
      setCustomSpacing(false);
      api.setLineSpacing(null);
    } else if (value === "custom") {
      setCustomSpacing(true);
    } else {
      setCustomSpacing(false);
      api.setLineSpacing({ rule: "multiple", value: Number(value) });
    }
    after();
  };
  return /* @__PURE__ */ jsxs("div", { className: "ela-toolbar-group", "data-group": "paragraph", children: [
    ALIGN_BUTTONS.map((b) => /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-testid": b.testid,
        title: b.label,
        "aria-label": b.label,
        "aria-pressed": align === b.align,
        "data-active": align === b.align,
        disabled: !supported,
        onClick: () => {
          api.setAlignment(b.align);
          after();
        },
        children: /* @__PURE__ */ jsx(ToolbarIcon, { name: b.icon })
      },
      b.align
    )),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-testid": "toolbar-outdent",
        title: "Decrease indent",
        "aria-label": "Decrease indent",
        disabled: !supported,
        onClick: () => {
          api.outdent();
          after();
        },
        children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "outdent" })
      }
    ),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-testid": "toolbar-indent",
        title: "Increase indent",
        "aria-label": "Increase indent",
        disabled: !supported,
        onClick: () => {
          api.indent();
          after();
        },
        children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "indent" })
      }
    ),
    /* @__PURE__ */ jsx("span", { className: "ela-toolbar-affordance", "aria-hidden": "true", children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "line-spacing" }) }),
    /* @__PURE__ */ jsxs(
      "select",
      {
        "data-testid": "toolbar-line-spacing",
        title: "Line spacing",
        "aria-label": "Line spacing",
        disabled: !supported,
        value: selectValue,
        onChange: (e) => onSpacingChange(e.target.value),
        children: [
          /* @__PURE__ */ jsx("option", { value: "", children: "Spacing" }),
          LINE_SPACING_MULTIPLES.map((m) => /* @__PURE__ */ jsx("option", { value: m, children: m.toFixed(2).replace(/\.00$/, ".0") }, m)),
          /* @__PURE__ */ jsx("option", { value: "custom", children: "Custom\u2026" })
        ]
      }
    ),
    customSpacing && /* @__PURE__ */ jsx(
      "input",
      {
        type: "number",
        className: "ela-toolbar-num",
        "data-testid": "toolbar-line-spacing-exact",
        title: "Exact line height (points)",
        "aria-label": "Exact line height in points",
        min: 1,
        step: 0.5,
        disabled: !supported,
        onChange: (e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v) && v > 0) api.setLineSpacing({ rule: "exact", pt: v });
          after();
        }
      }
    ),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-testid": "toolbar-para-popover",
        title: "Paragraph spacing & indent",
        "aria-label": "Paragraph spacing and indent",
        "aria-expanded": popoverOpen,
        "aria-haspopup": "dialog",
        disabled: !supported,
        onClick: () => setPopoverOpen((o) => !o),
        children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "para-spacing" })
      }
    ),
    popoverOpen && /* @__PURE__ */ jsxs(
      "div",
      {
        className: "ela-para-popover",
        role: "dialog",
        "aria-label": "Paragraph spacing and indent",
        "data-testid": "para-popover",
        children: [
          /* @__PURE__ */ jsxs("label", { children: [
            "Space before (pt)",
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "number",
                "data-testid": "toolbar-space-before",
                "aria-label": "Space before (points)",
                min: 0,
                step: 1,
                value: para.spaceBeforePt ?? "",
                onChange: (e) => {
                  const v = e.target.value === "" ? null : Number(e.target.value);
                  api.setSpaceBefore(v);
                  after();
                }
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("label", { children: [
            "Space after (pt)",
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "number",
                "data-testid": "toolbar-space-after",
                "aria-label": "Space after (points)",
                min: 0,
                step: 1,
                value: para.spaceAfterPt ?? "",
                onChange: (e) => {
                  const v = e.target.value === "" ? null : Number(e.target.value);
                  api.setSpaceAfter(v);
                  after();
                }
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("label", { children: [
            "First-line indent (pt)",
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "number",
                "data-testid": "toolbar-first-line",
                "aria-label": "First-line indent (points)",
                min: 0,
                step: 1,
                value: para.indent?.firstLinePt ?? "",
                onChange: (e) => {
                  api.setFirstLineIndent(Number(e.target.value) || 0);
                  after();
                }
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("label", { children: [
            "Hanging indent (pt)",
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "number",
                "data-testid": "toolbar-hanging",
                "aria-label": "Hanging indent (points)",
                min: 0,
                step: 1,
                value: para.indent?.hangingPt ?? "",
                onChange: (e) => {
                  api.setHangingIndent(Number(e.target.value) || 0);
                  after();
                }
              }
            )
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-testid": "toolbar-tabs",
        title: "Tab stops",
        "aria-label": "Tab stops",
        "aria-expanded": tabsOpen,
        "aria-haspopup": "dialog",
        disabled: !supported,
        onClick: () => setTabsOpen((o) => !o),
        children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "tabs" })
      }
    ),
    tabsOpen && /* @__PURE__ */ jsx(
      TabsDialog,
      {
        stops: para.tabStops ?? [],
        onChange: (stops) => {
          api.setTabStops(stops);
          after();
        }
      }
    )
  ] });
}
function TabsDialog({ stops, onChange }) {
  const update = (i, patch) => {
    onChange(stops.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  };
  const remove = (i) => onChange(stops.filter((_, idx) => idx !== i));
  const add = () => onChange([...stops, { posPt: 72, align: "left", leader: "none" }]);
  return /* @__PURE__ */ jsxs("div", { className: "ela-tabs-dialog", role: "dialog", "aria-label": "Tab stops", "data-testid": "tabs-dialog", children: [
    stops.length === 0 && /* @__PURE__ */ jsx("p", { className: "ela-tabs-empty", children: "No tab stops." }),
    stops.map((s, i) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: tab-stop rows are positional
      /* @__PURE__ */ jsxs("div", { className: "ela-tabs-row", "data-testid": `tab-stop-${i}`, children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "number",
            "data-testid": `tab-stop-pos-${i}`,
            "aria-label": `Tab stop ${i + 1} position (points)`,
            min: 0,
            step: 1,
            value: s.posPt,
            onChange: (e) => update(i, { posPt: Number(e.target.value) || 0 })
          }
        ),
        /* @__PURE__ */ jsx(
          "select",
          {
            "data-testid": `tab-stop-align-${i}`,
            "aria-label": `Tab stop ${i + 1} alignment`,
            value: s.align,
            onChange: (e) => update(i, { align: e.target.value }),
            children: TAB_ALIGN_OPTIONS.map((a) => /* @__PURE__ */ jsx("option", { value: a, children: TAB_ALIGN_LABELS[a] }, a))
          }
        ),
        /* @__PURE__ */ jsx(
          "select",
          {
            "data-testid": `tab-stop-leader-${i}`,
            "aria-label": `Tab stop ${i + 1} leader`,
            value: s.leader,
            onChange: (e) => update(i, { leader: e.target.value }),
            children: TAB_LEADER_OPTIONS.map((l) => /* @__PURE__ */ jsx("option", { value: l, children: TAB_LEADER_LABELS[l] }, l))
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "data-testid": `tab-stop-remove-${i}`,
            "aria-label": `Remove tab stop ${i + 1}`,
            onClick: () => remove(i),
            children: "\xD7"
          }
        )
      ] }, i)
    )),
    /* @__PURE__ */ jsx("button", { type: "button", "data-testid": "tab-stop-add", onClick: add, children: "Add tab stop" })
  ] });
}

// src/table-dto.ts
var TABLE_PICKER_MAX_ROWS = 8;
var TABLE_PICKER_MAX_COLS = 10;
var CELL_VALIGN_OPTIONS = ["top", "middle", "bottom"];
function defaultBorder() {
  return { widthPt: 1, color: "#000000", style: "solid" };
}
function TableSizePicker({
  onPick,
  maxRows = TABLE_PICKER_MAX_ROWS,
  maxCols = TABLE_PICKER_MAX_COLS,
  testId = "toolbar-table-picker"
}) {
  const [hover, setHover] = useState({ r: 0, c: 0 });
  const rows = Array.from({ length: maxRows }, (_, i) => i + 1);
  const cols = Array.from({ length: maxCols }, (_, i) => i + 1);
  return /* @__PURE__ */ jsxs("div", { className: "ela-table-picker", role: "dialog", "aria-label": "Insert table", "data-testid": testId, children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "ela-table-picker-grid",
        style: { gridTemplateColumns: `repeat(${maxCols}, 1fr)` },
        children: rows.map(
          (r) => cols.map((c) => {
            const on = r <= hover.r && c <= hover.c;
            return /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                className: "ela-table-picker-cell",
                "data-testid": `table-size-${r}x${c}`,
                "data-on": on,
                "aria-label": `${r} by ${c} table`,
                onMouseEnter: () => setHover({ r, c }),
                onFocus: () => setHover({ r, c }),
                onClick: () => onPick(r, c)
              },
              `${r}x${c}`
            );
          })
        )
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "ela-table-picker-label", "data-testid": "table-size-label", children: hover.r > 0 ? `${hover.r} \xD7 ${hover.c}` : "Insert table" })
  ] });
}
var MARK_BUTTONS = [
  { type: "bold", icon: "bold", title: "Bold (\u2318B)", comment: true },
  { type: "italic", icon: "italic", title: "Italic (\u2318I)", comment: true },
  { type: "underline", icon: "underline", title: "Underline (\u2318U)", comment: true },
  { type: "strikeout", icon: "strikeout", title: "Strikethrough", comment: true },
  { type: "code", icon: "code", title: "Inline code", comment: true },
  { type: "superscript", icon: "superscript", title: "Superscript" },
  { type: "subscript", icon: "subscript", title: "Subscript" },
  // W1 (C12): small-caps had a mark + ToggleMarkType but NO button; all-caps is new.
  { type: "smallcaps", icon: "small-caps", title: "Small caps (\u2318\u21E7K)" },
  { type: "all_caps", icon: "all-caps", title: "All caps" }
];
var HEADINGS = [1, 2, 3, 4, 5, 6];
var DEFAULT_TEXT_COLOR2 = "#1a1a1a";
var CHANGE_CASE_LABELS = {
  upper: "UPPERCASE",
  lower: "lowercase",
  title: "Title Case",
  sentence: "Sentence case",
  toggle: "tOGGLE cASE"
};
var UNDERLINE_STYLE_LABELS = {
  single: "Single",
  double: "Double",
  dotted: "Dotted",
  dashed: "Dashed",
  wavy: "Wavy",
  thick: "Thick"
};
function FormattingToolbar(props) {
  const { api, mode = "document", onInsertBlock, onInsertPageBreak, onCommand } = props;
  const isComment = mode === "comment";
  const [, setTick] = useState(0);
  const [imageSrc, setImageSrc] = useState("");
  const [tablePickerOpen, setTablePickerOpen] = useState(false);
  const [color, setColor] = useState(DEFAULT_TEXT_COLOR2);
  const [highlightColor, setHighlightColor] = useState("#fff3a3");
  const [underlineColor, setUnderlineColor] = useState(DEFAULT_TEXT_COLOR2);
  const active = api.active();
  const after = () => {
    setTick((n) => n + 1);
    onCommand?.();
  };
  const markActive = (t) => active.marks.has(t);
  const kindActive = (kind, level) => active.kind === kind && (level == null || active.level === level);
  const doMark = (t) => {
    api.toggleMark(t);
    after();
  };
  const doKind = (kind, level) => {
    api.setBlockKind(kind, level);
    after();
  };
  const insert = (block) => {
    onInsertBlock?.(block);
    after();
  };
  const cls = props.className ? `ela-toolbar ${props.className}` : "ela-toolbar";
  const marks = MARK_BUTTONS.filter((b) => !isComment || b.comment);
  const families = active.fontFamily && !FONT_FAMILY_STACK.includes(active.fontFamily) ? [active.fontFamily, ...FONT_FAMILY_STACK] : FONT_FAMILY_STACK;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cls,
      role: "toolbar",
      "aria-label": "Formatting",
      "data-testid": props.testId ?? "formatting-toolbar",
      "data-mode": mode,
      children: [
        !isComment && /* @__PURE__ */ jsxs("div", { className: "ela-toolbar-group", "data-group": "block", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "toolbar-paragraph",
              title: "Paragraph",
              "aria-label": "Paragraph",
              "aria-pressed": kindActive("paragraph"),
              "data-active": kindActive("paragraph"),
              onClick: () => doKind("paragraph"),
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "paragraph" })
            }
          ),
          HEADINGS.map((lvl) => /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": `toolbar-h${lvl}`,
              title: `Heading ${lvl}`,
              "aria-label": `Heading ${lvl}`,
              "aria-pressed": kindActive("heading", lvl),
              "data-active": kindActive("heading", lvl),
              onClick: () => doKind("heading", lvl),
              children: /* @__PURE__ */ jsx(HeadingIcon, { level: lvl })
            },
            lvl
          ))
        ] }),
        !isComment && /* @__PURE__ */ jsx(ParagraphGroup, { api, after }),
        !isComment && /* @__PURE__ */ jsxs("div", { className: "ela-toolbar-group", "data-group": "font", children: [
          /* @__PURE__ */ jsx("span", { className: "ela-toolbar-affordance", "aria-hidden": "true", children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "font-family" }) }),
          /* @__PURE__ */ jsxs(
            "select",
            {
              "data-testid": "toolbar-font-family",
              title: "Font",
              "aria-label": "Font family",
              value: active.fontFamily ?? "",
              onChange: (e) => {
                if (e.target.value) api.setFontFamily(e.target.value);
                after();
              },
              children: [
                /* @__PURE__ */ jsx("option", { value: "", children: "Font" }),
                families.map((f) => /* @__PURE__ */ jsx("option", { value: f, children: f }, f))
              ]
            }
          ),
          /* @__PURE__ */ jsx("span", { className: "ela-toolbar-affordance", "aria-hidden": "true", children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "font-size" }) }),
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "number",
              className: "ela-toolbar-num",
              "data-testid": "toolbar-font-size",
              title: "Font size (points)",
              "aria-label": "Font size (points)",
              list: "ela-font-size-stops",
              min: 1,
              value: active.fontSize ?? "",
              onChange: (e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v) && v > 0) api.setFontSize(v);
                after();
              }
            }
          ),
          /* @__PURE__ */ jsx("datalist", { id: "ela-font-size-stops", children: FONT_SIZE_STOPS.map((s) => /* @__PURE__ */ jsx("option", { value: s }, s)) })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "ela-toolbar-group", "data-group": "marks", children: [
          marks.map((b) => /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": `toolbar-${b.type}`,
              title: b.title,
              "aria-label": b.title,
              "aria-pressed": markActive(b.type),
              "data-active": markActive(b.type),
              onClick: () => doMark(b.type),
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: b.icon })
            },
            b.type
          )),
          /* @__PURE__ */ jsx(LinkPopover, { api, after })
        ] }),
        !isComment && /* @__PURE__ */ jsxs("div", { className: "ela-toolbar-group", "data-group": "underline-style", children: [
          /* @__PURE__ */ jsx("span", { className: "ela-toolbar-affordance", "aria-hidden": "true", children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "underline-style" }) }),
          /* @__PURE__ */ jsxs(
            "select",
            {
              "data-testid": "toolbar-underline-style",
              title: "Underline style",
              "aria-label": "Underline style",
              value: active.underlineStyle ?? "",
              onChange: (e) => {
                const s = e.target.value;
                if (s) api.setUnderlineStyle(s, underlineColor);
                after();
              },
              children: [
                /* @__PURE__ */ jsx("option", { value: "", children: "Underline\u2026" }),
                UNDERLINE_STYLE_OPTIONS.map((s) => /* @__PURE__ */ jsx("option", { value: s, children: UNDERLINE_STYLE_LABELS[s] }, s))
              ]
            }
          ),
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "color",
              "data-testid": "toolbar-underline-color",
              title: "Underline color",
              "aria-label": "Underline color",
              value: underlineColor,
              onChange: (e) => setUnderlineColor(e.target.value)
            }
          )
        ] }),
        !isComment && /* @__PURE__ */ jsxs("div", { className: "ela-toolbar-group", "data-group": "color", children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "color",
              "data-testid": "toolbar-color-input",
              title: "Text color",
              "aria-label": "Text color",
              value: color,
              onChange: (e) => setColor(e.target.value)
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "toolbar-color",
              title: "Apply text color",
              "aria-label": "Apply text color",
              "data-active": active.color != null,
              onClick: () => {
                api.setColor(color);
                after();
              },
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "color" })
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "ela-toolbar-text",
              "data-testid": "toolbar-color-auto",
              title: "Automatic (default) text color",
              "aria-label": "Automatic text color",
              onClick: () => {
                api.setColor(DEFAULT_TEXT_COLOR2);
                after();
              },
              children: "Auto"
            }
          ),
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "color",
              "data-testid": "toolbar-highlight-input",
              title: "Highlight color",
              "aria-label": "Highlight color",
              value: highlightColor,
              onChange: (e) => setHighlightColor(e.target.value)
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "toolbar-highlight",
              title: "Apply highlight",
              "aria-label": "Apply highlight",
              "data-active": active.highlight != null,
              onClick: () => {
                api.setHighlight(highlightColor);
                after();
              },
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "highlight" })
            }
          )
        ] }),
        !isComment && /* @__PURE__ */ jsxs("div", { className: "ela-toolbar-group", "data-group": "clear", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "toolbar-clear-format",
              title: "Clear formatting",
              "aria-label": "Clear formatting",
              disabled: !api.supports.clearFormatting,
              onClick: () => {
                api.clearFormatting();
                after();
              },
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "clear-format" })
            }
          ),
          /* @__PURE__ */ jsx("span", { className: "ela-toolbar-affordance", "aria-hidden": "true", children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "change-case" }) }),
          /* @__PURE__ */ jsxs(
            "select",
            {
              "data-testid": "toolbar-change-case",
              title: "Change case",
              "aria-label": "Change case",
              disabled: !api.supports.changeCase,
              value: "",
              onChange: (e) => {
                const m = e.target.value;
                if (m) api.changeCase(m);
                after();
              },
              children: [
                /* @__PURE__ */ jsx("option", { value: "", children: "Case" }),
                CHANGE_CASE_MODES.map((m) => /* @__PURE__ */ jsx("option", { value: m, children: CHANGE_CASE_LABELS[m] }, m))
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "ela-toolbar-group", "data-group": "lists", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "toolbar-bullet-list",
              title: "Bullet list",
              "aria-label": "Bullet list",
              "aria-pressed": kindActive("list_item"),
              "data-active": kindActive("list_item"),
              onClick: () => !isComment && api.supports.blockPalette ? insert(api.insertList(false)) : doKind("list_item", 1),
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "bullet-list" })
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "toolbar-ordered-list",
              title: "Ordered list",
              "aria-label": "Ordered list",
              onClick: () => !isComment && api.supports.blockPalette ? insert(api.insertList(true)) : doKind("list_item", 1),
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "ordered-list" })
            }
          ),
          !isComment && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "toolbar-blockquote",
              title: "Blockquote",
              "aria-label": "Blockquote",
              "aria-pressed": kindActive("blockquote"),
              "data-active": kindActive("blockquote"),
              onClick: () => doKind("blockquote"),
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "blockquote" })
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "toolbar-code-block",
              title: "Code block",
              "aria-label": "Code block",
              "aria-pressed": kindActive("code"),
              "data-active": kindActive("code"),
              onClick: () => doKind("code"),
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "code-block" })
            }
          )
        ] }),
        !isComment && /* @__PURE__ */ jsx(ListControls, { api, after }),
        !isComment && api.supports.blockPalette && /* @__PURE__ */ jsxs("div", { className: "ela-toolbar-group", "data-group": "insert", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "toolbar-table",
              title: "Insert table",
              "aria-label": "Insert table",
              "aria-expanded": tablePickerOpen,
              "aria-haspopup": "dialog",
              onClick: () => setTablePickerOpen((o) => !o),
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "table" })
            }
          ),
          tablePickerOpen && /* @__PURE__ */ jsx(
            TableSizePicker,
            {
              onPick: (rows, cols) => {
                insert(api.insertTable(rows, cols));
                setTablePickerOpen(false);
              }
            }
          ),
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "url",
              "data-testid": "toolbar-image-input",
              placeholder: "image URL",
              value: imageSrc,
              onChange: (e) => setImageSrc(e.target.value)
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "toolbar-image",
              title: "Insert image",
              "aria-label": "Insert image",
              disabled: imageSrc.length === 0,
              onClick: () => {
                if (imageSrc) insert(api.insertImage({ src: imageSrc, alt: "" }));
              },
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "image" })
            }
          ),
          onInsertPageBreak && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "ela-toolbar-text",
              "data-testid": "toolbar-page-break",
              title: "Insert page break (\u2318\u23CE)",
              "aria-label": "Insert page break",
              onClick: () => {
                onInsertPageBreak();
                after();
              },
              children: "Page break"
            }
          )
        ] })
      ]
    }
  );
}
function useDoczEditor(props) {
  const { doc, mode, renderMarks, readOnlyBlocks, onChange, onSelect, onProposalDecision } = props;
  const containerRef = useRef(null);
  const controllerRef = useRef(null);
  const optionsRef = useRef({ renderMarks, readOnlyBlocks, onChange, onProposalDecision });
  optionsRef.current = { renderMarks, readOnlyBlocks, onChange, onProposalDecision };
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const controller = createEditorController(container, {
      handle: doc,
      mode,
      renderMarks: optionsRef.current.renderMarks,
      readOnlyBlocks: optionsRef.current.readOnlyBlocks,
      onChange: (patch) => optionsRef.current.onChange?.(patch),
      onProposalDecision: (d) => optionsRef.current.onProposalDecision?.(d)
    });
    controllerRef.current = controller;
    controller.render();
    return () => {
      controller.destroy();
      controllerRef.current = null;
    };
  }, []);
  useEffect(() => {
    controllerRef.current?.setHandle(doc);
  }, [doc]);
  useEffect(() => {
    controllerRef.current?.setOptions({
      mode,
      renderMarks: optionsRef.current.renderMarks,
      readOnlyBlocks: optionsRef.current.readOnlyBlocks
    });
  }, [mode, renderMarks, readOnlyBlocks]);
  useEffect(() => {
    if (!onSelect) return;
    const handler = () => {
      const container = containerRef.current;
      const sel = container?.ownerDocument.getSelection?.();
      if (!container || !sel || sel.rangeCount === 0) {
        onSelect(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const within = container.contains(range.startContainer) && container.contains(range.endContainer);
      onSelect(within && !range.collapsed ? range : null);
    };
    const d = containerRef.current?.ownerDocument ?? document;
    d.addEventListener("selectionchange", handler);
    return () => d.removeEventListener("selectionchange", handler);
  }, [onSelect]);
  const call = useCallback((fn, fallback) => {
    const c = controllerRef.current;
    return c ? fn(c) : fallback;
  }, []);
  const handle = useMemo(
    () => ({
      buildSelector: (range) => call((c) => c.buildSelector(range), null),
      // The DOM surface's answer to "where is your live selection" — the host
      // positions the comment composer against it without touching the DOM
      // selection itself (a canvas surface answers the same question from its
      // engine geometry).
      selectionViewportRect: () => call((c) => c.selectionViewportRect(), null),
      placeCommentMark: (commentId, range) => call((c) => c.placeCommentMark(commentId, range), null),
      resolveAnchor: (selector, commentId, outcome) => call((c) => c.resolveAnchor(selector, commentId, outcome), {
        state: "orphaned",
        commentId,
        spans: []
      }),
      rebaseAnchor: (oldText, newText, selector) => call((c) => c.rebaseAnchor(oldText, newText, selector), null),
      applyTextEdit: (edit) => call((c) => c.applyTextEdit(edit), null),
      highlightAnchor: (commentId, state) => call((c) => c.highlightAnchor(commentId, state), []),
      showProposal: (proposal) => call((c) => c.showProposal(proposal), false),
      acceptProposal: (proposalId) => call((c) => c.acceptProposal(proposalId), null),
      rejectProposal: (proposalId) => call((c) => c.rejectProposal(proposalId), null),
      getInsertMenu: (context) => call((c) => c.getInsertMenu(context), []),
      validateBlock: (patch) => call((c) => c.validateBlock(patch), []),
      toggleMark: (type) => call((c) => c.toggleMark(type), { fragment: null, applied: false, toggledOff: false }),
      setMark: (mark) => call((c) => c.setMark(mark), null),
      clearFormatting: () => call((c) => c.clearFormatting(), null),
      changeCase: (mode2) => call((c) => c.changeCase(mode2), null),
      setBlockKind: (kind, level) => call((c) => c.setBlockKind(kind, level), null),
      setParaProps: (patch) => call((c) => c.setParaProps(patch), null),
      setPageDecoration: (patch) => call((c) => c.setPageDecoration(patch), {}),
      pageDecoration: () => call((c) => c.pageDecoration(), {}),
      insertBlock: (block) => call((c) => c.insertBlock(block), false),
      insertPageBreak: () => call((c) => c.insertPageBreak(), null),
      // History + clipboard + painter — the React shell is a pure passthrough to
      // the controller (which owns the snapshot ring and the DOM listeners).
      undo: () => call((c) => c.undo(), false),
      redo: () => call((c) => c.redo(), false),
      canUndo: () => call((c) => c.canUndo(), false),
      canRedo: () => call((c) => c.canRedo(), false),
      historyBarrier: () => call((c) => c.historyBarrier(), void 0),
      cut: () => call((c) => c.cut(), void 0),
      copy: () => call((c) => c.copy(), void 0),
      paste: () => call((c) => c.paste(), void 0),
      pasteFromSystem: () => call((c) => c.pasteFromSystem(), Promise.resolve("unsupported")),
      canPaste: () => call((c) => c.canPaste(), false),
      formatPainter: (sticky) => call((c) => c.formatPainter(sticky), false),
      painterArmed: () => call((c) => c.painterArmed(), false),
      handleShortcut: (ev) => call((c) => c.handleShortcut(ev), false),
      table: {
        selectTable: () => call((c) => c.table.selectTable(), false),
        deleteTable: () => call((c) => c.table.deleteTable(), false),
        insertRow: (where) => call((c) => c.table.insertRow(where), false),
        deleteRow: () => call((c) => c.table.deleteRow(), false),
        insertColumn: (where) => call((c) => c.table.insertColumn(where), false),
        deleteColumn: () => call((c) => c.table.deleteColumn(), false),
        mergeCells: () => call((c) => c.table.mergeCells(), false),
        splitCell: (r, cCols) => call((c) => c.table.splitCell(r, cCols), false),
        setCellShading: (hex) => call((c) => c.table.setCellShading(hex), false),
        setCellVAlign: (v) => call((c) => c.table.setCellVAlign(v), false),
        setCellBorders: (b) => call((c) => c.table.setCellBorders(b), false),
        setCellAlignH: (a) => call((c) => c.table.setCellAlignH(a), false),
        toggleHeader: (on) => call((c) => c.table.toggleHeader(on), false),
        setColumnWidth: (col, pt) => call((c) => c.table.setColumnWidth(col, pt), false),
        setRowHeadColumns: (n) => call((c) => c.table.setRowHeadColumns(n), false),
        applyTableStyle: (preset) => call((c) => c.table.applyTableStyle(preset), false),
        setHeaderStyle: (style) => call((c) => c.table.setHeaderStyle(style), false),
        setRowBanding: (on, color) => call((c) => c.table.setRowBanding(on, color), false)
      },
      inTable: () => call((c) => c.inTable(), false),
      tableSelection: () => call((c) => c.tableSelection(), null),
      hasCellRange: () => call((c) => c.hasCellRange(), false),
      tableHeaderOn: () => call((c) => c.tableHeaderOn?.() ?? false, false),
      setListStyle: (style) => call((c) => c.setListStyle(style), null),
      setListItemLevel: (delta) => call((c) => c.setListItemLevel(delta), null),
      restartList: (start) => call((c) => c.restartList(start), null),
      toggleTaskList: () => call((c) => c.toggleTaskList(), null),
      setChecklist: (on) => call((c) => c.setChecklist(on), null),
      setBulletGlyph: (glyph) => call((c) => c.setBulletGlyph(glyph), null),
      activeFormat: () => call((c) => c.activeFormat(), {
        marks: /* @__PURE__ */ new Set(),
        link: null,
        color: null,
        highlight: null,
        fontFamily: null,
        fontSize: null,
        underlineStyle: null,
        letterSpacing: null,
        textEffects: null,
        charBorder: null,
        emphasis: null,
        kind: "paragraph",
        level: null,
        para: emptyParaProps(),
        tableHeaderOn: false
      }),
      toHtml: () => call((c) => c.toHtml(), ""),
      get root() {
        return controllerRef.current?.root ?? null;
      }
    }),
    [call]
  );
  return { containerRef, handle };
}
var ElaborateEditor = forwardRef(
  function ElaborateEditor2(props, ref) {
    const { containerRef, handle } = useDoczEditor(props);
    useImperativeHandle(ref, () => handle, [handle]);
    const [, setSelTick] = useState(0);
    const showToolbar = props.toolbar === true && props.mode === "edit";
    useEffect(() => {
      if (!showToolbar) return;
      const doc = containerRef.current?.ownerDocument ?? document;
      const onSel = () => setSelTick((n) => n + 1);
      doc.addEventListener("selectionchange", onSel);
      return () => doc.removeEventListener("selectionchange", onSel);
    }, [showToolbar, containerRef]);
    const formatApi = useMemo(
      () => createDocumentFormatApi(handle, props.doc.module),
      [handle, props.doc]
    );
    const className = props.className ? `docz-canvas ${props.className}` : "docz-canvas";
    const canvas = /* @__PURE__ */ jsx(
      "div",
      {
        ref: containerRef,
        className,
        "data-testid": props.testId ?? "elaborate-editor",
        "data-mode": props.mode
      }
    );
    if (!showToolbar) return canvas;
    const toolbar = /* @__PURE__ */ jsx(
      FormattingToolbar,
      {
        api: formatApi,
        mode: "document",
        onInsertBlock: (block) => handle.insertBlock(block),
        onInsertPageBreak: () => handle.insertPageBreak(),
        onCommand: () => containerRef.current?.focus(),
        testId: "document-toolbar"
      }
    );
    if (props.toolbarContainer !== void 0) {
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        canvas,
        props.toolbarContainer ? createPortal(toolbar, props.toolbarContainer) : null
      ] });
    }
    return /* @__PURE__ */ jsxs("div", { className: "ela-editor-shell", "data-testid": `${props.testId ?? "elaborate-editor"}-shell`, children: [
      toolbar,
      canvas
    ] });
  }
);

// src/selection-state.ts
function canvasHasSelection(sel) {
  if (sel == null) return false;
  return sel.anchor.blockOrd !== sel.focus.blockOrd || sel.anchor.byteOffset !== sel.focus.byteOffset;
}
var ROW_COL_BUTTONS = [
  {
    testid: "tt-row-above",
    icon: "table-row-above",
    label: "Insert row above",
    run: (h) => h.table.insertRow("above")
  },
  {
    testid: "tt-row-below",
    icon: "table-row-below",
    label: "Insert row below",
    run: (h) => h.table.insertRow("below")
  },
  {
    testid: "tt-row-del",
    icon: "table-row-delete",
    label: "Delete row",
    run: (h) => h.table.deleteRow()
  },
  {
    testid: "tt-col-left",
    icon: "table-col-left",
    label: "Insert column left",
    run: (h) => h.table.insertColumn("left")
  },
  {
    testid: "tt-col-right",
    icon: "table-col-right",
    label: "Insert column right",
    run: (h) => h.table.insertColumn("right")
  },
  {
    testid: "tt-col-del",
    icon: "table-col-delete",
    label: "Delete column",
    run: (h) => h.table.deleteColumn()
  }
];
var VALIGN_ICONS = {
  top: "valign-top",
  middle: "valign-middle",
  bottom: "valign-bottom"
};
function TableToolbar(props) {
  const { handle, hasCellRange = false, cellMerged = false, headerOn = false, onCommand } = props;
  const after = () => onCommand?.();
  const cls = props.className ? `ela-toolbar ela-table-toolbar ${props.className}` : "ela-toolbar ela-table-toolbar";
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cls,
      role: "toolbar",
      "aria-label": "Table",
      "data-testid": props.testId ?? "table-toolbar",
      children: [
        /* @__PURE__ */ jsx("div", { className: "ela-toolbar-group", "data-group": "table-structure", children: ROW_COL_BUTTONS.map((b) => /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "data-testid": b.testid,
            title: b.label,
            "aria-label": b.label,
            onClick: () => {
              b.run(handle);
              after();
            },
            children: /* @__PURE__ */ jsx(ToolbarIcon, { name: b.icon })
          },
          b.testid
        )) }),
        /* @__PURE__ */ jsxs("div", { className: "ela-toolbar-group", "data-group": "table-cells", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "tt-merge",
              title: "Merge cells",
              "aria-label": "Merge cells",
              "aria-disabled": !hasCellRange,
              disabled: !hasCellRange,
              onClick: () => {
                handle.table.mergeCells();
                after();
              },
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "table-merge" })
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "tt-split",
              title: "Split cell",
              "aria-label": "Split cell",
              "aria-disabled": !cellMerged,
              disabled: !cellMerged,
              onClick: () => {
                handle.table.splitCell(1, 2);
                after();
              },
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "table-split" })
            }
          ),
          CELL_VALIGN_OPTIONS.map((v) => /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": `tt-valign-${v}`,
              title: `Vertical align ${v}`,
              "aria-label": `Vertical align ${v}`,
              onClick: () => {
                handle.table.setCellVAlign(v);
                after();
              },
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: VALIGN_ICONS[v] })
            },
            v
          )),
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "color",
              "data-testid": "tt-cell-shade",
              title: "Cell shading",
              "aria-label": "Cell shading",
              onChange: (e) => {
                handle.table.setCellShading(e.target.value);
                after();
              }
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "tt-cell-shade-clear",
              title: "Clear cell shading",
              "aria-label": "Clear cell shading",
              onClick: () => {
                handle.table.setCellShading(null);
                after();
              },
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "cell-shade" })
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "tt-cell-border",
              title: "Cell borders",
              "aria-label": "Cell borders",
              onClick: () => {
                handle.table.setCellBorders({
                  top: defaultBorder(),
                  left: defaultBorder(),
                  bottom: defaultBorder(),
                  right: defaultBorder()
                });
                after();
              },
              children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "cell-border" })
            }
          )
        ] }),
        /* @__PURE__ */ jsx("div", { className: "ela-toolbar-group", "data-group": "table-header", children: /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "data-testid": "tt-header",
            title: "Toggle header row",
            "aria-label": "Toggle header row",
            "aria-pressed": headerOn,
            "data-active": headerOn,
            onClick: () => {
              handle.table.toggleHeader(!headerOn);
              after();
            },
            children: /* @__PURE__ */ jsx(ToolbarIcon, { name: "table-header" })
          }
        ) })
      ]
    }
  );
}

// src/version.ts
var LIB_VERSION = "0.6.0";

export { ALL_LIST_STYLES, BLOCK_JOIN, BORDER_COLOR_PRESETS, BORDER_PRESETS, BORDER_STYLE_OPTIONS, BORDER_WIDTH_OPTIONS, BULLET_GLYPHS, BorderEditor, CARET_GESTURE_CHORDS, CONTEXTUAL_TABS, CommandControl, CommandGlyph, CommentEditor, DEFAULT_TOOLBAR_CONFIG, EDITOR_COMMANDS, EDITOR_COMMAND_IDS, ESSENTIAL_COMMAND_IDS, ElaborateEditor, FORMAT_SUPPORT_KEYS, FormattingToolbar, GROUP_LABELS, GROUP_MODELS, INDENT_STEP_PT, LIB, LIB_VERSION, LINE_SPACING_MULTIPLES, LIST_FORMAT_ENTRIES, LinkPopover, ListControls, MENUBAR_ORDER, MenuBar, PLAIN_TEXT_BLOCK_KINDS, PROPOSAL_OVERLAY_CLASS, ParagraphGroup, RIBBON_TABS, RIBBON_TAB_LABELS, RIBBON_TAB_ORDER, RIBBON_TAB_SPECS, ROLE_ORDER, RibbonToolbar, SimpleToolbar, StyleGallery, TableSizePicker, TableStyleGallery, TableToolbar, ToolbarShell, VARIANT_LADDER, WEB_ELABORATE_EDITOR_TOKEN, applyBorderPayload, buildCellBorders, buildSelector, buildSelectorFromOffsets, buildSelectorFromSlices, bulletGlyphValue, canvasHasSelection, cellBordersToPageBorders, cellBordersToParaBorders, charIndexWithin, clampListLevel, clearProposalOverlay, computeGroupVariants, createClipboardController, createDocHistory, createDocumentFormatApi, createEditorController, createFormatPainter, createFragmentController, createFragmentFormatApi, createKeyHandler, createRunner, createShortcutDispatcher, danglingPlacementIds, defaultBorder, deriveArchetype, deriveTextEdit, dispatchShortcut, durableAnchorRefusal, editorToolbarConfigSchema, effectiveRole, emptyParaProps, emptySectionDecoration, explodeRawHtmlTable, findBlockRoot, fragmentByteLen, fragmentParaProps, fragmentPlainText, fragmentTextEditFromBeforeInput, fragmentTextInRange, groupModel, groupReductionPriority, htmlContainsTable, isChecklist, isCommandEnabled, isCommandOnTab, isCrossBlock, isCuratedBulletGlyph, isDefaultSectionDecoration, layoutGroup, linkExtentAt, listStartValue, listStateOfBlock, locateSelector, locateSelectorAcrossBlocks, locateSelectorInText, matchKeybinding, mergeParaProps, mergeSectionDecoration, misgroupedPlacementIds, mixedStyleProps, mixedStylePropsInFragment, nextVariant, orderedListStyle, parseBorderPayload, parseLineNumbering, parseListStart, parsePageMargins, parseToolbarConfig, parseToolbarConfigSafe, placeCaretAtByte, placementShowsCommand, proposalTestId, readCommandColor, rebaseOutcomeToHit, rebaseStateToHitState, reductionOrderFor, resolveCommands, resolveShortcut, resolveSimpleToolbar, resolveStyleGallery, resolveToolbarConfig, resolveToolbarConfigFrom, ribbonTabSpec, runShortcut, selectionByteRange, serializeBorderPayload, serializeLineNumbering, serializePageMargins, showProposalOverlay, spliceBytes, styleSlug, supportsLists, supportsParaProps, supportsTables, surfacePlainText, tabsForCommand, textEditFromBeforeInput, toPaletteCommand, toWasmSelector, unplacedRibbonCommands, useDoczEditor, useFragmentEditor, useRibbonScaling, validateToolbarConfig };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map